/**
 * Spanish (es) resource catalog — Onada 1.
 *
 * MUST mirror ca.ts key-for-key (parity is asserted by the i18n factory in development).
 * Keys are English code identifiers; only values are localized.
 */

import { existenciesEs } from './existencies/es.js';
import { qualityEs } from './quality/es.js';
import { compresEs } from './compres/es.js';
import { catalegEs } from './cataleg/es.js';

export const es = {
  common: {
    appName: 'Obrava',
    save: 'Guarda',
    cancel: 'Cancela',
    confirm: 'Confirma',
    ok: 'OK',
    delete: 'Elimina',
    edit: 'Edita',
    add: 'Añade',
    search: 'Busca',
    back: 'Atrás',
    next: 'Siguiente',
    close: 'Cierra',
    loading: 'Cargando…',
    retry: 'Reintenta',
    yes: 'Sí',
    no: 'No',
    all: 'Todos',
    none: 'Ninguno',
    today: 'Hoy',
    required: 'Obligatorio',
    optional: 'Opcional',
    showPassword: 'Mostrar la contraseña',
    hidePassword: 'Ocultar la contraseña',
    updateAvailableTitle: 'Hay una actualización disponible',
    updateAvailableBody: 'Guarda lo que estés haciendo y actualiza para cargar la última versión.',
    updateAction: 'Actualizar',
  },

  auth: {
    signIn: 'Inicia sesión',
    signOut: 'Cierra sesión',
    pin: 'PIN',
    enterPin: 'Introduce el PIN',
    email: 'Correo electrónico',
    password: 'Contraseña',
    wrongPin: 'PIN incorrecto',
    selectOperator: 'Selecciona operario',
    shift: 'Turno',
  },

  errors: {
    generic: 'Se ha producido un error',
    network: 'Error de conexión',
    notFound: 'No encontrado',
    forbidden: 'No tienes permiso',
    conflict: 'Conflicto de datos',
    tryAgain: 'Inténtalo de nuevo',
  },

  validation: {
    required: 'Este campo es obligatorio',
    invalidEmail: 'Correo electrónico no válido',
    invalidNumber: 'Número no válido',
    mustBePositive: 'Debe ser positivo',
    reasonRequired: 'Hay que indicar un motivo',
    tooShort: 'Demasiado corto',
    tooLong: 'Demasiado largo',
  },

  sync: {
    online: 'En línea',
    offline: 'Sin conexión',
    syncing: 'Sincronizando',
    syncError: 'Error de sincronización',
    pendingChanges: 'Cambios pendientes',
    allSynced: 'Todo sincronizado',
    savedOffline: 'Guardado sin conexión',
    title: 'Sincronización',
    queued: '{{count}} operaciones en cola',
    retrying: 'Reintentando automáticamente…',
    stuck: 'No se ha podido sincronizar. Seguimos intentándolo; el trabajo está guardado en el dispositivo.',
    blocked: 'Sesión caducada. Vuelve a entrar para sincronizar el trabajo pendiente.',
    lastSync: 'Última sincronización',
    never: 'Todavía no',
    lastError: 'Último error',
    attempts: 'Intentos',
    syncNow: 'Sincronizar ahora',
    downloadStuck:
      'No se ha podido descargar ningún dato nuevo. Seguimos intentándolo; lo que ves es la última copia guardada en el dispositivo.',
    downloadTenantMoved:
      'El obrador activo ha cambiado desde la oficina. Vuelve a elegir obrador para seguir recibiendo datos.',
    lastDownload: 'Última descarga',
    lastDownloadError: 'Último error de descarga',
    retryDownload: 'Descargar ahora',
  },

  notifications: {
    title: 'Notificaciones',
    markRead: 'Marca como leída',
    empty: 'No hay notificaciones',
    lowStock: 'Existencias bajo mínimo',
    expirySoon: 'Caducidad próxima',
    incidentHigh: 'Incidencia grave',
  },

  'domain.articles': {
    article: 'Artículo',
    rawMaterial: 'Materia prima',
    'rawMaterial.abbr': 'MP',
    intermediateProduct: 'Producto semielaborado',
    finishedProduct: 'Producto acabado',
    packaging: 'Embalaje',
    bom: 'Lista de ingredientes',
    'bom.abbr': 'BOM',
    technicalSheet: 'Ficha técnica',
    'technicalSheet.abbr': 'FT',
    smartImporter: 'Importador inteligente',
  },

  'domain.recipes': {
    recipe: 'Receta',
    recipeVersion: 'Versión de receta',
    recipeStep: 'Paso de elaboración',
    recipeScaling: 'Escalado de receta',
    yield: 'Rendimiento',
    leadTime: 'Tiempo de antelación',
  },

  'domain.lots': {
    lot: 'Lote',
    finishedLot: 'Lote de producto acabado',
    supplierLot: 'Lote de proveedor',
    internalLot: 'Lote interno',
    lotNumber: 'Número de lote',
    expiry: 'Caducidad',
    shelfLife: 'Vida útil',
    productionDate: 'Fecha de elaboración',
    label: 'Etiqueta',
  },

  'domain.stock': {
    stock: 'Existencias',
    stockMovement: 'Movimiento de existencias',
    stockAdjustment: 'Ajuste de existencias',
    inventory: 'Inventario',
    stockCount: 'Recuento',
    minimumStock: 'Stock mínimo',
    belowMinimum: 'Bajo mínimo',
    loss: 'Merma',
    'lossReason.burnt': 'Quemado',
    'lossReason.dropped': 'Caído',
    'lossReason.expired': 'Caducado',
    warehouseZone: 'Zona de almacén',
    'zone.dry': 'Seco',
    'zone.refrigerated': 'Refrigerado',
    'zone.frozen': 'Congelado',
    'zone.shipping': 'Expediciones',
    'zone.quarantine': 'Cuarentena',
  },

  'domain.recipes.orders': {
    workOrder: 'Orden de trabajo',
    'workOrder.abbr': 'OT',
    productionOrder: 'Orden de fabricación',
    'productionOrder.abbr': 'OF',
    packagingOrder: 'Orden de envasado',
    cleaningOrder: 'Orden de limpieza',
    weighing: 'Pesaje',
    consumption: 'Consumo',
    plannedQuantity: 'Cantidad prevista',
    realQuantity: 'Cantidad real',
    deviation: 'Desviación',
    closing: 'Cierre',
    closeWorkOrder: 'Cerrar OT',
    agenda: 'Agenda de producción',
    planning: 'Planificación',
  },

  'domain.quality': {
    traceability: 'Trazabilidad',
    recall: 'Retirada de producto',
    haccp: 'APPCC',
    criticalControlPoint: 'Punto de control crítico',
    'criticalControlPoint.abbr': 'PCC',
    nonConformity: 'No conformidad',
    correctiveAction: 'Acción correctora',
    incident: 'Incidencia',
    severity: 'Gravedad',
    'severity.low': 'Baja',
    'severity.medium': 'Media',
    'severity.high': 'Alta',
    auditLog: 'Registro de auditoría',
  },

  // Annex II of Reg. (EU) 1169/2011, verbatim: these are DENOMINATIONS, not tags.
  // Shortening one changes what the fitxa tècnica and the label declare, so they
  // stay whole — the sulphite threshold included. Lactose is NOT a separate
  // allergen: it lives inside the milk denomination.
  'domain.allergens': {
    allergen: 'Alérgeno',
    allergenTraces: 'Trazas',
    gluten: 'Cereales con gluten y derivados',
    crustaceans: 'Crustáceos y derivados',
    eggs: 'Huevos y derivados',
    fish: 'Pescado y derivados',
    peanuts: 'Cacahuetes y derivados',
    soybeans: 'Soja y derivados',
    milk: 'Leche y derivados (incluida la lactosa)',
    nuts: 'Frutos de cáscara y derivados',
    celery: 'Apio y derivados',
    mustard: 'Mostaza y derivados',
    sesame: 'Sésamo y derivados',
    sulphites: 'Dióxido de azufre y sulfitos (>10 mg/kg o >10 mg/l)',
    lupin: 'Altramuces y derivados',
    molluscs: 'Moluscos y derivados',
  },

  'domain.roles': {
    operator: 'Operario',
    productionManager: 'Responsable de producción',
    quality: 'Calidad',
    purchasing: 'Compras',
    sales: 'Ventas',
    admin: 'Administrador',
    viewer: 'Consulta',
    section: 'Sección',
    workPoint: 'Punto de trabajo',
    device: 'Dispositivo',
    scale: 'Báscula',
    labelPrinter: 'Etiquetadora',
  },

  'domain.units': {
    'unit.kg': 'kg',
    'unit.units': 'uds',
    'unit.boxes': 'cajas',
    'unit.loaves': 'barras',
    name_kg: 'Kilogramo',
    name_L: 'Litro',
    name_Un: 'Unidad',
    handlingUnit: 'Unidad de manipulación',
    'handlingUnit.abbr': 'HU',
    fefo: 'FEFO',
  },

  'domain.status': {
    'status.planned': 'Planificada',
    'status.inProgress': 'En curso',
    'status.completed': 'Completada',
    'status.cancelled': 'Cancelada',
    'status.pending': 'Pendiente',
    'connection.online': 'Sincronizado',
    'connection.offline': 'Sin conexión',
    'connection.syncing': 'Sincronizando',
    'connection.pending': '{{count}} por sincronizar',
    'connection.pendingRetrying_one': '{{count}} pendiente · reintentando',
    'connection.pendingRetrying_other': '{{count}} pendientes · reintentando',
    'connection.sessionExpired': 'Sesión caducada',
    'connection.stale': 'Datos desactualizados',
    'reason.required': 'Motivo',
    'shift.morning': 'Mañana',
    'shift.afternoon': 'Tarde',
    'shift.night': 'Noche',
  },

  'domain.parties': {
    obrador: 'Obrador',
    tenant: 'Obrador',
    supplier: 'Proveedor',
    client: 'Cliente',
    reception: 'Recepción',
    manualReception: 'Recepción manual',
    deliveryNote: 'Albarán',
    purchaseOrder: 'Orden de compra',
    salesOrder: 'Pedido de venta',
    shipment: 'Expedición',
    shift: 'Turno',
    alert: 'Aviso',
    notification: 'Notificación',
  },

  // Auditoría (F0.9) — acciones, actor, columnas y labels de entidad compartidos
  // por el visor del backoffice y el de la consola.
  'domain.audit': {
    action_create: 'Alta',
    action_update: 'Modificación',
    action_delete: 'Baja',
    action_restore: 'Reactivación',
    action_support_view: 'Acceso de soporte',
    action_tenant_switch: 'Cambio de obrador',
    action_packaging_labels_lost: 'Selección de etiquetas no guardada',
    action_reception_exceptions_lost: 'Excepciones de recepción no abiertas',
    action_reception_quality_nc_lost: 'No conformidades de recepción no abiertas',
    action_reception_on_unavailable_order: 'Recepción de un pedido no disponible',
    action_pin_issued: 'PIN emitido',
    action_pin_regenerated: 'PIN regenerado',
    action_pin_revoked: 'PIN retirado',
    action_pin_reactivated: 'PIN reactivado',
    action_pin_converted: 'Conversión a PIN',
    action_pin_login: 'Entrada con PIN',
    action_device_enrolled: 'Dispositivo enrolado',
    action_device_revoked: 'Dispositivo revocado',
    action_device_unlock: 'Dispositivo desbloqueado',
    action_stock_without_expiry: 'Existencias sin caducidad',
    action_consumption_on_unavailable_lot: 'Consumo de un lote no disponible',
    action_stock_state_change: 'Cambio de estado de existencias',
    action_reception_missing_data: 'Recepción sin proveedor o sin lote',
    action_access_code_sent: 'Código de acceso enviado',
    actorSystem: 'Sistema',
    actorSupport: 'Obrava (soporte)',
    supportBadge: 'Soporte Obrava',
    colWhen: 'Fecha y hora',
    colWho: 'Usuario',
    colAction: 'Acción',
    colEntity: 'Elemento',
    colDetails: 'Detalles',
    reasonLabel: 'Motivo',
    entity_article_categories: 'Categoría de artículo',
    entity_articles: 'Artículo',
    entity_article_ingredients: 'Ingrediente de artículo',
    entity_article_food_safety_notes: 'Seguridad alimentaria de artículo',
    entity_clients: 'Cliente',
    entity_client_shipping_addresses: 'Dirección de envío de cliente',
    entity_compliance_items: 'Elemento de cumplimiento',
    entity_conservation_conditions: 'Condición de conservación',
    entity_container_types: 'Tipo de envase',
    entity_devices: 'Dispositivo',
    entity_ingredients: 'Ingrediente',
    entity_label_templates: 'Plantilla de etiqueta',
    entity_legislation_notes: 'Nota de legislación',
    entity_packaging_units: 'Unidad de embalaje',
    entity_preparation_modes: 'Modo de preparación',
    entity_processes: 'Proceso',
    entity_process_resources: 'Recurso de un proceso',
    entity_recipes: 'Receta',
    entity_recipe_versions: 'Versión de receta',
    entity_resources: 'Recurso',
    entity_resource_types: 'Tipo de recurso',
    entity_sections: 'Sección',
    entity_shifts: 'Turno',
    entity_storage_locations: 'Ubicación',
    entity_supplier_articles: 'Artículo de proveedor',
    entity_suppliers: 'Proveedor',
    entity_tech_sheets: 'Ficha técnica',
    entity_taxes: 'Impuesto',
    entity_units: 'Unidad',
    entity_usage_types: 'Tipo de uso',
    entity_warehouse_zones: 'Zona de almacén',
    entity_work_points: 'Punto de trabajo',
    entity_users: 'Usuario',
    entity_user_roles: 'Rol de usuario',
    entity_user_menu_access: 'Visibilidad de menú',
    entity_supplier_documents: 'Documento de proveedor',
    entity_non_conformities: 'No conformidad',
    entity_corrective_actions: 'Acción correctora',
    entity_self_check_templates: 'Plantilla de autocontrol',
    entity_self_check_template_fields: 'Campo de autocontrol',
    entity_worker_trainings: 'Formación de manipulador',
    entity_tenant_settings: 'Configuración',
    entity_purchase_orders: 'Pedido de compra',
    entity_purchase_order_lines: 'Línea de pedido',
    entity_purchase_order_templates: 'Plantilla de pedido',
    entity_purchase_order_template_lines: 'Línea de plantilla',
    entity_receptions: 'Recepción',
    entity_reception_exceptions: 'Excepción de recepción',
    entity_purchase_returns: 'Devolución de compra',
    entity_stock_state_changes: 'Estado de un lote',
    entity_stock_counts: 'Recuento de existencias',
    entity_lot_label_reprints: 'Reimpresión de etiqueta de lote',
    entity_reception_corrections: 'Rectificación de recepción',
    entity_work_orders: 'Orden de trabajo',
    entity_work_order_resources: 'Recurso de una OT',
    entity_work_order_packaging_labels: 'Etiquetas de envase solicitadas',
    entity_sales_orders: 'Pedido de venta',
    entity_sales_order_lines: 'Línea de pedido de venta',
    entity_shipments: 'Expedición',
    entity_shipment_lines: 'Línea de expedición',
    entity_audit_log: 'Registro de actividad',
  },

  'planta.login': {
    queuedForOtherObradorNamed:
      'Este dispositivo todavía tiene trabajo pendiente de sincronizar de {{obrador}}. Entra en {{obrador}}, espera a que se sincronice y después cambia de obrador.',
    queuedForOtherObrador:
      'Este dispositivo todavía tiene trabajo pendiente de sincronizar de otro obrador. Entra en él, espera a que se sincronice y después cambia de obrador.',
    noPlantaAccessNamed:
      'Este usuario no tiene acceso a la App planta en {{obrador}}. Pide al administrador que te añada el rol de Operario.',
    noPlantaAccess:
      'Este usuario no tiene acceso a la App planta. Pide al administrador que te añada el rol de Operario.',
    noPlantaAccessOtherObradors: 'Si trabajas en otro obrador, cámbiate a él desde el Backoffice.',
    title: 'Obrava',
    subtitle: 'Everything, under control.',
    signInTitle: 'Inicia el turno',
    email: 'Correo electrónico',
    emailPlaceholder: 'operario@obrador.es',
    password: 'Contraseña',
    signInCta: 'Entra',
    signingIn: 'Entrando…',
    preparing: 'Preparando tus órdenes…',
    invalidCredentials: 'Correo o contraseña incorrectos',
    noTenantTitle: 'Cuenta sin obrador',
    noTenantBody:
      'Esta cuenta no tiene ningún obrador asignado. Contacta con el responsable de tu obrador.',
    selectObrador: 'Selecciona el obrador',
    selectObradorBody: 'Tu cuenta tiene acceso a más de un obrador. Elige con cuál trabajas en este turno.',
    continue: 'Continúa',
    // Entrada con PIN y enrolamiento de la tableta (OBR-25)
    pinTitle: 'Entra con tu PIN',
    pinSubtitle: 'Escribe los {{total}} caracteres de tu PIN.',
    pinSlotsLabel: 'PIN, {{entered}} de {{total}} caracteres escritos',
    pinBackspace: 'Borra el último carácter',
    pinInvalid: 'PIN incorrecto',
    pinConnecting: 'Conectando…',
    pinChecking: 'Comprobando el PIN…',
    pinOffline: 'Sin conexión. Para entrar con PIN hace falta conexión.',
    pinTryLater: 'No se ha podido entrar ahora mismo. Vuelve a intentarlo.',
    pinLockedTimed:
      'Esta tableta se ha bloqueado por demasiados PINs erróneos. Vuelve a intentarlo dentro de {{time}}.',
    pinLocked:
      'Esta tableta se ha bloqueado por demasiados PINs erróneos. Pide a un responsable que la desbloquee.',
    queuedForOtherOperator:
      'Esta tableta todavía tiene trabajo pendiente de sincronizar de otro operario. Que entre él y lo sincronice, o que un responsable entre con correo y contraseña.',
    useEmail: 'Acceso con correo',
    usePin: 'Entra con PIN',
  },

  'planta.nav': {
    orders: 'Órdenes',
    alerts: 'Avisos',
    reception: 'Recepción',
    sales: 'Ventas',
    settings: 'Más',
    badgeLabel: '{{label}}. Tareas pendientes: {{count}}',
  },

  'planta.settings': {
    title: 'Preferencias',
    language: 'Idioma',
    theme: 'Tema',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    obrador: 'Obrador',
    version: 'Versión {{version}} · Build {{build}}',
    printer: 'Impresora de etiquetas',
    printerChoose: 'Elige dónde se imprimen las etiquetas',
    printerNone:
      'Ninguna impresora configurada. Un administrador debe añadir una en el Backoffice (Configuración · Dispositivos).',
    printerUnavailable: 'Esta versión de la app no puede imprimir. Instala la última versión.',
    printerTest: 'Prueba de impresión',
    printerTesting: 'Enviando…',
    printerTestOk: 'Prueba enviada a {{printer}}',
    printerTestError:
      'No se ha podido imprimir en {{printer}}. Comprueba que esté encendida y conectada a la misma red que la tableta.',
  },

  'planta.obrador': {
    title: 'Obrador',
    change: 'Cambiar de obrador',
    sheetTitle: 'Obrador del turno',
    sheetBody:
      'Elige dónde trabajas. Se volverán a cargar las órdenes y los datos de ese obrador.',
    active: 'Activo',
    switching: 'Cambiando de obrador…',
    syncNow: 'Sincronizar ahora',
    errorOffline: 'Sin conexión. Conéctate para cambiar de obrador.',
    errorUnsynced:
      'Tienes trabajo pendiente de sincronizar. Sincronízalo antes de cambiar de obrador.',
    errorFailed: 'No se ha podido cambiar de obrador. Inténtalo de nuevo.',
    errorNotAMember: 'Ya no tienes acceso a este obrador.',
    errorNoAccess:
      'En este obrador no tienes acceso a la App planta. Pide al administrador que te añada el rol de Operario.',
    errorSignedOut: 'Se ha cerrado el turno. Vuelve a entrar para continuar.',
  },

  'planta.home': {
    title: 'Inicio',
    greeting: 'Hola, {{name}}',
    myWorkOrders: 'Mis OT',
    today: 'Hoy',
    pending: 'Pendientes',
    inProgress: 'En curso',
    noWorkOrders: 'No tienes órdenes asignadas',
    noWorkOrdersBody: 'Cuando el responsable publique órdenes para ti, aparecerán aquí.',
    seeAll: 'Verlas todas',
    refreshing: 'Actualizando…',
    pullToRefresh: 'Desliza para actualizar',
    signOutConfirm: '¿Quieres cerrar el turno?',
  },

  'planta.workOrders': {
    title: 'Órdenes de trabajo',
    start: 'Empieza',
    resume: 'Continúa',
    detail: 'Detalle de la OT',
    ingredients: 'Ingredientes',
    steps: 'Pasos',
    filterAll: 'Todas',
    filterPending: 'Pendientes',
    filterInProgress: 'En curso',
    filterCompleted: 'Hechas',
    summary: '{{pending}} pendientes, {{inProgress}} en curso',
    offlineNotice: 'Puedes seguir trabajando. Los datos se enviarán cuando vuelva la conexión.',
    empty: 'No hay órdenes en este estado',
    emptyBody: 'Cambia el filtro o desliza para actualizar.',
    emptyStale: 'No se han podido descargar las órdenes',
    emptyStaleBody:
      'Lo que ves es la última copia guardada en el dispositivo. Seguimos intentándolo; desliza para reintentarlo ahora.',
    quantityWithUnit: '{{quantity}} {{unit}}',
    number: 'OT {{number}}',
    executionPending: 'Ejecución disponible pronto',
    executionPendingBody:
      'La ejecución de la OT (pesaje, pasos y cierre) llega en una próxima versión.',
  },

  'planta.execution': {
    title: 'Ejecución',
    startWorkOrder: 'Empieza la OT',
    resumeWorkOrder: 'Continúa la OT',
    starting: 'Empezando…',
    step: 'Paso {{number}}',
    stepCount: 'Paso {{current}} de {{total}}',
    markStepDone: 'Marca el paso como hecho',
    stepDone: 'Paso hecho',
    stepPending: 'Pendiente',
    stepInProgress: 'En curso',
    stepBlockedByPcc: 'Hay que registrar el control crítico (PCC) para continuar',
    blockingStep: 'Paso bloqueante',
    allStepsDone: 'Todos los pasos hechos',
    recordControl: 'Registra el control',
    targetTemperature: 'Temperatura objetivo',
    targetTemperatureLabeled: 'Temperatura objetivo: {{value}}',
    estimatedDuration: 'Duración estimada',
    estimatedDurationLabeled: 'Duración estimada: {{value}}',
    unitCelsius: '°C',
    minutes: '{{count}} min',
    noSteps: 'Esta receta no tiene pasos',
    todayProduction: 'Producción de hoy',
    plannedVsConsumed: 'Previsto / Consumido',
    notStartedTitle: 'OT lista para empezar',
    notStartedBody: 'Empieza la OT para desplegar los pasos de la receta.',
    progress_one: '{{done}} de {{count}} paso hecho',
    progress_other: '{{done}} de {{count}} pasos hechos',
    controlBadgeWeight: 'Pesaje',
    controlBadgeTimer: 'Temporizador',
    controlBadgeTemperature: 'Temperatura',
    controlBadgePcc: 'PCC',
    pccBlockingBadge: 'PCC bloqueante',
    controlRequired: 'Control obligatorio',
    recordControlTitle: 'Registra el control',
    measurementValue: 'Valor medido',
    measurementUnit: 'Unidad',
    measurementNote: 'Observaciones',
    measurementNotePlaceholder: 'Observaciones (opcional)',
    saveMeasurement: 'Guarda el control',
    savingMeasurement: 'Guardando…',
    measurementSaved: 'Control registrado',
    lastMeasurement: 'Último control: {{value}}',
    criticalLimits: 'Límites críticos: {{range}}',
    limitRangeMin: 'mín. {{min}}',
    limitRangeMax: 'máx. {{max}}',
    limitRangeBetween: '{{min}} – {{max}}',
    withinLimits: 'Dentro de límites',
    outOfLimits: 'Fuera de límites',
    nonConforming: 'No conforme',
    requireIncidentTitle: 'Medida fuera de límites',
    requireIncidentBody:
      'El control crítico está fuera de los límites. Hay que registrar una incidencia y una acción correctora antes de continuar.',
    logIncident: 'Registra incidencia',
    goToWeighing: 'Ve al pesaje',
    reportIncidentLink: 'Incidencia',
    blockedStepHint: 'Hay que registrar el control crítico para poder marcar este paso.',
      closedLockTitle: 'OT cerrada',
    closedLockBody: 'Esta OT está finalizada. Para modificar algo hay que reabrirla desde el BackOffice.',
    goToRegister: 'Registro de pesadas',
},

  'planta.weighing': {
    title: 'Pesaje y consumo',
    subtitle: 'Registra el consumo real de cada ingrediente',
    planned: 'Previsto',
    real: 'Real',
    deviation: 'Desviación',
    deviationValue: '{{value}} %',
    deviationWarning: 'Desviación superior al límite ({{threshold}} %)',
    deviationWarningShort: 'Desviación elevada',
    confirmWeight: 'Confirma el peso',
    weighing: 'Guardando…',
    enterRealQuantity: 'Cantidad consumida real',
    selectLot: 'Selecciona el lote',
    selectArticle: 'Selecciona el ingrediente',
    ingredient: 'Ingrediente',
    noIngredients: 'Esta receta no tiene ingredientes',
    recorded: 'Peso registrado',
    recordedAt: 'Registrado a las {{time}}',
    consumed: 'Consumido',
    remaining: 'Pendiente',
    notStartedTitle: 'Empieza la OT antes de pesar',
    notStartedBody: 'Hay que empezar la OT para registrar el consumo de los ingredientes.',
    weighIngredient: 'Pesa el ingrediente',
    plannedVsReal: 'Previsto {{planned}} · Real {{real}}',
    todayProductionTitle: 'Producción de hoy',
    todayProductionEmpty: 'Todavía no hay consumo registrado',
    consumedVsPlanned: '{{consumed}} / {{planned}}',
    notWeighedYet: 'Sin pesar',
    source: 'Origen',
    sourceManual: 'Manual',
    sourceScale: 'Báscula',
    save: 'Guarda el consumo',
    saving: 'Guardando…',
    saved: 'Consumo registrado',
      theoreticalQuantity: 'Cantidad teórica',
    proposedFromRecipe: 'Propuesto por la receta',
    tolerance: 'Tolerancia ±{{value}} %',
    overTolerance: 'Fuera de tolerancia (±{{tolerance}} %)',
    incidentAutoCreated: 'Se ha generado una incidencia por desviación',
    lotsTitle: 'Lotes consumidos',
    lotsSubtitle: 'Elige de dónde sale la materia prima',
    lotAvailable: 'Disponible {{quantity}}',
    lotExpiry: 'Cad. {{date}}',
    lotNoExpiry: 'Sin caducidad',
    noLotsAvailable: 'No hay existencias con lote de este ingrediente',
    noLotsHint: 'Puedes registrar el consumo igualmente; quedará sin lote.',
    addLot: 'Añade un lote',
    removeLot: 'Quita el lote',
    lotQuantity: 'Cantidad de este lote',
    totalSelected: 'Total seleccionado {{quantity}}',
    scanLabel: 'Escanea la etiqueta',
    scanTitle: 'Escanea el QR de la etiqueta',
    scanHint: 'Apunta la cámara al código QR del lote',
    scanPermissionTitle: 'Se necesita permiso de cámara',
    scanPermissionBody: 'Para leer etiquetas, da acceso a la cámara.',
    scanPermissionGrant: 'Dar acceso',
    applyTheoretical: 'Aplica la cantidad teórica',
    applyTheoreticalHint: 'Se consumirá lo que dice la receta, sin pesar',
    sourceTheoretical: 'Teórico',
    registerOpen: 'Registro de pesadas',
    registerTitle: 'Registro de pesadas',
    registerSubtitle: 'Todo lo declarado en esta OT',
    registerEmpty: 'Todavía no se ha declarado ningún consumo',
    editWeighing: 'Corrige',
    deleteWeighing: 'Elimina',
    voidTitle: '¿Por qué lo corriges?',
    voidBodyEdit: 'La pesada anterior se conservará anulada, con el motivo.',
    voidBodyDelete: 'La pesada se conservará anulada, con el motivo.',
    voidReason: 'Motivo',
    voidReasonPlaceholder: 'Ej.: error al teclear la cifra',
    voidReasonRequired: 'Hay que escribir el motivo',
    voidConfirm: 'Anula la pesada',
    voidedBadge: 'Anulada',
    correctedBadge: 'Corrección',
    voidedBy: 'Anulada por {{name}}',
    alreadyWeighed: 'Ya declarado',
    sequentialTitle: 'Preparación secuencial',
    sequentialSubtitle: 'Un ingrediente cada vez, en el orden de la receta',
    sequentialLocked: 'Termina el ingrediente anterior para continuar',
    sequentialProgress: 'Ingrediente {{current}} de {{total}}',
    sequentialAllDone: 'Todos los ingredientes están preparados',
    closedTitle: 'OT cerrada',
    scaleNote: '×{{factor}} la receta: las cantidades ya van multiplicadas.',
    scaleUnknown:
      'Este pedido no se puede escalar (la receta y el pedido no hablan de la misma unidad): las cantidades son las del lote base de la receta.',
    closedBody: 'Los consumos de una OT cerrada no se pueden modificar.',
    noPermissionToEdit: 'No tienes permiso para corregir esta pesada',
},

  'planta.incidents': {
    photoUnavailable: 'Adjuntar foto todavía no está disponible.',
    title: 'Incidencia',
    subtitle: 'Notifica una incidencia sin parar el trabajo',
    newIncident: 'Nueva incidencia',
    type: 'Tipo',
    severity: 'Gravedad',
    comment: 'Comentario',
    photo: 'Foto (opcional)',
    addPhoto: 'Añade foto',
    photoAttached: 'Foto adjuntada',
    removePhoto: 'Quita la foto',
    photoDeferredHint: 'La foto se subirá cuando haya conexión.',
    report: 'Notifica la incidencia',
    reporting: 'Enviando…',
    reported: 'Incidencia notificada',
    typeRawMaterial: 'Materia prima',
    typeEquipment: 'Equipo',
    typeProcess: 'Proceso',
    typeQuality: 'Calidad',
    typeOther: 'Otros',
    severityLow: 'Baja',
    severityMedium: 'Media',
    severityHigh: 'Alta',
    commentPlaceholder: 'Describe qué ha pasado',
    nonBlockingHint: 'Notificar una incidencia no para la OT.',
    selectType: 'Elige el tipo',
    selectSeverity: 'Elige la gravedad',
    reportAnother: 'Notifica otra',
    backToOt: 'Vuelve a la OT',
    recentIncidents: 'Incidencias de esta OT',
    reportedAt: 'Notificada a las {{time}}',
      takePhoto: 'Haz una foto',
    retakePhoto: 'Repite la foto',
    photoPermissionDenied: 'Sin acceso a la cámara no se puede hacer la foto',
    photoPendingUpload: 'La foto se subirá al recuperar conexión',
    autoDeviationComment: 'Desviación de {{deviation}} en {{article}} (tolerancia ±{{tolerance}} %)',
},

  'planta.closing': {
    title: 'Cierre de OT',
    subtitle: 'Confirma la producción real y genera el lote',
    realQuantity: 'Cantidad real',
    plannedQuantity: 'Cantidad prevista',
    enterRealQuantity: 'Cantidad real producida',
    realQuantityRequired: 'Indica la cantidad real producida',
    merma: 'Merma',
    mermaValue: '{{quantity}} ({{percent}} %)',
    mermaValueNoPct: '{{quantity}}',
    overyield: 'Sobreproducción',
    mermaNone: 'Sin merma',
    mermaReason: 'Motivo de la merma',
    mermaReasonRequired: 'Indica el motivo de la merma',
    mermaReasonPrompt: 'Elige el motivo de la merma',
    mermaWarning: 'Merma superior al límite ({{threshold}} %)',
    'mermaReason.burnt': 'Quemado',
    'mermaReason.dropped': 'Caído',
    'mermaReason.expired': 'Caducado',
    'mermaReason.damage': 'Dañado',
    'mermaReason.loss': 'Pérdida',
    suggestedExpiry: 'Caducidad sugerida',
    noExpiry: 'Sin caducidad definida',
    lotNumber: 'Número de lote',
    lotPending: 'Pendiente de asignar',
    lotPendingHint: 'El número definitivo se asignará al sincronizar.',
    generateLot: 'Genera el lote',
    generatedLotSection: 'Lote que se generará',
    closing: 'Cerrando…',
    closed: 'OT cerrada',
    completedTitle: 'OT cerrada',
    completedBody: 'El lote se ha generado y se sincronizará automáticamente.',
    lotGenerated: 'Lote generado',
    printLabel: 'Imprime etiqueta',
    closeoutNote: 'Nota de cierre',
    packagingLabelsTitle: 'Etiquetas de envase',
    packagingLabelsHint:
      'Vienen marcados todos los envases del artículo. Desmarca lo que no haga falta.',
    packagingLabelCountUnknown: 'El número lo pondrá quien emita la etiqueta',
    packagingLabelCount_one: '{{count}} etiqueta',
    packagingLabelCount_other: '{{count}} etiquetas',
    closeoutNotePlaceholder: 'Nota de cierre (opcional)',
    consumptionSummary: 'Consumo registrado',
    noConsumption: 'Aún no se ha registrado ningún pesaje.',
    confirmClose: 'Cierra la OT y genera el lote',
    closeError: 'No se ha podido cerrar la OT. Inténtalo de nuevo.',
    backToOrder: 'Vuelve a la OT',
    openCloseScreen: 'Cierra la OT',
  },

  'planta.datePicker': {
    title: 'Elige una fecha',
    empty: 'Sin fecha',
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    clear: 'Borrar la fecha',
  },

  'planta.reception': {
    tasksTitle: 'Pendiente',
    tasksNothing: 'Nada pendiente de hoy ni atrasado',
    returnsTitle: 'Devoluciones pendientes',
    returnsEmpty: 'No hay ninguna devolución pendiente de salir.',
    taskOverdue_one: '{{count}} atrasada',
    taskOverdue_other: '{{count}} atrasadas',
    taskToday_one: '{{count}} de hoy',
    taskToday_other: '{{count}} de hoy',
    taskFuture_one: '{{count}} futura',
    taskFuture_other: '{{count}} futuras',
    taskUndated_one: '{{count}} sin fecha',
    taskUndated_other: '{{count}} sin fecha',
    groupOverdue: 'Atrasadas',
    groupToday: 'Hoy',
    groupFuture: 'Próximas',
    groupUndated: 'Sin fecha prevista',
    returnPlanned: 'Prevista: {{date}}',
    returnChipOverdue: 'Atrasada',
    returnChipToday: 'Hoy',
    returnDetailTitle: 'Salida de devolución',
    returnLot: 'Lote',
    returnSupplierLot: 'Lote del proveedor',
    returnReason: 'Motivo',
    returnObservations: 'Observaciones',
    returnReception: 'Albarán {{number}}',
    returnOrder: 'Pedido {{number}}',
    returnVerifyHint: 'Comprueba que el artículo, el lote y la cantidad son los que carga el transportista.',
    returnBlockedNote: 'Esta mercancía está bloqueada hasta que salga: producción no la puede gastar.',
    returnConfirm: 'Confirmar la salida',
    returnConfirming: 'Confirmando…',
    returnExited: 'Salida confirmada: {{date}}, {{time}}',
    returnNumberKnown: 'Albarán de devolución {{number}}',
    returnNumberPending: 'El albarán de devolución se numerará cuando se sincronice.',
    returnNotFound: 'Esta devolución ya no está en la lista: quizá ya ha salido.',
    returnBack: 'Volver a las devoluciones',
    returnNoPermission: 'No tienes permiso para confirmar devoluciones.',
    returnError: 'No se ha podido registrar la salida. Vuelve a intentarlo.',
    title: 'Recepción de materia prima',
    supplier: 'Proveedor',
    article: 'Artículo',
    quantity: 'Cantidad',
    supplierLotCode: 'Lote del proveedor',
    expiry: 'Caducidad',
    addLine: 'Añade artículo',
    confirm: 'Confirma recepción',
    selectMode: '¿Cómo quieres registrar la entrada?',
    newReception: 'Nueva recepción',
    deliveryNote: 'Nº albarán del proveedor',
    deliveryNoteRequired: 'Falta el número de albarán del proveedor',
    selectSupplier: 'Elige un proveedor',
    selectArticle: 'Elige un artículo',
    lines: 'Líneas',
    removeLine: 'Quita',
    lotOptional: 'Lote del proveedor (opcional)',
    expiryOptional: 'Caducidad (opcional)',
    control: 'Control de recepción',
    controlOk: 'Conforme',
    controlNok: 'No conforme',
    confirmed: 'Recepción confirmada',
    pendingSync: 'Pendiente de sincronizar',
    recent: 'Recepciones recientes',
    empty: 'Todavía no has registrado ninguna recepción',
    noSuppliers: 'No hay proveedores. Sincroniza con conexión.',
    noArticles: 'No hay artículos de compra disponibles.',
    noPendingOrders: 'Ningún pedido pendiente de recibir',
    addAtLeastOneLine: 'Añade al menos una línea con cantidad',
    webUnsupported: 'La recepción se hace desde la app móvil.',
    scan: 'Escanea albarán',
    scanning: 'Escaneando…',
    scanFailed: 'No se ha podido leer el albarán. Introdúcelo manualmente.',
    scanUnmatched_one: '{{count}} línea no reconocida. Añádela manualmente.',
    scanUnmatched_other: '{{count}} líneas no reconocidas. Añádelas manualmente.',
    print: 'Imprime etiqueta',
    printUnsupported: 'La impresión requiere una versión de la app con impresora.',
    printedTo: 'Etiqueta enviada a {{printer}}',
    printNoPrinter:
      'No hay ninguna impresora configurada. Un administrador debe añadir una en el Backoffice.',
    printError:
      'No se ha podido imprimir en {{printer}}. Comprueba que esté encendida y en la misma red.',
    ordered: 'Pedido',
    receivedSoFar: 'Ya recibido',
    pendingQuantity: 'Pendiente',
    orderedConversion: '{{ordered}} × {{factor}} = {{total}}',
    quantityWithBase: '{{quantity}} ({{base}})',
    receivedQuantity: 'Cantidad recibida',
    receivedQuantityIn: 'Cantidad recibida ({{unit}})',
    equalsBase: '= {{total}}',
    entryUnit: 'Unidad de la cantidad',
    noConversion: 'El envase «{{unit}}» no tiene conversión en la ficha del artículo. Indica la cantidad en {{base}}.',
    receiveOrder: 'Recibir un pedido',
    receiveWithoutOrder: 'Recepción sin pedido',
    ordersToReceive: 'Pedidos por recibir',
    windowDue: 'Hasta hoy',
    windowWeek: 'Esta semana',
    windowAll: 'Todos',
    statusAll: 'Todos los estados',
    statusAwaiting: 'Pendiente de entrega',
    statusPartial: 'Entregado parcialmente',
    allSuppliers: 'Todos los proveedores',
    expectedDelivery: 'Entrega prevista: {{date}}',
    noExpectedDelivery: 'Sin fecha de entrega prevista',
    overdue: 'Retrasado',
    noOrdersMatch: 'Ningún pedido con estos filtros',
    clearFilters: 'Quitar los filtros',
    entry: 'Entrada {{number}}',
    removeEntry: 'Quitar la entrada',
    addEntry: 'Añadir otra entrada',
    thisReceipt: 'En este albarán',
    willRemainPending: 'Quedará pendiente',
    expiryEstimatedLabel: 'Caducidad estimada',
    expiryEstimatedHint_one: 'Propuesta: recepción + {{count}} día de vida útil. Cámbiala si el proveedor indica una real.',
    expiryEstimatedHint_other: 'Propuesta: recepción + {{count}} días de vida útil. Cámbiala si el proveedor indica una real.',
    lineSummary: 'Pedido {{ordered}} · Ya recibido {{received}} · Pendiente {{pending}}',
    entriesNone: 'Ninguna entrada todavía',
    entriesSummary_one: '{{count}} entrada · {{total}}',
    entriesSummary_other: '{{count}} entradas · {{total}}',
    lineNeedsNote: 'Falta la nota',
    expandLine: 'Desplegar la línea',
    collapseLine: 'Plegar la línea',
    exceptionOverQuantity: 'Pasa {{excess}} de lo que faltaba. Se registrará como excepción.',
    exceptionOutsideOrder: 'Artículo no pedido pero que recibes. Se registrará como excepción y hay que indicar el motivo.',
    pickerTitle: 'Añade un artículo',
    pickerClose: 'Cerrar',
    pickerSearch: 'Busca por nombre o código',
    pickerUnrequestedHelp: 'Artículo no pedido pero que recibes. Hay que indicar el motivo.',
    pickerNoSupplierArticles: 'Este proveedor no tiene artículos vinculados.',
    pickerNoMatch: 'Ningún artículo coincide con la búsqueda',
    addArticleRestricted: 'Solo un responsable puede añadir un artículo que no está en el pedido.',
    unrequestedNote: '¿Por qué lo recibes?',
    unrequestedNoteHint: 'Ej.: el proveedor lo ha añadido al albarán',
    scanUnrequestedSkipped_one: '{{count}} línea de un artículo que no está en el pedido no se ha añadido: solo un responsable puede añadirlo.',
    scanUnrequestedSkipped_other: '{{count}} líneas de artículos que no están en el pedido no se han añadido: solo un responsable puede añadirlos.',
    flagException: 'Marcar como excepción',
    unflagException: 'Quitar la marca',
    flaggedException: 'Excepción',
    exceptionNote: '¿Qué pasa?',
    exceptionNoteHint: 'Ej.: sacos de 20 kg en lugar de 25',
    exceptionNoteRequired: 'Hay que explicar qué pasa',
    exceptionNotesMissing: 'Explica qué pasa con las entradas marcadas como excepción.',
    controlNote: 'Observación',
    controlNoteHint: '¿Qué tiene de no conforme?',
    createIncident: 'Crear una incidencia',
    createIncidentHint: 'Quedará ligada a esta recepción y la verá el responsable.',
    controlNoteRequired: 'Escribe qué pasa para crear la incidencia',
    controlNotesMissing: 'Escribe qué pasa en las entradas no conformes que abrirán una incidencia.',
    filterByWindow: 'Filtra por entrega prevista',
    filterByStatus: 'Filtra por estado',
    filterBySupplier: 'Filtra por proveedor',
    orderNumber: 'Pedido {{number}}',
    orderNoLongerPending: 'Este pedido ya no está pendiente de recibir.',
    historyOpen: 'Historial de recepciones',
    historyTitle: 'Historial de recepciones',
    historySearch: 'Busca por proveedor, albarán o pedido',
    historyFilterByWindow: 'Filtrar por fecha',
    historyWindowToday: 'Hoy',
    historyWindowWeek: '7 días',
    historyWindowMonth: '30 días',
    historyWindowAll: 'Todas',
    historyEmpty: 'Todavía no has hecho ninguna recepción en este dispositivo',
    historyNoMatch: 'Ninguna recepción coincide con la búsqueda',
    historyDeliveryNote: 'Albarán {{number}}',
    historyNoSupplier: 'Sin proveedor',
    historyCapped: 'Se muestran las {{count}} más recientes. Afina la búsqueda para encontrar otras.',
    historyScopeNote: 'Aquí salen las recepciones hechas en este dispositivo.',
    printCopies_one: 'Imprimir {{count}} etiqueta',
    printCopies_other: 'Imprimir {{count}} etiquetas',
    copiesLabel: 'Copias',
    copiesLess: 'Menos copias',
    copiesMore: 'Más copias',
  },

  'planta.sales': {
    title: 'Ventas',
    prepareCta: 'Prepara pedidos',
    newOrderCta: 'Nuevo pedido',
    recent: 'Actividad reciente',
    empty: 'Ningún pedido todavía',
    newOrderTitle: 'Nuevo pedido de venta',
    client: 'Cliente',
    selectClient: 'Elige un cliente',
    noClients: 'No hay clientes. Sincroniza con conexión.',
    deliveryDate: 'Fecha de entrega',
    notes: 'Notas',
    lines: 'Líneas',
    addLine: 'Añade artículo',
    selectArticle: 'Elige un artículo',
    noArticles: 'No hay artículos de venta disponibles.',
    quantity: 'Cantidad',
    removeLine: 'Quita',
    addAtLeastOneLine: 'Añade al menos una línea con cantidad',
    create: 'Crea el pedido',
    created: 'Pedido creado',
    pendingSync: 'Pendiente de sincronizar',
    available: 'Disponible: {{quantity}}',
    committed: 'Comprometido: {{quantity}}',
    insufficientStock: 'Stock insuficiente (aviso, no bloquea)',
    webUnsupported: 'Las ventas se gestionan desde la app móvil.',
  },

  'planta.picking': {
    noLotsToShip:
      'No hay ningún lote disponible para expedir estos artículos. Comprueba que las existencias estén dadas de alta con lote: las creadas con un ajuste manual no se pueden expedir.',
    title: 'Preparar pedidos',
    pendingOrders: 'Pedidos pendientes',
    noPendingOrders: 'Ningún pedido pendiente de preparar',
    filterToday: 'Hoy',
    filterWeek: 'Esta semana',
    filterAll: 'Todos',
    deliveryFor: 'Entrega: {{date}}',
    ordered: 'Pedido',
    prepared: 'Preparado',
    lot: 'Lote',
    suggestedLot: 'Lote sugerido (FEFO)',
    changeLot: 'Cambia el lote',
    noLots: 'Sin lotes disponibles',
    expiresOn: 'Caduca: {{date}}',
    available: 'Disponible: {{quantity}}',
    insufficientStock: 'Stock insuficiente (aviso, no bloquea)',
    ship: 'Expide',
    shipConfirmTitle: '¿Expedir el pedido?',
    shipConfirmBody: 'Se dará de baja el stock y se generará el albarán de venta.',
    partialHint: 'Las cantidades no servidas quedan pendientes (backorder).',
    shipped: 'Pedido expedido',
    pendingSync: 'Pendiente de sincronizar',
    webUnsupported: 'La preparación de pedidos se hace desde la app móvil.',
  },

  'planta.config': {
    title: 'Configuración incompleta',
    body: 'Faltan variables de entorno necesarias para conectar con el servidor. La app no puede arrancar hasta que estén definidas en el despliegue.',
    missingLabel: 'Variables que faltan:',
    hint: 'Defínelas en la configuración del proyecto (Vercel) y vuelve a desplegar.',
  },

  'backoffice.agenda': {
    title: 'Planificación',
    subtitle: 'Órdenes de trabajo planificadas por día y turno',
    byShift: 'Por turno',
    byDay: 'Por día',
    publish: 'Publica',
    publishing: 'Publicando…',
    draft: 'Borrador',
    published: 'Publicada',
    create: 'Crea OT',
    date: 'Fecha',
    today: 'Hoy',
    previousDay: 'Día anterior',
    nextDay: 'Día siguiente',
    noShift: 'Sin turno',
    unassigned: 'Sin asignar',
    quantityWithUnit: '{{quantity}} {{unit}}',
    empty: 'No hay ninguna orden de trabajo para este día. Crea una para planificar producción.',
    emptyFiltered: 'Ninguna orden de trabajo coincide con este filtro para este día.',
    loadError: 'No se han podido cargar las órdenes de trabajo',
    publishError: 'No se ha podido publicar la orden de trabajo',
    // Filtros rápidos (accesos directos desde el panel)
    filterAll: 'Todas',
    filterInProgress: 'En curso',
    filterDrafts: 'Borradores',
    // Calendario mensual de producción
    previousMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    dayHeading: 'Producción del {{date}}',
    ordersCount: '{{count}} OT',
    moreOrders: '+{{count}} más',
  },

  'backoffice.workOrders': {
    title: 'Órdenes de trabajo',
    create: 'Crea OT',
    assign: 'Asigna',
    assignToUser: 'Asigna a operario',
    assignToSection: 'Asigna a sección',
    sectionWithoutMembers:
      'Nadie tiene asignada la sección «{{name}}». Esta OT no aparecerá en ningún dispositivo hasta que asignes a alguien desde Configuración · Usuarios.',
    cancel: 'Cancela',
    cancellationReason: 'Motivo de cancelación',
    lowStockWarning: 'Existencias insuficientes (aviso, no bloquea)',
    // Create OT (screen 09)
    createTitle: 'Nueva orden de trabajo',
    createSubtitle: 'Planifica una OT a partir de una receta publicada',
    backToAgenda: 'Volver a la agenda',
    sectionRecipe: 'Receta y producto',
    sectionPlanning: 'Cantidad y planificación',
    sectionAssignment: 'Asignación',
    sectionResources: 'Recursos',
    sectionResourcesHint:
      'La receta pide estos recursos. Elige cuál usará esta OT; si aún no lo sabes, podrás hacerlo desde el detalle de la OT.',
    resourceNeed: '{{quantity}} × {{type}}',
    resourceDuration: '{{minutes}} min',
    resourceMachine: 'Recurso asignado',
    resourceNotChosen: 'Sin elegir',
    recipe: 'Receta',
    recipeHint: 'Solo recetas con una versión publicada activa',
    selectRecipe: 'Selecciona una receta',
    pinnedVersionNote:
      'La OT fija la versión activa de la receta en este momento; no cambiará aunque la receta se revise después.',
    workOrderType: 'Tipo de OT',
    plannedQuantity: 'Cantidad planificada',
    plannedUnit: 'Unidad',
    plannedUnitHint: 'La pone la receta: rinde {{quantity}} {{unit}}.',
    plannedUnitNoRecipe: 'La pone la receta que elijas.',
    scaleHint: 'Esto es {{factor}} veces la receta: el operario pesará cada ingrediente multiplicado por este número.',
    selectUnit: 'Selecciona una unidad',
    productionDate: 'Fecha de producción',
    shift: 'Turno',
    noShift: 'Sin turno',
    assignTo: 'Asigna a',
    assignToHint: 'Una OT se asigna a una persona O a una sección',
    selectMember: 'Selecciona un operario',
    selectSection: 'Selecciona una sección',
    saveDraft: 'Guarda borrador',
    saveAndPublish: 'Guarda y publica',
    publishing: 'Publicando…',
    publishHint:
      'Guarda como borrador para planificar sin que salga en la agenda, o guarda y publica para que la app de planta pueda ejecutarla.',
    shortIngredient:
      '{{name}}: se necesitan {{required}}, disponibles {{available}}',
    fixValidation: 'Revisa los campos marcados antes de guardar.',
    createError: 'No se ha podido crear la orden de trabajo',
    assignmentError:
      'Se ha creado la OT pero no se ha podido guardar la asignación. Revísala desde la agenda.',
    cancelError: 'No se ha podido cancelar la orden de trabajo',
    noUnits:
      'Aún no hay unidades definidas para el obrador. Defínelas en Configuración antes de crear OT.',
    noPlannableRecipes:
      'No hay ninguna receta con una versión publicada. Publica una receta antes de crear una OT.',
    // Work order type enum labels
    'type.production': 'Producción',
    'type.packaging': 'Envasado',
    // Work order status enum labels
    'status.planned': 'Planificada',
    'status.in_progress': 'En curso',
    'status.completed': 'Completada',
    'status.cancelled': 'Cancelada',
    // Work order step status enum labels (detalle OT / trazabilidad)
    'stepStatus.pending': 'Pendiente',
    'stepStatus.in_progress': 'En curso',
    'stepStatus.completed': 'Hecho',
    'stepStatus.skipped': 'Omitido',
    // Weighing source enum labels
    'source.manual': 'Manual',
    'source.scale': 'Báscula',
    'source.theoretical': 'Teórico',
  },

  'backoffice.workOrderDetail': {
    editTitle: 'Edita la planificación',
    editBody: 'Puedes corregir la cantidad, la fecha, el turno y quién la hace mientras no se haya empezado. La receta y su versión no se pueden cambiar: para producir otra cosa, cancela la OT y crea una nueva.',
    subtitle: 'Trazabilidad de la orden de trabajo',
    backToAgenda: 'Volver a la agenda',
    loadError: 'No se ha podido cargar la orden de trabajo',
    notFound: 'No se ha encontrado la orden de trabajo.',
    // Cabecera
    article: 'Producto',
    plannedQuantity: 'Cantidad planificada',
    realQuantity: 'Cantidad real',
    merma: 'Merma',
    mermaValue: '{{quantity}} {{unit}} ({{pct}}%)',
    assignment: 'Asignación',
    unassigned: 'Sin asignar',
    shift: 'Turno',
    noShift: 'Sin turno',
    productionDate: 'Fecha de producción',
    publishedAt: 'Publicada',
    startedAt: 'Iniciada',
    completedAt: 'Completada',
    cancellationReason: 'Motivo de cancelación',
    empty: '—',
    quantityWithUnit: '{{quantity}} {{unit}}',
    // Pasos
    sectionSteps: 'Pasos de producción',
    stepsEmpty: 'El operario todavía no ha activado ningún paso.',
    sectionResources: 'Recursos',
    resourcesEmpty: 'La receta de esta OT no pide recursos.',
    resourceSaveError: 'No se ha podido guardar el recurso. Vuelve a intentarlo.',
    stepBlocking: 'Bloqueante (PCC)',
    stepCompletedAt: 'Hecho {{at}}',
    stepCompletedBy: 'por {{name}}',
    recordMeasured: 'Medida',
    recordNote: 'Nota',
    // Pesajes
    sectionWeighings: 'Pesajes',
    weighingsEmpty: 'Todavía no se ha registrado ningún pesaje.',
    colArticle: 'Materia prima',
    colPlanned: 'Previsto',
    colReal: 'Real',
    colDeviation: 'Desviación',
    colSource: 'Origen',
    deviationValue: '{{pct}}%',
    // Incidencias
    sectionIncidents: 'Incidencias',
    incidentsEmpty: 'Ninguna incidencia reportada en esta OT.',
    incidentResolved: 'Resuelta {{at}}',
    // Lote
    sectionLot: 'Lote producido',
    lotEmpty: 'Todavía no se ha generado ningún lote (la OT no se ha cerrado).',
    packagingLabelsTitle: 'Etiquetas de envase',
    packagingUnitUnknown: 'Envase',
    packagingLabelPrint_one: 'Genera {{packaging}} ({{count}} etiqueta)',
    packagingLabelPrint_other: 'Genera {{packaging}} ({{count}} etiquetas)',
    packagingLabelPrinting: 'Generando…',
    packagingLabelIssued: 'Ya emitida',
    packagingLabelError:
      'No se ha podido guardar la selección o generar la etiqueta de {{packaging}}. Inténtalo de nuevo.',
    packagingLoadError:
      'No se han podido leer las etiquetas de envase de esta orden. Mientras no se puedan leer no se puede cambiar nada: recarga la página.',
    packagingNothingRequested:
      'Esta orden de envasado se ha cerrado sin ningún envase solicitado. Marca los que hagan falta para poder generar las etiquetas.',
    packagingNoPermission:
      'Emitir etiquetas es del responsable de producción o del administrador.',
    packagingCopiesRange: 'Escribe un número de etiquetas entre 1 y {{max}}.',
    packagingTooManyCopies:
      'Hacen falta {{total}} en total, más de las {{max}} que se pueden generar de una vez. Genéralas por tandas.',
    packagingFactorDrift:
      'Se pidieron {{requested}} unidades por envase; el artículo ahora dice {{configured}}.',
    packagingIssuedUnknown:
      'Todavía no se sabe si se han emitido etiquetas',
    packagingLabelNoLotYet:
      'La orden todavía no se ha cerrado: se pueden marcar los envases, pero las etiquetas no se generan hasta que haya lote.',
    packagingLabelNoTemplate:
      'No hay ningún formato de etiqueta utilizable para este artículo. Define uno en Configuración · Formatos de etiqueta o elige uno en la ficha del artículo.',
    packagingUnconfigured: 'Ya no es un envase del artículo',
    packagingIssuedLocked: 'Ya se han emitido etiquetas: la selección no se puede deshacer',
    lotQuantity: 'Cantidad producida',
    lotExpiry: 'Caducidad',
    lotNoExpiry: 'Sin caducidad',
    // Común
    recordedBy: 'Registrado por',
    recordedAt: 'Registrado',
    by: 'por {{name}}',
      sourceTheoretical: 'Teórico',
    voided: 'Anulada',
    voidedReason: 'Motivo: {{reason}}',
    tolerance: 'Tolerancia',
},

  'backoffice.dashboard': {
    title: 'Panel',
    subtitle: 'Resumen operativo del obrador · {{date}}',
    createOt: 'Crea OT',
    loadError: 'No se han podido cargar algunos datos del panel',
    // Planning KPIs (rol de planificación)
    planningSection: 'Planificación de hoy',
    otTodayLabel: 'OT de hoy',
    otTodayHint: 'Órdenes de trabajo planificadas para hoy',
    otByStatusLabel: 'OT por estado',
    draftsLabel: 'Borradores pendientes',
    goToAgenda: 'Ver agenda',
    // Read KPIs (cualquier miembro)
    operationsSection: 'Operaciones',
    lowStockItems: 'Artículos bajo mínimo',
    lowStockEmpty: 'Ningún artículo por debajo del mínimo. Todo en orden.',
    recentLots: 'Últimos lotes producidos',
    recentLotsEmpty: 'Aún no se ha producido ningún lote.',
    goToExistences: 'Ver existencias',
    // Visual dashboard (KPIs + gráficos)
    production: 'Producción',
    inProgressLabel: 'En curso',
    kpiActive: 'activas',
    kpiPending: 'pendientes',
    productionChartSubtitle: 'Lotes producidos · últimos 14 días',
    thisPeriod: 'Este período',
    otByStatusSubtitle_one: '{{count}} orden · últimos 30 días',
    otByStatusSubtitle_other: '{{count}} órdenes · últimos 30 días',
    donutTotal: 'total',
    lotsProduced: 'Lotes producidos',
    lotsChartSubtitle: 'Por día · últimos 7 días',
    lotsTotal_one: '{{count}} lote',
    lotsTotal_other: '{{count}} lotes',
    // Personalización del panel (modo edición, guardado en el navegador)
    customize: 'Personaliza el panel',
    customizeDone: 'Hecho',
    customizeReset: 'Restaura',
    customizeHint:
      'Oculta, muestra y reordena las tarjetas con los controles de cada una. Los cambios se guardan en este navegador.',
    moveEarlier: 'Mueve antes',
    moveLater: 'Mueve después',
    showCard: 'Muestra la tarjeta',
    hideCard: 'Oculta la tarjeta',
    // Ventas y Compras (KPIs + paneles)
    salesTitle: 'Ventas',
    salesSubtitle: 'Pedidos abiertos · por fecha de entrega',
    salesPendingLabel: 'Pendientes de servir',
    salesEmpty: 'Ningún pedido pendiente de servir.',
    goToSales: 'Ver ventas',
    deliveriesToday: '{{count}} hoy',
    purchasesTitle: 'Compras',
    purchasesSubtitle: 'Pedidos de compra abiertos · por fecha prevista',
    purchasesOpenLabel: 'Compras en curso',
    purchasesEmpty: 'Ningún pedido de compra en curso.',
    goToPurchases: 'Ver compras',
    noDate: 'Sin fecha',
    // Incidencias (KPI + panel, con realtime)
    incidentsTitle: 'Incidencias recientes',
    incidentsSubtitle: 'Pendientes de resolución',
    incidentsEmpty: 'Ninguna incidencia pendiente.',
    goToIncidents: 'Ver incidencias',
    kpiAttention: 'atención',
    kpiTitleOtToday: 'Órdenes de hoy',
    kpiDescOtToday: 'Planificadas para hoy',
    kpiTitleInProgress: 'En curso',
    kpiDescInProgress: 'Órdenes en producción',
    kpiTitleProduction: 'Producción',
    kpiDescProduction: 'Lotes producidos hoy',
    kpiTitleSales: 'Pendientes de servir',
    kpiDescSales: 'Pedidos abiertos',
    kpiTitlePurchases: 'Compras',
    kpiDescPurchases: 'Compras en curso',
    kpiTitleDrafts: 'Recetas',
    kpiDescDrafts: 'Borradores pendientes',
    kpiTitleLowStock: 'Existencias',
    kpiDescLowStock: 'Artículos bajo mínimo',
    kpiTitleIncidents: 'Incidencias',
    kpiDescIncidents: 'Incidencias abiertas',
    // Legado (no usado actualmente)
    productionToday: 'Producción de hoy',
    openIncidents: 'Incidencias abiertas',
    expiringSoon: 'Caducan pronto',
  },

  'backoffice.production': {
    title: 'Producción',
    subtitle: 'Lotes producidos por día',
    date: 'Fecha',
    today: 'Hoy',
    previousDay: 'Día anterior',
    nextDay: 'Día siguiente',
    lotNumber: 'Lote',
    product: 'Producto',
    quantity: 'Cantidad',
    expiry: 'Caducidad',
    noExpiry: 'Sin caducidad',
    status: 'Estado',
    quantityWithUnit: '{{quantity}} {{unit}}',
    empty: 'No se ha producido ningún lote este día.',
    loadError: 'No se han podido cargar los lotes producidos',
  },

  'backoffice.incidents': {
    title: 'Incidencias',
    subtitle: 'Incidencias reportadas desde planta',
    filterAll: 'Todas',
    filterOpen: 'Pendientes',
    filterResolved: 'Resueltas',
    statusOpen: 'Pendiente',
    statusResolved: 'Resuelta',
    severity: 'Gravedad',
    severityLow: 'Baja',
    severityMedium: 'Media',
    severityHigh: 'Alta',
    type: 'Tipo',
    typeRawMaterial: 'Materia prima',
    typeEquipment: 'Equipo',
    typeProcess: 'Proceso',
    typeQuality: 'Calidad',
    typeOther: 'Otros',
    workOrder: 'OT',
    noWorkOrder: 'Sin OT',
    reception: 'Recepción',
    deliveryNoteRef: 'albarán {{number}}',
    supplier: 'Proveedor',
    purchaseOrder: 'Pedido',
    article: 'Artículo',
    lot: 'Lote',
    supplierLotRef: 'lote del proveedor {{code}}',
    reportedBy: 'Reportada por',
    reportedAt: 'Reportada',
    resolvedAt: 'Resuelta',
    unknownUser: 'Operario',
    resolve: 'Marcar como resuelta',
    resolving: 'Resolviendo…',
    resolveError: 'No se ha podido resolver la incidencia',
    live: 'En directo',
    empty: 'No hay incidencias.',
    emptyOpen: 'No hay incidencias pendientes. Todo en orden.',
    loadError: 'No se han podido cargar las incidencias',
    toastTitle: 'Nueva incidencia',
    toastView: 'Ver',
    resolveTitle: 'Cierra la incidencia',
    resolveBody:
      'Indica cómo se ha resuelto. Cerrarla no mueve el stock: si hay que devolver mercancía, se genera una devolución aparte.',
    resolutionLabel: 'Resolución',
    resolutionNoAction: 'Sin acción',
    resolutionAccepted: 'Aceptado',
    resolutionTotalReturn: 'Devolución total',
    resolutionPartialReturn: 'Devolución parcial',
    resolutionCredit: 'Abono / compensación',
    resolvedWith: 'Resuelta: {{resolution}}',
    resolutionNoteLabel: 'Nota (opcional)',
    resolutionNotePlaceholder: 'Qué se ha acordado con el proveedor',
    closeAction: 'Cierra la incidencia',
    createReturn: 'Genera una devolución',
    resolveErrorResolutionRequired: 'Elige cómo se ha resuelto.',
    resolveErrorAlreadyResolved: 'Otra persona ya ha cerrado esta incidencia.',
    resolveErrorForbidden: 'No tienes permiso para cerrar incidencias.',
  },

  'backoffice.articles': {
    title: 'Artículos',
    create: 'Crea artículo',
    sku: 'Código',
    name: 'Nombre',
    type: 'Tipo',
    shelfLife: 'Vida útil (días)',
    allergens: 'Alérgenos',
    allergensDerived: 'Alérgenos derivados automáticamente del BOM',
  },

  'backoffice.common': {
    appName: 'Obrava Backoffice',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    saving: 'Guardando…',
    creating: 'Creando…',
    actionError: 'No se ha podido completar la acción',
    placeUnavailable: '{{place}} (no disponible)',
    supplierInactive: '{{name}} (inactivo)',
    supplierUnavailable: 'Proveedor no disponible',
    loadError: 'No se han podido cargar los datos',
    readOnly: 'Solo lectura',
    noPermission: 'No tienes permiso para esta acción',
    optional: 'Opcional',
    none: 'Ninguno',
    dashboard: 'Panel',
    agenda: 'Planificación',
    production: 'Producción',
    incidents: 'Incidencias',
    catalog: 'Catálogo',
    articles: 'Artículos',
    recipes: 'Recetas',
    existences: 'Existencias',
    purchases: 'Compras',
    purchaseOrders: 'Pedidos de compra',
    purchaseDeliveryNotes: 'Albaranes de compra',
    receptionExceptions: 'Excepciones',
    purchaseReturns: 'Devoluciones',
    purchaseTemplates: 'Plantillas',
    thirdParties: 'Terceros',
    clients: 'Clientes',
    suppliers: 'Proveedores',
    team: 'Equipo',
    users: 'Usuarios',
    operators: 'Operarios',
    sales: 'Ventas',
    salesOrders: 'Pedidos de venta',
    technicalSheet: 'Ficha técnica',
    import: 'Importación',
    settings: 'Configuración',
    language: 'Idioma',
    theme: 'Tema',
    account: 'Cuenta y perfil',
    passwordTitle: 'Contraseña',
    passwordHint: 'Para cambiarla te enviamos un correo con un enlace; en él elegirás la contraseña nueva.',
    passwordCta: 'Envíame un enlace para cambiarla',
    copyVersion: 'Copiar versión',
    versionCopied: 'Versión copiada',
    collapse: 'Minimiza',
    navSectionOperative: 'Operativa',
    navSectionProduct: 'Producto',
    navSectionStock: 'Existencias y movimientos',
    navSectionPurchases: 'Compras',
    navSectionSales: 'Ventas',
    navSectionQuality: 'Calidad',
    navNonConformities: 'No conformidades',
    navSelfChecks: 'Autocontroles',
    navEvidence: 'Evidencias',
    navStockCounts: 'Recuentos de existencias',
    navSectionData: 'Datos',
    navSectionManagement: 'Gestión',
    openMenu: 'Abre el menú',
    closeMenu: 'Cierra el menú',
    comingSoon: 'Próximamente',
    obrador: 'Obrador',
    switchObrador: 'Cambiar de obrador',
    switchingObrador: 'Cambiando de obrador…',
    switchObradorError: 'No se ha podido cambiar de obrador',
    activeObrador: 'Obrador activo',
    memberDeleted: '{{name}} (baja)',
  },

  'backoffice.auth': {
    title: 'Obrava Backoffice',
    subtitle: 'Everything, under control.',
    signInTitle: 'Inicia sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    signInCta: 'Entra',
    signingIn: 'Entrando…',
    signInError: 'Credenciales incorrectas',
    showPassword: 'Mostrar la contraseña',
    hidePassword: 'Ocultar la contraseña',
    noTenantTitle: 'Cuenta sin obrador',
    noTenantBody:
      'Esta cuenta no tiene ningún obrador asignado. Contacta con el administrador de tu obrador.',
    noBackofficeAccessTitle: 'Sin acceso al Backoffice',
    noBackofficeAccessBody:
      'En {{obrador}}, tus roles solo abren la App planta. Si también necesitas el Backoffice, pide al administrador que te añada un rol de oficina, por ejemplo Consulta.',
    noRolesBody:
      'En {{obrador}} no tienes ningún rol asignado. Pide al administrador que te asigne uno.',
    backToSignIn: 'Vuelve a iniciar sesión',
    rememberMe: 'Mantén la sesión en este equipo',
    rememberMeHint:
      'Actívalo solo en ordenadores de uso habitual. En equipos compartidos, desactívalo y la sesión se cerrará al salir del navegador.',
    forgotPassword: '¿Has olvidado la contraseña?',
    resetTitle: 'Restablece la contraseña',
    resetEmailStep: 'Introduce tu correo y te enviaremos un enlace para elegir una contraseña nueva.',
    resetSendCta: 'Envíame el enlace',
    resetSending: 'Enviando…',
    resetNewPassword: 'Contraseña nueva',
    resetConfirmPassword: 'Confirma la contraseña',
    resetSubmitCta: 'Guarda la contraseña nueva',
    resetSubmitting: 'Guardando…',
    resetSuccess: 'Contraseña actualizada. Ya puedes iniciar sesión.',
    resetSendError: 'No se ha podido enviar el correo. Comprueba la dirección e inténtalo de nuevo.',
    resetLinkSent: 'Te hemos enviado un correo a {{email}}. Abre el enlace que encontrarás para elegir una contraseña nueva (revisa también el spam).',
    resetLinkInvalid: 'Este enlace ha caducado o ya se ha usado. Pide uno nuevo.',
    resetRetry: '¿No te ha llegado? Inténtalo de nuevo',
    resetPasswordWeak: 'La contraseña debe tener al menos 6 caracteres.',
    resetPasswordMismatch: 'Las contraseñas no coinciden.',
    resetGenericError: 'No se ha podido restablecer la contraseña.',
    accessLoadErrorTitle: 'No se han podido cargar tus accesos',
    accessLoadErrorBody:
      'No hemos podido comprobar qué puedes abrir en {{obrador}} y, mientras no lo sepamos, no te mostramos ninguna pantalla. Inténtalo de nuevo; si el problema continúa, avisa al administrador.',
    accessLoadRetry: 'Reintentar',
    noMenuAccessTitle: 'Sin opciones disponibles',
    noMenuAccessBody:
      'En {{obrador}} tienes todas las opciones del Backoffice ocultas. Pide al administrador que te vuelva a activar alguna.',
  },

  'backoffice.catalog': {
    title: 'Catálogo',
    subtitle: 'Artículos del obrador: compra, semielaborados y venta',
    newArticle: 'Nuevo artículo',
    import: 'Importa artículos',
    importSupplierArticles: 'Importa relación artículo-proveedor',
    searchPlaceholder: 'Busca por código o nombre',
    filterType: 'Tipología',
    typeAll: 'Todas',
    typePurchase: 'Compra',
    typeIntermediate: 'Semielaborado',
    typeSale: 'Venta',
    filterCategory: 'Categoría',
    filterTax: 'Impuesto',
    categoryAll: 'Todas',
    colSku: 'Código',
    colName: 'Nombre',
    colType: 'Tipología',
    colCategory: 'Categoría',
    colActions: 'Acciones',
    techSheetAction: 'Ficha técnica',
    colStock: 'Existencias',
    empty: 'Todavía no hay ningún artículo',
    emptyFiltered: 'Ningún artículo coincide con los filtros',
    showInactive: 'Muestra los dados de baja',
    inactiveBadge: 'Baja',
    colNature: 'Naturaleza',
    'nature.is_purchased': 'Se compra',
    'nature.is_manufactured': 'Se fabrica',
    'nature.is_sold': 'Se vende',
    noCategory: 'Sin categoría',
    noTax: 'Sin impuesto',
    selectAll: 'Selecciona todos los artículos visibles',
    selectedCount: 'Seleccionados: {{count}}',
    clearSelection: 'Vaciar la selección',
    bulkType: 'Cambiar la tipología',
    bulkNature: 'Cambiar la naturaleza',
    bulkCategory: 'Cambiar la categoría',
    bulkTax: 'Cambiar el impuesto',
    bulkApply: 'Aplicar el cambio',
    bulkConfirm: 'Artículos afectados por el cambio: {{count}}.',
    bulkNatureHint: 'Solo se modifican las marcas que cambies; las demás se mantienen como están.',
    bulkNatureManualNote:
      'Al desactivar «se fabrica», la información de seguridad alimentaria de estos artículos pasa a manual.',
    bulkNatureKeep: 'Dejarla como está',
    bulkNatureOn: 'Activarla',
    bulkNatureOff: 'Desactivarla',
    bulkMixed: 'Valores distintos',
  },

  'backoffice.articleForm': {
    linkStock: 'Existencias',
    linkPurchases: 'Compras',
    linkSales: 'Ventas',
    unsavedChanges: 'Cambios sin guardar',
    backToCatalog: 'Volver al catálogo',
    foodSafetyMode: 'Origen de la información',
    foodSafetyModeHint: 'Cómo se obtienen los ingredientes, los alérgenos y la información nutricional de la ficha técnica.',
    foodSafetyMode_manual: 'Manual — se informa en esta ficha',
    foodSafetyMode_derived: 'Calculada desde la receta',
    foodSafetyModeManualOnly: 'Manual. Una materia prima o un producto auxiliar no tiene receta de la que derivar la información.',
    safetyNotesLabel: 'Otras informaciones',
    derivedNoRecipe: 'Este artículo aún no tiene ninguna versión de receta publicada, así que no se puede calcular la información. Publica la receta o cambia el origen a manual.',
    derivedFromRecipe: 'Calculado de la versión {{version}} de la receta.',
    derivedNutritionEmpty: 'Ninguna materia prima de la receta declara información nutricional.',
    derivedNutritionBasisYield: 'Por 100 g de producto acabado, sobre el rendimiento de la receta.',
    derivedNutritionBasisBom: 'Por 100 g, sobre la suma de los ingredientes (la receta no tiene rendimiento en peso).',
    derivedNutritionCoverage: 'Solo el {{percent}}% del peso de la receta declara información nutricional: los valores quedan por debajo del real.',
    titleNew: 'Nuevo artículo',
    titleEdit: 'Ficha de artículo',
    subtitleNew: 'Alta de un artículo del catálogo',
    sectionIdentity: 'Identificación',
    sectionClassification: 'Clasificación y comportamiento',
    sectionUnits: 'Unidades y conversiones',
    sectionStock: 'Existencias y aprovisionamiento',
    sectionAllergens: 'Alérgenos',
    sectionDanger: 'Baja del artículo',
    sku: 'Código (SKU)',
    name: 'Nombre comercial',
    articleType: 'Tipología',
    category: 'Categoría',
    barcode: 'Código de barras',
    isPurchased: 'Se compra',
    isManufactured: 'Se fabrica',
    isSold: 'Se vende',
    baseUnit: 'Unidad base (existencias)',
    baseUnitPlaceholder: 'Selecciona una unidad',
    recipeUnit: 'Unidad de receta',
    recipeUnitHint:
      'La unidad en la que la receta pide esta materia prima. Las existencias y las compras siguen en la unidad base.',
    recipeUnitSameAsBase: 'La misma que la unidad base',
    recipeUnitSameAsBaseNamed: 'La misma que la base ({{code}})',
    recipeUnitUnavailable: 'Unidad no disponible',
    recipeFactor: 'Equivalencia de receta',
    recipeFactorLead: '1 {{base}} equivale a',
    recipeFactorAria: 'Cantidad de {{recipe}} que equivale a 1 {{base}}',
    recipeFactorExample:
      'Una receta que gaste 1 {{recipe}} descontará {{quantity}} {{base}} de las existencias.',
    recipeFactorRequired: 'Indica la equivalencia: la unidad de receta es distinta de la base.',
    recipeFactorNotPositive: 'La equivalencia debe ser un número mayor que cero.',
    recipeUnitChangeWarning_one:
      'Hay {{count}} línea de receta que lleva este artículo en {{unit}}. Si cambias la unidad de receta, dejará de tener equivalencia con la unidad base y las existencias se descontarán 1:1.',
    recipeUnitChangeWarning_other:
      'Hay {{count}} líneas de receta que llevan este artículo en {{unit}}. Si cambias la unidad de receta, dejarán de tener equivalencia con la unidad base y las existencias se descontarán 1:1.',
    validationError: 'No se ha guardado: revisa los campos marcados en rojo.',
    validationErrorField: 'No se ha guardado: revisa «{{field}}».',
    noUnits:
      'Todavía no hay unidades definidas para el obrador. Defínelas en Configuración antes de crear artículos.',
    isVariableConversion: 'Conversión variable',
    isVariableConversionHint:
      'Artículo sin equivalencia fija entre la unidad que se cuenta y la que se mide (p. ej. piezas cortadas): se pesa en cada movimiento.',
    shelfLifeDays: 'Vida útil (días)',
    shelfLifeHint: 'Determina la caducidad sugerida del lote al cerrar la OT.',
    vatRate: 'IVA (%)',
    tax: 'Impuesto',
    taxHint: 'Se elige del maestro de impuestos (Configuración · Impuestos)',
    taxNone: 'Sin impuesto',
    taxUnavailable: 'Impuesto dado de baja',
    valueUnavailable: 'No disponible',
    noTaxesMaster: 'No hay impuestos configurados. Crea alguno en Configuración · Impuestos.',
    minStock: 'Existencias mínimas',
    maxStock: 'Existencias máximas',
    minStockHint: 'Aviso de existencias bajo mínimo (nunca bloquea).',
    defaultSupplier: 'Proveedor por defecto',
    defaultPlace: 'Lugar por defecto',
    defaultPlaceHint: 'Dónde quedan las existencias al recibirlas o producirlas, sin preguntárselo a nadie. Puede ser una zona, una sección o una ubicación.',
    purchasePrice: 'Precio de compra',
    allergensManualHint:
      'Marca los alérgenos presentes en la materia prima o el embalaje (contiene / trazas).',
    allergensDerivedHint:
      'Los alérgenos de un producto acabado o semielaborado se derivan automáticamente del BOM de la receta. No se editan manualmente.',
    allergensDerivedEmpty:
      'Todavía no hay alérgenos derivados. Se agregarán del BOM cuando la receta esté definida (Onada 1 — M3).',
    presenceContains: 'Contiene',
    presenceTraces: 'Trazas',
    presenceNone: 'Ausente',
    save: 'Guarda el artículo',
    saved: 'Artículo guardado',
    saveError: 'No se ha podido guardar el artículo',
    allergensSaveError:
      'El artículo se ha guardado, pero no se han podido guardar los alérgenos. Vuelve a intentarlo.',
    conversionsSaveError:
      'El artículo se ha guardado, pero no se han podido guardar las conversiones. Vuelve a intentarlo.',
    create: 'Crea artículo',
    created: 'Artículo creado',
    createdBody: 'El artículo se ha añadido al catálogo.',
    createError: 'No se ha podido crear el artículo',
    cancel: 'Cancela',
    deactivate: 'Da de baja',
    deactivateTitle: 'Da de baja el artículo',
    deactivateBody:
      'Baja lógica reversible: el artículo desaparece del catálogo activo pero se conserva para la trazabilidad. Indica el motivo (obligatorio).',
    deactivatedBanner: 'Este artículo está dado de baja.',
    reactivate: 'Reactiva el artículo',
    notFound: 'No se ha encontrado el artículo',
    addIngredient: 'Añadir ingrediente',
    addPackagingUnit: 'Añadir unidad de embalaje',
    addSupplier: 'Añadir proveedor',
    complianceLabel: 'Legislación y cumplimiento',
    complianceSaveError: 'No se han podido guardar las declaraciones de cumplimiento.',
    conservationCondition: 'Condición de conservación',
    containerNotes: 'Notas del envase',
    containerSize: 'Tamaño',
    containerType: 'Tipo de envase',
    containerUnitsPer: 'Unidades por envase',
    containerWeight: 'Peso (g)',
    createIngredient: 'Crear ingrediente',
    createIngredientError: 'No se ha podido crear el ingrediente.',
    defaultLabelTemplate: 'Formato de etiqueta por defecto',
    description: 'Descripción',
    descriptionHint: 'Breve descripción que aparece en la ficha técnica',
    image: 'Imagen del producto',
    imageHint: 'JPG o PNG, hasta 5 MB',
    imageInvalidType: 'El archivo no es una imagen.',
    imageTooLarge: 'La imagen supera los 5 MB.',
    imageUploadError: 'No se ha podido subir la imagen.',
    imageUploading: 'Subiendo…',
    ingredient: 'Ingrediente',
    ingredientPercentage: '%',
    ingredientsHint: 'En orden decreciente de peso; aparecen en la ficha técnica',
    ingredientsLabel: 'Composición declarada',
    ingredientsSaveError: 'No se ha podido guardar la composición.',
    isManufacturedHint: 'Se fabrica con receta propia',
    isPurchasedHint: 'Se compra a un proveedor',
    isSoldHint: 'Se vende a cliente',
    isStockTracked: 'Controlar existencias',
    isStockTrackedHint: 'Si se desactiva, el artículo no genera movimientos ni avisos de stock (p. ej. agua de red), pero sigue disponible en recetas y ficha técnica.',
    lastPrice: 'Precio de compra (unidad base)',
    lastPriceUnit: 'Precio de compra ({{code}})',
    purchaseUnit: 'Unidad de compra',
    purchaseUnitHint: 'Formato en que se compra a este proveedor (Caja, Saco…)',
    purchaseUnitBase: 'Unidad base',
    moveDown: 'Bajar',
    moveUp: 'Subir',
    newIngredientName: 'Nuevo ingrediente',
    noComplianceItems: 'No hay declaraciones de cumplimiento configuradas. Añádelas en Configuración.',
    noIngredientsMaster: 'No hay ingredientes en el maestro. Crea uno abajo o en Configuración.',
    noPackagingUnits: 'No hay unidades de embalaje configuradas. Añádelas en Configuración.',
    noActivePrimarySupplier: 'El proveedor principal de este artículo ({{name}}) está dado de baja. El artículo no tiene ningún proveedor principal activo hasta que elijas otro.',
    noSuppliers: 'Todavía no hay proveedores vinculados.',
    noSuppliersMaster: 'No hay proveedores en el maestro. Créalos en Configuración.',
    nutritionLabel: 'Información nutricional (por 100 g)',
    packagingDefault: 'Compra por defecto',
    packagingDefaultBase: 'Compra por defecto: unidad base ({{code}})',
    packagingDefaultSales: 'Venta por defecto',
    packagingDefaultSalesBase: 'Venta por defecto: unidad base ({{code}})',
    packagingHint: '1 unidad de embalaje = N {{code}} (siempre contra la unidad base)',
    packagingRecipeEquivalence: 'Equivale a {{quantity}} {{unit}} para receta.',
    packagingQuantity: 'Equivale a',
    packagingSaveError: 'No se han podido guardar las unidades de embalaje.',
    packagingUnit: 'Unidad de embalaje',
    packagingUnitsLabel: 'Unidades de embalaje',
    pdfInvalidType: 'El archivo debe ser un PDF.',
    pdfTooLarge: 'El PDF supera los 10 MB.',
    preparationMode: 'Modo de preparación',
    removeSupplier: 'Quitar proveedor',
    priceHistory: 'Histórico de precios',
    removeSupplierBody: 'Debes indicar el motivo para quitar este proveedor. La vinculación se da de baja, no se elimina.',
    removeSupplierTitle: 'Quitar proveedor',
    salePrice: 'Precio de venta',
    salePriceHint: 'Por unidad base. Rellena por defecto el precio en las líneas de pedido de venta.',
    salePriceHintUnit: 'Por {{code}}. Rellena por defecto el precio en las líneas de pedido de venta.',
    sectionConservation: 'Conservación y uso',
    sectionLabeling: 'Etiquetado',
    sectionPackaging: 'Envasado y logística',
    sectionSafety: 'Seguridad alimentaria',
    sectionSafetyHint: 'Escribe la información de seguridad alimentaria en bloques de texto libre. Aparecerán en la ficha técnica en este mismo orden.',
    safetyBlockTitle: 'Título',
    safetyBlockTitlePlaceholder: 'Ej.: Alérgenos',
    safetyBlockBody: 'Texto',
    safetyBlockBodyPlaceholder: 'Escribe aquí el texto que quieres que aparezca en la ficha técnica',
    addSafetyBlock: 'Añadir bloque de texto',
    removeSafetyBlock: 'Quitar el bloque',
    moveSafetyBlockUp: 'Mover arriba',
    moveSafetyBlockDown: 'Mover abajo',
    safetyEmpty: 'Todavía no hay ningún bloque de texto. Añade uno para empezar.',
    safetyBodyRequired: 'El texto del bloque no puede quedar vacío',
    sectionSuppliers: 'Proveedores',
    skuAutoHint: 'Se generará automáticamente al guardar',
    supplier: 'Proveedor',
    supplierDescription: 'Descripción del proveedor',
    supplierInactiveHint: 'Proveedor dado de baja: no se puede usar en compras nuevas. El vínculo, el precio y la referencia se conservan para el histórico.',
    supplierPrimary: 'Proveedor principal',
    supplierRef: 'Código del proveedor',
    suppliersHint: 'Un proveedor principal como máximo',
    suppliersSaveError: 'No se han podido guardar los proveedores.',
    techSheet: 'Ficha técnica del proveedor',
    techSheetCreateHint: 'Guarda el artículo para adjuntar la ficha técnica',
    techSheetDownload: 'Descargar',
    techSheetUploadError: 'No se ha podido subir la ficha técnica.',
    techSheetUploading: 'Subiendo…',
    usageType: 'Tipo de uso',
    nutritionEnergyKj: 'Energía (kJ)',
    nutritionEnergyKcal: 'Energía (kcal)',
    nutritionFat: 'Grasas (g)',
    nutritionSaturatedFat: 'de las cuales saturadas (g)',
    nutritionCarbohydrate: 'Hidratos de carbono (g)',
    nutritionSugars: 'de los cuales azúcares (g)',
    nutritionProtein: 'Proteïnas (g)',
    nutritionSalt: 'Sal (g)',
  },

  'backoffice.technicalSheetScreen': {
    sourceManual: 'Información declarada en la ficha del artículo (origen manual).',
    sourceDerived: 'Calculado de la versión {{version}} de la receta activa.',
    ingredientsEmpty: 'Todavía no hay ingredientes que declarar.',
    allergensEmpty: 'Ningún alérgeno declarado.',
    title: 'Ficha técnica',
    subtitle: 'Documento reglamentario derivado del BOM (Reg. UE 1169/2011)',
    generateVersion: 'Genera versión',
    generating: 'Generando…',
    generateError: 'No se ha podido generar la versión',
    versionHistory: 'Historial de versiones',
    noVersions: 'Todavía no hay ninguna versión de ficha técnica',
    version: 'Versión',
    versionLabel: 'v{{n}}',
    date: 'Fecha',
    responsible: 'Responsable',
    status: 'Estado',
    statusDraft: 'Borrador',
    statusApproved: 'Aprobada',
    statusArchived: 'Archivada',
    ingredientsTitle: 'Ingredientes (orden ponderal)',
    allergensTitle: 'Alérgenos (14 UE, agregados)',
    nutritionTitle: 'Información nutricional / 100 g',
    foodSafetyTitle: 'Seguridad alimentaria',
    foodSafetyHint: 'Textos de seguridad alimentaria del artículo. Se editan en la ficha de artículo y quedan congelados en cada versión de la ficha técnica.',
    foodSafetyEmpty: 'Este artículo todavía no tiene textos de seguridad alimentaria.',
    legislationTitle: 'Legislación y normativa',
    legislationHint: 'Textos legales del obrador (Configuración · Legislación y normativa). Se imprimen como texto final de la ficha técnica.',
    legislationEmpty: 'Todavía no hay textos de legislación configurados.',
    nutritionPlaceholder: 'Pendiente (Onada 2 — M15)',
    labelNote:
      'La etiqueta NO se genera aquí: se genera al cerrar la OT o al envasar (con lote y caducidad).',
    conservation: 'Conservación',
    netWeight: 'Peso neto',
    legalDenomination: 'Denominación legal',
    shelfLifeDays: 'Vida útil (días)',
    noAllergens: 'Sin alérgenos declarados',
    bomNoRecipe:
      'Este artículo no tiene ninguna receta con versión publicada. Define y publica la receta para que la ficha pueda sacar los ingredientes.',
    allergensFromBom: 'Alérgenos agregados automáticamente del BOM de la receta.',
    onlyFinishedHint: 'La ficha técnica solo se genera para artículos fabricados (elaborados con receta propia).',
    nutritionHint: 'Valores por 100 g (Reg. UE 1169/2011). Se editan en la ficha de artículo y quedan congelados en cada versión de la ficha técnica.',
    nutritionEmpty: 'Todavía no hay información nutricional.',
    nutritionEnergyKj: 'Energía (kJ)',
    nutritionEnergyKcal: 'Energía (kcal)',
    nutritionFat: 'Grasas (g)',
    nutritionSaturatedFat: 'de las cuales saturadas (g)',
    nutritionCarbohydrate: 'Hidratos de carbono (g)',
    nutritionSugars: 'de los cuales azúcares (g)',
    nutritionProtein: 'Proteínas (g)',
    nutritionSalt: 'Sal (g)',
    diffModalTitle: 'Cambios de la nueva versión',
    diffModalIntro: 'Revisa qué cambia respecto a la versión vigente antes de generar el borrador.',
    diffFirstVersion: 'No hay ninguna versión anterior: se creará la primera versión de la ficha técnica.',
    diffNoChanges: 'Ningún cambio respecto a la versión vigente.',
    diffKindAdded: 'Añadido',
    diffKindRemoved: 'Eliminado',
    diffKindChanged: 'Modificado',
    diffKindUnchanged: 'Sin cambios',
    diffFieldIngredients: 'Ingredientes',
    diffFieldAllergens: 'Alérgenos',
    diffFieldShelfLife: 'Vida útil (días)',
    diffFieldNutrition: 'Información nutricional',
    diffFieldRecipeVersion: 'Versión de receta',
    confirmGenerate: 'Genera y aprueba la versión',
    colActions: 'Acciones',
    archive: 'Archiva',
    archiving: 'Archivando…',
    archiveError: 'No se ha podido archivar la versión',
    frozenError: 'Una versión aprobada es un documento congelado por el servidor: no se puede modificar. Genera una versión nueva.',
    pdfDocumentTitle: 'Ficha técnica de producto',
    pdfVersion: 'Versión',
    pdfRevisionDate: 'Fecha de revisión',
    pdfArticleName: 'Nombre artículo',
    pdfProductCode: 'Código producto',
    pdfDescription: 'Descripción producto',
    pdfUsage: 'Tipo de uso',
    pdfNutritionTitle: 'Información nutricional por 100 g',
    pdfConservationTitle: 'Condiciones de conservación',
    pdfContainerType: 'Tipo de envase',
    pdfShelfLife: 'Consumo preferente',
    pdfLogisticsTitle: 'Datos logísticos',
    pdfContainerFormatTitle: 'Formato del envase',
    pdfUnitsPerBox: 'Ud./caja',
    pdfBoxesPerPallet: 'Cajas/palé',
    pdfDimensions: 'Dimensiones (H×L×P)',
    pdfContains: 'Contiene',
    pdfMayContain: 'Puede contener',
    pdfLegislationTitle: 'Legislación',
    pdfDays: 'días',
    downloadPdf: 'Descargar PDF',
    generatingPdf: 'Generando…',
    pdfGenerationError: 'No se ha podido generar el PDF de la ficha técnica.',
  },

  'backoffice.articleTypeLabel': {
    raw_material: 'Materia prima',
    intermediate: 'Producto semielaborado',
    finished: 'Producto acabado',
    packaging: 'Producto auxiliar',
  },

  'backoffice.articleCategoryLabel': {
    raw_material: 'Materia prima',
    auxiliary: 'Auxiliar',
    intermediate: 'Semielaborado',
    finished: 'Acabado',
    packaging: 'Embalaje',
  },

  'backoffice.recipes': {
    // List (screen 13)
    title: 'Recetas',
    subtitle: 'Recetas del obrador con versionado inmutable',
    newRecipe: 'Nueva receta',
    searchPlaceholder: 'Busca por nombre o artículo',
    colName: 'Receta',
    colOutput: 'Artículo producido',
    colVersion: 'Versión activa',
    empty: 'Todavía no hay ninguna receta',
    emptyFiltered: 'Ninguna receta coincide con la búsqueda',
    showInactive: 'Mostrar las dadas de baja',
    inactiveBadge: 'Baja',
    noActiveVersion: 'Sin versión activa',
    draftBadge: 'Borrador',
    create: 'Crea receta',
    newVersion: 'Nueva versión',
    // Detail (screen 13)
    detailSubtitle: 'Versión activa, BOM en orden ponderal e historial',
    backToList: 'Volver a recetas',
    activeVersion: 'Versión activa',
    versionLabel: 'Versión {{n}}',
    noActiveVersionTitle: 'Esta receta todavía no tiene ninguna versión publicada',
    noActiveVersionBody:
      'Publica una versión borrador para que aparezca en el catálogo y se pueda crear OT.',
    sectionMeta: 'Datos de la versión',
    sectionIngredients: 'Ingredientes (orden ponderal)',
    sectionSteps: 'Elaboración',
    sectionAllergens: 'Alérgenos (14 UE, agregados del BOM)',
    sectionScaling: 'Escalado de receta',
    sectionVersions: 'Historial de versiones',
    recipeType: 'Tipo de receta',
    recipeTypeHint:
      'Una de envasado consume el granel y el envase, y en planta pregunta de qué envases quieres etiqueta.',
    recipeType_normal: 'Normal',
    recipeType_packaging: 'Envasado',
    leadTimeDays: 'Tiempo de antelación (días)',
    notes: 'Notas',
    colIngredient: 'Ingrediente',
    colQuantity: 'Cantidad',
    colUnit: 'Unidad',
    colStep: 'Paso',
    colDescription: 'Nombre',
    colDuration: 'Duración (min)',
    colTemperature: 'Temperatura (°C)',
    colControl: 'Control',
    blockingBadge: 'Bloqueante',
    expandIntermediate: 'Despliega el BOM',
    collapseIntermediate: 'Repliega',
    intermediateNoRecipe: 'Este semielaborado no tiene receta publicada',
    noIngredients: 'Esta versión no tiene ingredientes',
    noSteps: 'Esta versión no tiene pasos de elaboración',
    noAllergens: 'Sin alérgenos declarados',
    presenceTraces: 'Trazas',
    // Scaling
    scalingHint:
      'Introduce un rendimiento objetivo o un factor para ver las cantidades escaladas. No modifica la receta.',
    scalingTargetYield: 'Rendimiento objetivo',
    scalingFactor: 'Factor',
    scalingResultTitle: 'Cantidades escaladas',
    scalingInvalid: 'Introduce un valor positivo',
    // Version history
    versionNumber: 'Versión',
    versionStatus: 'Estado',
    versionDate: 'Fecha',
    statusDraft: 'Borrador',
    statusPublished: 'Publicada',
    statusActive: 'Activa',
    openDraft: 'Edita el borrador',
    viewVersion: 'Consultar',
    // Actions
    newVersionFromActive: 'Nueva versión',
    editDraft: 'Edita',
    publish: 'Publica',
    publishing: 'Publicando…',
    discard: 'Descarta',
    discarding: 'Descartando…',
    publishHint:
      'Al publicar se congela esta versión, se agregan los alérgenos del BOM y pasa a ser la versión activa de la receta.',
    fixValidation: 'Revisa las filas marcadas antes de guardar o publicar.',
    packagingNotDeclared:
      'Los envases que haya en esta lista los consume la orden de trabajo como cualquier otra l\u00ednea, pero NO salen en la declaraci\u00f3n de ingredientes de la ficha t\u00e9cnica ni de la etiqueta: all\u00ed solo va lo que se come.',
    publishNeedsIngredients:
      'Una versi\u00f3n sin ingredientes no se puede publicar: de esta lista salen los al\u00e9rgenos, la ficha t\u00e9cnica y la etiqueta. A\u00f1ade al menos un ingrediente. El borrador puedes guardarlo igualmente.',
    rescaleOffer:
      'El rendimiento ha pasado de {{from}} a {{to}}. \u00bfQuieres multiplicar los ingredientes por {{factor}}? Si lo que has hecho es corregir un rendimiento mal escrito, d\u00e9jalos como est\u00e1n.',
    rescaleApply: 'Reescala los ingredientes',
    rescaleKeep: 'D\u00e9jalos como est\u00e1n',
    // Editor
    editorTitleNew: 'Nueva receta',
    editorTitleEdit: 'Edita borrador',
    editorSubtitleNew: 'Crea una receta y su versión 1 (borrador)',
    editorSubtitleEdit: 'Edita la versión borrador antes de publicar',
    editorSectionMeta: 'Planificación',
    editorSectionOutputs: 'Resultado de la receta',
    editorSectionIngredients: 'Materia prima',
    editorSectionSteps: 'Elaboración',
    editorSectionResources: 'Recursos',
    name: 'Nombre de la receta',
    // Resultado de la receta (outputs table). The first line is the recipe's
    // main product; the rest are additional products of the same elaboración.
    outputProduct: 'Producto',
    outputsHint:
      'La primera línea es el producto principal de la receta. Añade más si la elaboración produce otros.',
    addOutput: 'Añade línea',
    removeOutput: 'Elimina',
    addIngredient: 'Añade ingrediente',
    removeIngredient: 'Elimina',
    moveUp: 'Arriba',
    moveDown: 'Abajo',
    ingredient: 'Ingrediente',
    quantity: 'Cantidad',
    unit: 'Unidad',
    addStep: 'Añade un paso en blanco',
    // Recursos (lo que ocupa la elaboración y no es materia prima)
    resource: 'Tipo de recurso',
    resourceQuantity: 'Cantidad',
    resourceDuration: 'Tiempo (min)',
    selectResource: 'Selecciona un tipo',
    addResource: 'Añade recurso',
    removeResource: 'Elimina',
    noResources: 'Todavía no hay recursos en esta receta.',
    noResourcesMaster:
      'No hay tipos de recurso definidos para el obrador. Defínelos en Configuración · Recursos para poder pedirlos en la receta.',
    colResource: 'Tipo de recurso',
    colResourceDuration: 'Tiempo (min)',
    resourceLegacyMachine: 'recurso fijado: {{name}}',
    removeStep: 'Elimina',
    process: 'Proceso',
    selectProcess: 'Selecciona un proceso',
    // Pasos de elaboración (OBR-17): instancias editables, con ingredientes y recursos propios
    stepLabel: 'Paso {{n}}',
    stepName: 'Nombre del paso',
    stepNamePlaceholder: 'p. ej. Amasado inicial',
    stepInstructions: 'Instrucciones',
    stepInstructionsPlaceholder: 'Qué hay que hacer en este paso (opcional)',
    stepControl: 'Control',
    stepProcessHint:
      'Opcional. Un paso se puede escribir a mano; si eliges un proceso, se rellenan sus valores como punto de partida y luego los adaptas a la receta.',
    noProcess: 'Sin proceso',
    processRetired: 'Proceso dado de baja',
    addStepFromProcess: 'Añade un paso desde un proceso',
    stepFromProcessPlaceholder: 'Elige un proceso…',
    reapplyProcess: 'Vuelve a aplicar los valores del proceso',
    reapplyConfirmTitle: 'Vuelve a aplicar el proceso «{{process}}»',
    reapplyConfirmBody: 'Este paso ya tiene valores escritos que no coinciden con los del proceso. Si continúas, se sobrescribirán con los del proceso y se perderá lo que había escrito en estos campos:',
    reapplyConfirmAction: 'Sobrescribe el paso',
    removeStepConfirmTitle: 'Elimina el {{step}}',
    removeStepConfirmBody: 'Se eliminará el paso «{{name}}» con las instrucciones, el control y los recursos que has definido.',
    removeStepConfirmIngredients_one: 'El ingrediente que entra se queda en el BOM, sin paso: {{names}}.',
    removeStepConfirmIngredients_other: 'Los {{count}} ingredientes que entran se quedan en el BOM, sin paso: {{names}}.',
    removeStepConfirmUnsaved: 'Todavía no se ha guardado: si te arrepientes, sal sin guardar y se mantiene la versión guardada.',
    removeStepConfirmAction: 'Elimina el paso',
    duplicateOutput: 'Ya existe una receta para «{{name}}». Solo se puede tener una receta por artículo: ábrela y crea una versión nueva.',
    duplicateOutputGeneric: 'Ya existe una receta para este artículo. Solo se puede tener una receta por artículo: ábrela y crea una versión nueva.',
    openExistingRecipe: 'Abre la receta existente',
    duplicateStep: 'Duplica',
    stepResources: 'Recursos necesarios',
    noStepResources: 'Este paso no necesita ningún recurso.',
    stepIngredients: 'Ingredientes que se incorporan',
    noStepIngredients: 'Ninguno. Se asignan desde la sección de materia prima.',
    stepOrderHint:
      'Arrastra un paso por el nombre o usa las flechas para cambiar su orden. La numeración es solo de visualización.',
    noStepsYet: 'Todavía no hay ningún paso. Añade uno en blanco o desde un proceso.',
    ingredientStep: 'Se incorpora en el paso',
    ingredientStepNone: 'Sin paso',
    ingredientStepHint:
      'Opcional: el paso en que se incorpora cada ingrediente, para que la App lo pueda mostrar cuando toque.',
    stepMissing: 'El paso asignado a este ingrediente ya no existe',
    looseResourcesTitle: 'Recursos sin paso asignado',
    looseResourcesHint:
      'Líneas de recursos que la receta pedía en conjunto, de antes de que los recursos fueran por paso. Se mantienen tal cual; para asignarlas a un paso, añádelas al paso y elimínalas de aquí.',
    saveNothing: 'No hay cambios que guardar',
    stepDuration: 'Duración (min)',
    stepTemperature: 'Temperatura (°C)',
    isBlocking: 'Bloqueante (solo PCC)',
    save: 'Guarda el borrador',
    saved: 'Borrador guardado',
    cancel: 'Cancela',
    noUnits:
      'Todavía no hay unidades definidas para el obrador. Defínelas en Configuración antes de crear recetas.',
    noOutputArticles:
      'No hay artículos fabricados o acabados en el catálogo. Crea uno antes de definir una receta.',
    noProcesses:
      'Todavía no hay procesos definidos. Puedes escribir los pasos a mano, o definir procesos en Configuración · Procesos para que sirvan de plantilla.',
    selectArticle: 'Selecciona un artículo',
    selectUnit: 'Selecciona una unidad',
    readOnly: 'Solo lectura',
    // Errors / validation
    requiredIngredient: 'Hace falta al menos un ingrediente',
    blockingNeedsPcc: 'Solo un paso con control PCC puede ser bloqueante',
    outputCannotBeIngredient:
      'Una receta no puede llevar como ingrediente un artículo que ella misma produce',
    createError: 'No se ha podido crear la receta',
    loadError:
      'No se han podido cargar los datos de la receta. Inténtalo de nuevo antes de editarla.',
    saveError: 'No se ha podido guardar el borrador',
    publishError: 'No se ha podido publicar la versión',
    publishErrorActivate:
      'La versión se ha publicado pero no se ha podido marcar como activa. Vuelve a probar a publicar.',
    discardError: 'No se ha podido descartar el borrador',
    unresolvedAllergensWarning:
      'Algunos semielaborados del BOM no tienen receta publicada; sus alérgenos no se han podido agregar.',
    // Discard dialog
    discardTitle: 'Descarta el borrador',
    discardBody:
      'Se eliminará esta versión borrador y su BOM y pasos. Si es la única versión, también se dará de baja la receta. Indica el motivo (obligatorio).',
    notFound: 'No se ha encontrado la receta',
    // Step control type enum labels
    'controlType.none': 'Ninguno',
    'controlType.weight': 'Pesaje',
    'controlType.timer': 'Temporizador',
    'controlType.temperature': 'Temperatura',
    'controlType.pcc': 'PCC',
      tolerancePct: 'Tolerancia (%)',
    tolerancePctHint: 'Vacío = se aplica la tolerancia del obrador',
    lineStockEquivalent: 'Existencias: {{quantity}} {{unit}} de consumo teórico.',
    lineNoEquivalenceUseBase:
      '«{{unit}}» no tiene equivalencia con la unidad base del artículo ({{base}}): las existencias se descontarían 1:1. Pon la línea en {{base}}.',
    lineNoEquivalence:
      '«{{unit}}» no tiene equivalencia con la unidad base del artículo ({{base}}): las existencias se descontarían 1:1. Configura la unidad de receta en la ficha del artículo.',
    tolerancePctPlaceholder: 'Obrador',
    sequentialPreparation: 'Preparación secuencial',
    sequentialPreparationHint: 'El operario prepara los ingredientes uno a uno, en este orden',
    dragToReorder: 'Arrastra para reordenar',
    dragHandle: 'Reordena el ingrediente',
    ingredientOrderHint: 'El orden de esta lista es el orden de preparación en planta',
},

  'backoffice.stock': {
    articleTitle: 'Existencias — {{article}}',
    articleCard: 'Ficha del artículo',
    allArticles: 'Todas las existencias',
    articleEmpty: 'Este artículo todavía no tiene existencias registradas.',
    title: 'Existencias',
    subtitle: 'Existencias por artículo, lote y caducidad',
    available: 'Disponible',
    blocked: 'Bloqueado',
    blockedChip: 'Bloqueado: {{quantity}} {{unit}}',
    blockedHint:
      'Excepciones de recepción pendientes de decisión y devoluciones pendientes de devolver: no cuenta como disponible para producción.',
    blockedSummary:
      'Físico: {{physical}} {{unit}} · Bloqueado: {{blocked}} {{unit}} · Disponible: {{free}} {{unit}}',
    byLot: 'Por lote',
    byZone: 'Por zona',
    belowMinimum: 'Bajo mínimo',
    // Article list
    searchPlaceholder: 'Busca por SKU o nombre',
    filterType: 'Tipología',
    filterStatus: 'Estado',
    statusAll: 'Todos',
    statusExpiring: 'Caduca pronto',
    statusExpired: 'Caducado',
    expiringBadge: 'Caduca pronto',
    expiredBadge: 'Caducado',
    typeAll: 'Todas',
    typePurchase: 'Compra',
    typeIntermediate: 'Semielaborado',
    typeSale: 'Venta',
    colSku: 'SKU',
    colName: 'Artículo',
    colType: 'Tipología',
    colAvailable: 'Disponible',
    colCommitted: 'Comprometido',
    colUnit: 'Unidad',
    empty: 'Todavía no hay existencias. Se generarán con las recepciones y la producción.',
    emptyFiltered: 'Ningún artículo coincide con el filtro.',
    loadError: 'No se han podido cargar las existencias',
    // Lot breakdown
    lotBreakdown: 'Desglose por lote',
    colLot: 'Lote',
    colExpiry: 'Caducidad',
    colPlace: 'Lugar',
    colQuantity: 'Cantidad',
    colStatus: 'Estado',
    noLots: 'Este artículo no tiene existencias por lote.',
    noPlace: 'Sin lugar',
    // Existencias por lugar (filtro, OBR-90)
    filterPlace: 'Lugar',
    placeAll: 'Todos los lugares',
    colInPlace: 'En este lugar',
    placeNoteZone: 'Existencias en {{place}}, incluidas sus secciones y ubicaciones.',
    placeNoteSection: 'Existencias en {{place}}, incluidas sus ubicaciones.',
    placeNoteLocation: 'Existencias en {{place}}.',
    placeNoteNone: 'Existencias que aún no tienen lugar. Desde el desglose por lote, «Mover» las lleva donde toca.',
    emptyPlace: 'No hay existencias en este lugar.',
    noLotsInPlace: 'Este artículo no tiene lotes en este lugar.',
    noExpiry: 'Sin caducidad',
    supplierLotCode: 'Lote del proveedor: {{code}}',
    expand: 'Muestra los lotes',
    collapse: 'Oculta los lotes',
    'lotStatus.available': 'Disponible',
    'lotStatus.quarantined': 'En cuarentena',
    'lotStatus.consumed': 'Consumido',
    'lotStatus.expired': 'Caducado',
    notTracked: 'Sin control de existencias',
    inBaseUnit: '(= {{quantity}} {{unit}})',
    // Mover existencias (traspaso entre lugares del obrador)
    moveAction: 'Mover',
    moveTitle: 'Mover existencias',
    moveBody: 'Cambia dónde están las existencias. El total del artículo no varía: quedan dos movimientos de traspaso, uno de salida y uno de entrada.',
    moveFrom: 'Origen',
    moveTo: 'Destino',
    moveToPlaceholder: 'Elige adónde van',
    moveQuantity: 'Cantidad',
    moveAvailable: 'Disponible en el origen: {{quantity}} {{unit}}',
    moveQuantityInvalid: 'Debe ser mayor que cero y no puede superar lo que hay en el origen.',
    moveNote: 'Nota',
    moveNotePlaceholder: 'Opcional',
    moveConfirm: 'Mover',
    moving: 'Moviendo…',
    moveError: 'No se han podido mover las existencias.',
    moveErrorInsufficient: 'En el origen ya no hay suficiente. Vuelve a abrir el lote para ver lo que queda.',
    moveNoDestinations: 'No hay ningún otro lugar adonde moverlas. Configura la estructura del obrador.',
  },

  'backoffice.inventory': {
    title: 'Inventario',
    startCount: 'Inicia recuento',
    adjust: 'Ajusta',
    adjustReason: 'Motivo del ajuste',
    // Adjust dialog (screen 14)
    adjustTitle: 'Ajusta existencias',
    adjustBody:
      'Los movimientos de existencias nunca se editan. Un ajuste crea un movimiento nuevo con el motivo documentado.',
    delta: 'Variación (signo incluido)',
    deltaHint: 'Positivo para añadir existencias, negativo para restarlas.',
    deltaInvalid: 'Indica una variación distinta de cero.',
    reason: 'Motivo del ajuste',
    reasonNote: 'Nota del motivo',
    reasonNotePlaceholder: 'Describe el motivo del ajuste',
    confirm: 'Registra el ajuste',
    adjusting: 'Registrando…',
    adjustError: 'No se ha podido registrar el ajuste',
    selectReason: 'Selecciona un motivo',
  },

  'backoffice.movements': {
    title: 'Movimientos de existencias',
    type: 'Tipo',
    reason: 'Motivo',
    reverse: 'Contrarresta',
    appendOnlyNote: 'Los movimientos no se editan; las correcciones son contramovimientos',
    // Ledger (screen 14)
    colDate: 'Fecha',
    colType: 'Tipo',
    colQuantity: 'Cantidad',
    colReason: 'Motivo',
    colPlace: 'Lugar',
    colWho: 'Registrado por',
    empty: 'Todavía no hay ningún movimiento para este artículo.',
    loadError: 'No se han podido cargar los movimientos',
    quantityWithUnit: '{{quantity}} {{unit}}',
  },

  'backoffice.movementType': {
    reception: 'Recepción',
    o01_consumption: 'Consumo de OT',
    production_output: 'Producción',
    adjustment: 'Ajuste',
    write_off: 'Baja',
    reversal: 'Contramovimiento',
    shipment: 'Expedición',
    transfer: 'Traspaso',
    purchase_return: 'Devolución de compra',
  },

  'backoffice.movementReason': {
    recount: 'Recuento',
    loss: 'Merma',
    damage: 'Daño',
    expired: 'Caducado',
    burnt: 'Quemado',
    dropped: 'Caído',
    correction: 'Corrección',
    reception: 'Recepción',
    production: 'Producción',
    consumption: 'Consumo',
  },

  'backoffice.purchaseOrders': {
    articleTitle: 'Compras — {{article}}',
    articleCard: 'Ficha del artículo',
    allOrders: 'Todos los pedidos',
    articleEmpty: 'De este artículo no se ha comprado nunca: ningún pedido ni albarán de compra.',
    articleLinesTitle: 'Líneas de pedido',
    articleNoLines: 'Este artículo no aparece en ningún pedido de compra.',
    articleReceptionsTitle: 'Recepciones',
    articleNoReceptions: 'Este artículo no se ha recibido en ningún albarán.',
    totalOrdered: 'Pedido',
    totalReceived: 'Recibido',
    totalPending: 'Pendiente de recibir',
    unmeasurableLines_one:
      '{{count}} línea no entra en los totales: su formato no tiene conversión configurada para el artículo.',
    unmeasurableLines_other:
      '{{count}} líneas no entran en los totales: su formato no tiene conversión configurada para el artículo.',
    notVisibleToPlanta:
      'Este pedido todavía no es visible en la app de planta. Confírmalo para que el operario pueda recibirlo.',
    title: 'Pedidos de compra',
    newOrder: 'Nuevo pedido',
    allSuppliers: 'Todos los proveedores',
    searchSupplier: 'Busca un proveedor',
    noSupplierMatches: 'Ning\u00fan proveedor coincide',
    expectedFrom: 'Entrega prevista desde',
    expectedTo: 'Entrega prevista hasta',
    clearFilters: 'Quita los filtros',
    searchLabel: 'Busca por número o proveedor',
    emptyFiltered: 'Ning\u00fan pedido encaja con estos filtros. Prueba a ampliarlos o qu\u00edtalos.',
    selectAll: 'Selecciona todos los pedidos visibles',
    selectedCount_one: '{{count}} pedido seleccionado',
    selectedCount_other: '{{count}} pedidos seleccionados',
    clearSelection: 'Vaciar la selecci\u00f3n',
    colSent: 'Enviado',
    finishOrder: 'Finalizar',
    finishOrderLabel: 'Finalizar el pedido {{number}}',
    closedWithPending: 'Con pendiente',
    closedWithPendingHint: 'Finalizado con pendiente de recibir: lo que faltaba ya no se espera.',
    bulkConfirm: 'Confirma ({{count}})',
    bulkSend_one: 'Envía ({{count}})',
    bulkSend_other: 'Envía ({{count}})',
    bulkApply: 'Aplica la acci\u00f3n',
    bulkConfirmTitle: 'Confirma los pedidos',
    bulkSendTitle: 'Envía los pedidos',
    bulkConfirmBody_one:
      'Se confirmar\u00e1 {{count}} de los {{selected}} marcados: pasar\u00e1 a \u00abpendiente de entrega\u00bb. El resto se quedan como est\u00e1n y te dir\u00e1 por qu\u00e9.',
    bulkConfirmBody_other:
      'Se confirmar\u00e1n {{count}} de los {{selected}} marcados: pasar\u00e1n a \u00abpendiente de entrega\u00bb. El resto se quedan como est\u00e1n y te dir\u00e1 por qu\u00e9.',
    bulkSendBody_one: 'Se enviará {{count}} de los {{selected}} marcados por el canal de su proveedor: por correo si lo tiene, o marcado como enviado si recibe los pedidos por otros medios. Las de proveedores que las reciben por WhatsApp no salen solas: te las ofreceremos para abrirlas y enviarlas tú. El resto se queda como está y te dirá por qué.',
    bulkSendBody_other: 'Se enviarán {{count}} de los {{selected}} marcados por el canal de su proveedor: por correo si lo tiene, o marcados como enviados si recibe los pedidos por otros medios. Las de proveedores que las reciben por WhatsApp no salen solas: te las ofreceremos para abrirlas y enviarlas tú. El resto se queda como está y te dirá por qué.',
    bulkFinish: 'Finalizar ({{count}})',
    bulkFinishTitle: 'Finalizar los pedidos',
    bulkFinishBody_one:
      'Se finalizará {{count}} de los {{selected}} marcados: confirmas que no esperas el resto. Pasará a «Entregado» con lo que se ha recibido; los albaranes y las existencias no cambian, y lo que falta queda registrado como no recibido con tu nombre, la fecha y el motivo. El resto se quedan como están y te dirá por qué.',
    bulkFinishBody_other:
      'Se finalizarán {{count}} de los {{selected}} marcados: confirmas que no esperas el resto. Pasarán a «Entregado» con lo que se ha recibido; los albaranes y las existencias no cambian, y lo que falta de cada uno queda registrado como no recibido con tu nombre, la fecha y el mismo motivo. El resto se quedan como están y te dirá por qué.',
    bulkFinishConfirm_one: 'Finalizar {{count}} pedido',
    bulkFinishConfirm_other: 'Finalizar {{count}} pedidos',
    bulkFinishBlockedClosed: '{{order}}: ya lo tiene todo recibido, no hay nada que finalizar.',
    bulkFinishBlockedNothing: '{{order}}: todavía no se ha recibido nada; si no tiene que llegar, cancélalo.',
    bulkFinished_one: '{{count}} pedido finalizado',
    bulkFinished_other: '{{count}} pedidos finalizados',
    skip_nothing_received_one: '{{count}} todavía no ha recibido nada (si no tiene que llegar, se cancela)',
    skip_nothing_received_other: '{{count}} todavía no han recibido nada (si no tienen que llegar, se cancelan)',
    bulkApplied_one: '{{count}} pedido actualizado',
    bulkApplied_other: '{{count}} pedidos actualizados',
    skip_already_confirmed_one: '{{count}} ya estaba confirmado',
    skip_already_confirmed_other: '{{count}} ya estaban confirmados',
    skip_not_confirmed_one: '{{count}} todav\u00eda es un borrador sin confirmar',
    skip_not_confirmed_other: '{{count}} todav\u00eda son borradores sin confirmar',
    skip_already_sent_one: '{{count}} ya constaba enviado',
    skip_already_sent_other: '{{count}} ya constaban enviados',
    skip_closed_one: '{{count}} ya ha llegado',
    skip_closed_other: '{{count}} ya han llegado',
    skip_cancelled_one: '{{count}} est\u00e1 cancelado',
    skip_cancelled_other: '{{count}} est\u00e1n cancelados',
    bulkFailed_one: '{{count}} ha fallado: {{orders}}',
    bulkFailed_other: '{{count}} han fallado: {{orders}}',
    orderNumber: 'Núm.',
    supplier: 'Proveedor',
    orderDate: 'Fecha',
    expectedDate: 'Entrega prevista',
    status: 'Estado',
    lines: 'Líneas',
    empty: 'Todavía no hay pedidos de compra',
    searchPlaceholder: 'Busca por número o proveedor…',
    bulkEmailed_one: '{{count}} enviado por correo',
    bulkEmailed_other: '{{count}} enviados por correo',
    bulkMarked_one: '{{count}} marcado como enviado',
    bulkMarked_other: '{{count}} marcados como enviados',
    bulkWhatsapp_one: '{{count}} es de un proveedor que la recibe por WhatsApp: no se ha enviado, ábrela desde aquí',
    bulkWhatsapp_other: '{{count}} son de proveedores que las reciben por WhatsApp: no se han enviado, ábrelas desde aquí',
    bulkWhatsappOpen: 'Ábrelas por WhatsApp',
    whatsappQueueTitle: 'Pedidos por WhatsApp',
    whatsappQueueBody: 'Cada pedido se abre en WhatsApp con el mensaje escrito y lo envías tú. WhatsApp no nos dice si se ha enviado: cuando lo hayas hecho, pulsa «Ya lo he enviado» para que quede constancia.',
    whatsappQueueOpen: 'Abre WhatsApp',
    whatsappQueueSent: 'Ya lo he enviado',
    whatsappQueueDone: 'Consta como enviado',
    whatsappQueueNoNumber: 'Sin número utilizable: hace falta el prefijo del país en la ficha del proveedor',
    whatsappQueueLoading: 'Preparando los mensajes…',
    whatsappQueueError: 'No se ha podido registrar el envío de este pedido.',
    whatsappQueueClose: 'Cierra',
    skip_no_recipients_one: '{{count}} no tiene ningún correo del proveedor',
    skip_no_recipients_other: '{{count}} no tienen ningún correo del proveedor',
    skip_not_found_one: '{{count}} no se ha encontrado',
    skip_not_found_other: '{{count}} no se han encontrado',
  },

  'backoffice.purchaseTemplates': {
    title: 'Plantillas',
    newTemplate: 'Nueva plantilla',
    editTemplate: 'Edita la plantilla',
    empty:
      'Todavía no hay ninguna plantilla. Una plantilla guarda el pedido que se repite —proveedor, artículos, unidades y cantidades— para no tener que montarlo cada vez.',
    colName: 'Plantilla',
    colNotes: 'Notas',
    name: 'Nombre de la plantilla',
    nameHint: 'Como la llamáis entre vosotros: «Semanal», «Reposición urgente».',
    nameRequired: 'Ponle un nombre a la plantilla.',
    nameTaken: 'Este proveedor ya tiene una plantilla con este nombre. Ponle otro.',
    notesHint: 'Viajan al pedido que se genere; allí se pueden cambiar.',
    usualQuantity: 'Cantidad habitual',
    emptyLines: 'Añade al menos un artículo a la plantilla.',
    createOrder: 'Crea pedido',
    saveAsTemplate: 'Guarda como plantilla',
    deleteTitle: 'Da de baja la plantilla',
    deleteBody:
      'Dejará de aparecer en la lista y en el campo «Plantilla» de Nuevo pedido. Los pedidos que se hayan hecho con ella no se tocan.',
  },
  'backoffice.priceHistory': {
    title: 'Histórico de precios',
    subtitle: '{{supplier}} · precios por {{unit}}, sin IVA',
    subtitleNoUnit: '{{supplier}} · precios por unidad base, sin IVA',
    intro: 'Cada cambio del precio de la ficha queda registrado. Lo que se paga en un pedido concreto no aparece aquí: se queda en el documento.',
    loadError: 'No se ha podido cargar el histórico de precios.',
    empty: 'Todavía no hay ningún cambio de precio para esta relación.',
    colWhen: 'Fecha',
    colBefore: 'Precio anterior',
    colAfter: 'Precio nuevo',
    colChange: 'Cambio',
    colSource: 'Origen',
    colWho: 'Quién',
    kindInitial: 'Primera carga',
    kindCleared: 'Precio borrado',
    sourceManual: 'Ficha del artículo',
    sourceImport: 'Importación',
    sourcePurchaseOrder: 'Pedido {{number}}',
    sourcePurchaseOrderUnknown: 'Pedido',
    sourceLegacy: 'Anterior al histórico',
    system: 'Sistema',
    close: 'Cerrar',
  },
  'backoffice.purchaseOrderDetail': {
    newTitle: 'Nuevo pedido de compra',
    editTitle: 'Pedido {{number}}',
    supplier: 'Proveedor',
    selectSupplier: 'Selecciona un proveedor',
    orderDate: 'Fecha de pedido',
    expectedDate: 'Fecha de entrega prevista',
    deliveryDaysHint: 'Reparte {{days}}',
    template: 'Plantilla',
    templatePlaceholder: 'Parte de una plantilla (opcional)',
    templateHint:
      'Rellena el proveedor, los artículos, las unidades y las cantidades. Todo se puede cambiar antes de guardar.',
    noTemplateMatches: 'Ninguna plantilla coincide',
    templateSupplierInactive: 'El proveedor de esta plantilla está dado de baja y no admite pedidos nuevos. Reactívalo o cambia el proveedor en la plantilla.',
    replaceTemplateTitle: '¿Sustituir por la plantilla?',
    replaceTemplateBody:
      'El pedido pasará a ser el de la plantilla «{{name}}»: proveedor, artículos, cantidades y notas. Lo que hay ahora se perderá.',
    replaceTemplateConfirm: 'Sustituye',
    deliveryNotUsualDay:
      'No es día de reparto de este proveedor (reparte {{days}}). Se puede guardar igualmente.',
    deliveryBeforeOrder: 'La entrega no puede ser anterior a la fecha de pedido.',
    notes: 'Notas',
    linesTitle: 'Líneas',
    addLine: 'Añade artículo',
    article: 'Artículo',
    selectArticle: 'Selecciona un artículo',
    orderedQuantity: 'Cantidad',
    purchaseUnit: 'Unidad de compra',
    baseUnit: 'Unidad base',
    agreedPrice: 'Precio pactado',
    agreedPriceUnit: 'Precio (\u20ac/{{unit}})',
    pricePerUnit: '{{price}} \u20ac/{{unit}}',
    lineAmount: '= {{amount}} \u20ac',
    priceNeedsConversion: 'Hace falta la conversión del envase para indicar el precio por unidad base.',
    colPrice: 'Precio pactado',
    priceCatalog: 'Vigente en el catálogo: {{price}} €/{{unit}}',
    priceCatalogNone: 'Sin precio vigente en el catálogo',
    updateSupplierPrice: 'Actualiza el precio del proveedor',
    updatePriceTitle: 'Actualiza el precio del proveedor',
    updatePriceBody: '«{{article}}» en «{{supplier}}»: el precio vigente pasará de {{from}} a {{to}} €/{{unit}}. El cambio quedará en el histórico de precios con este pedido como origen. Los pedidos ya hechos no cambian.',
    updatePriceBodyFirst: '«{{article}}» en «{{supplier}}» todavía no tiene precio vigente: pasará a {{to}} €/{{unit}}. El cambio quedará en el histórico de precios con este pedido como origen. Los pedidos ya hechos no cambian.',
    updatePriceConfirm: 'Actualiza el precio',
    updatePriceError: 'No se ha podido actualizar el precio.',
    updatePriceDone: 'Precio del proveedor actualizado. Consta en el histórico de precios del artículo.',
    priceMissingHint: 'Sin precio pactado en el catálogo para este proveedor: indícalo para poder valorar la línea.',
    lineQuantityTimes: '{{quantity}} \u00d7 {{unit}}',
    lineEquivalenceShort: '{{quantity}} {{pack}} \u00d7 {{factor}} {{base}}',
    lineNotes: 'Nota de la l\u00ednea',
    lineNotesHint: 'Opcional, solo para este art\u00edculo. La nota del pedido va arriba.',
    unknownQuantityHint:
      'No se puede dar por entregado: hay líneas con un formato sin conversión configurada y no se sabe cuánto se ha pedido. Configura la conversión en la ficha del artículo, en Unidades y conversiones, o finaliza el pedido dejando el resto sin recibir.',
    unknownQuantityBanner:
      'Hay líneas que no se podrán comparar con lo que llegue: su formato no tiene conversión configurada para el artículo. Se muestran en la unidad de compra.',
    pickSupplierFirst: 'Elige primero el proveedor',
    searchArticle: 'Busca por código o descripción',
    noArticleMatches: 'Ningún artículo coincide',
    noSupplierMatches: 'Ningún proveedor coincide',
    supplierHasNoArticles:
      'Este proveedor no tiene ningún artículo vinculado. Vincúlalos desde la ficha del artículo, en Proveedores.',
    articleUnlinked: 'sin relación con este proveedor',
    droppedArticles_one:
      'Ha salido del pedido porque este proveedor no lo sirve: {{articles}}.',
    droppedArticles_other:
      'Han salido del pedido porque este proveedor no los sirve: {{articles}}.',
    supplierArticlesUnavailable:
      'No se ha podido leer qué artículos sirve este proveedor. Vuelve a intentarlo; mientras tanto la lista puede salir vacía.',
    missingConversion:
      'Este formato no tiene conversión configurada para el artículo: el pedido no podrá comparar lo que llegue con lo que se ha pedido. Configúrala en la ficha del artículo, en Unidades y conversiones.',
    ordered: 'Pedido',
    received: 'Recibido',
    pending: 'Pendiente',
    remove: 'Quita',
    save: 'Guarda',
    confirm: 'Confirma pedido',
    confirmHint: 'El pedido pasa a «Pendiente de entrega».',
    closeOrder: 'Finalizar el pedido',
    registerReception: 'Registrar la recepción',
    registerReceptionHint:
      'Para cuando la mercancía ya ha llegado pero no se ha registrado en planta: escribe lo que ha llegado de verdad y saldrá el albarán.',
    markDelivered: 'Marcar como entregado',
    markDeliveredHint: 'Ya ha llegado todo: esto solo lo deja registrado en el pedido.',
    closeOrderHint:
      'Si lo que falta ya no va a llegar, finaliza el pedido con lo recibido: lo pendiente queda registrado como no recibido y deja de estar pendiente. Hay que indicar el motivo.',
    closeDialogTitle: 'Finaliza el pedido {{number}}',
    closeDialogBody:
      'Confirmas que no esperas el resto. El pedido pasará a «Entregado» con lo que se ha recibido: los albaranes y las existencias no cambian, y lo que falta queda registrado como no recibido con tu nombre, la fecha y el motivo.',
    closeDialogPendingTitle: 'Se deja sin recibir',
    closeDialogUnknown: 'cantidad desconocida (el envase no tiene conversión)',
    closeError: 'No se ha podido finalizar el pedido. Inténtalo de nuevo.',
    closeErrorNotOpen:
      'Este pedido ya no se puede finalizar: quizá se ha servido o cancelado mientras tanto. Refresca la página.',
    closeErrorNothingPending:
      'No falta nada por recibir: el pedido ya se puede marcar como entregado. Refresca la página.',
    closeErrorNothingReceived:
      'No se ha recibido nada de este pedido, y no se puede finalizar. Si no va a llegar, cancélalo.',
    closedTitle: 'Finalizado con pendiente de recibir',
    closedMeta: '{{user}} · {{date}}',
    closedReason: 'Motivo',
    closedLinesTitle: 'Se dejó sin recibir',
    closedLine: '{{article}}: {{pending}} de {{ordered}}',
    closedLineUnknown: '{{article}}: cantidad desconocida',
    nothingReceivedHint:
      'Todavía no se ha recibido nada de este pedido. Un pedido no se puede dar por entregado sin cantidades recibidas: registra primero la recepción.',
    receptionsTitle: 'Albaranes de compra',
    noReceptions: 'Ninguna recepción registrada todavía.',
    emptyLines: 'Añade al menos una línea al pedido.',
    lowStockWarning: 'Existencias bajo mínimo para algunos artículos (aviso, no bloquea).',
    pdfTitle: 'Pedido de compra',
    pdfSupplierRef: 'Ref. proveedor',
    pdfError: 'No se ha podido generar el PDF.',
    emailDefaultSubject: 'Pedido {{comanda}} · {{obrador}}',
    sendingTitle: 'Envío al proveedor',
    sendingDraftHint: 'Confirma el pedido para poder enviarlo y descargar su PDF.',
    sendingPending: 'Pendiente de enviar',
    sendingSent: 'Enviado',
    sendingWillEmail: 'Se enviará por correo a {{recipients}}.',
    sendingManualChannel: 'Este proveedor recibe los pedidos por otros medios: márcalo como enviado cuando se lo hayas hecho llegar.',
    sendingNoRecipients: 'Este proveedor no tiene ningún correo de pedidos. Añádelo en su ficha (Configuración › Proveedores), escribe uno al enviarlo o márcalo como enviado. El correo general no se usa para esto.',
    sendingLastEmail: 'Enviado por correo el {{date}} a {{recipients}}.',
    sendingLastManual: 'Marcado como enviado el {{date}}.',
    sendingWhatsappReady: 'Este proveedor recibe los pedidos por WhatsApp en el {{phone}}. «Envía por WhatsApp» abre WhatsApp con el pedido escrito y lo envías tú: Obrava no puede saber si el mensaje ha salido ni si ha llegado, así que lo confirmas tú.',
    sendingWhatsappNoPhone: 'Este proveedor recibe los pedidos por WhatsApp pero no tiene ningún número en la ficha. Añádelo en Terceros · Proveedores, o marca el pedido como enviado cuando se lo hayas hecho llegar.',
    sendingWhatsappNoPrefix: 'El número de WhatsApp de este proveedor («{{phone}}») no se puede abrir: hace falta el prefijo del país («+34…»). Corrígelo en la ficha, o marca el pedido como enviado cuando se lo hayas hecho llegar.',
    sendWhatsappAction: 'Envía por WhatsApp',
    resendWhatsappAction: 'Reenvía por WhatsApp',
    whatsappDialogTitle: 'WhatsApp · pedido {{number}}',
    whatsappDialogBody: 'Se ha abierto WhatsApp con el pedido escrito, para el número {{phone}}. WhatsApp no nos dice si has enviado el mensaje ni si ha llegado: cuando lo hayas enviado, confírmalo aquí y quedará constancia en el pedido.',
    whatsappDialogNumber: 'Número de WhatsApp: +{{number}}',
    whatsappDialogOmitted_one: 'Una línea no cabía: el mensaje dice que hay más. Complétalo por otro medio (p. ej. el PDF por correo).',
    whatsappDialogOmitted_other: '{{count}} líneas no cabían: el mensaje dice que hay más. Complétalo por otro medio (p. ej. el PDF por correo).',
    whatsappPreview: 'El mensaje',
    whatsappReopen: 'Vuelve a abrir WhatsApp',
    whatsappConfirm: 'Ya lo he enviado',
    whatsappDefaultTemplate: 'Hola, os enviamos el pedido {{comanda}} de {{obrador}}.\n\n{{linies}}\n\nEntrega prevista: {{entrega}}\n\n¡Gracias!',
    whatsappDefaultTemplateNoDelivery: 'Hola, os enviamos el pedido {{comanda}} de {{obrador}}.\n\n{{linies}}\n\n¡Gracias!',
    whatsappLineRef: 'ref. {{ref}}',
    whatsappReviewBody: 'Este es el mensaje que se abrirá en WhatsApp, para el número {{phone}}. Puedes retocarlo en WhatsApp antes de enviarlo; lo que quede escrito allí es lo que envíes, no lo de aquí.',
    whatsappOpenAction: 'Abre WhatsApp',
    whatsappTooLong: 'El texto es demasiado largo para el enlace de WhatsApp y quizá no se abra entero. Acorta la plantilla de WhatsApp del proveedor.',
    historyMessage: 'Ver el mensaje',
    whatsappNotes: 'Notas: {{notes}}',
    whatsappMoreLines: '… y {{count}} líneas más',
    sendingLastWhatsapp: 'Marcado como enviado por WhatsApp el {{date}} (al {{phone}}). WhatsApp no confirma la entrega.',
    resultWhatsapp: 'Marcado como enviado · sin confirmación de entrega',
    sendSkippedNoPhone: 'El proveedor no tiene ningún número de WhatsApp en la ficha.',
    sendSkippedWhatsapp: 'Este proveedor recibe los pedidos por WhatsApp: no se envía desde aquí. Abre WhatsApp desde el pedido.',
    sendingLegacy: 'Consta como enviado el {{date}}.',
    sendAction: 'Envía al proveedor',
    sendByEmailAction: 'Envía por correo',
    resendAction: 'Reenvía',
    markSentAction: 'Marca como enviado',
    downloadPdf: 'Descarga el PDF',
    sendDialogTitle: 'Envía el pedido {{number}}',
    resendDialogTitle: 'Reenvía el pedido {{number}}',
    sendTo: 'Para',
    sendToHint: 'Separa las direcciones con comas. Si cambias alguna, solo vale para este envío.',
    sendToInvalid: 'Hay direcciones que no son válidas: {{list}}',
    sendToEmpty: 'Pon al menos una dirección.',
    sendSubject: 'Asunto',
    sendAttachPdf: 'Adjunta el PDF del pedido',
    sendConfirm: 'Envía',
    resendConfirm: 'Reenvía',
    markSentTitle: 'Marca como enviado',
    markSentBody: 'Quedará constancia de que el pedido {{number}} se ha hecho llegar al proveedor por otros medios, con la fecha y la hora de ahora. No se envía ningún correo.',
    sendResultSent: 'Pedido enviado a {{recipients}}.',
    sendResultMarked: 'Pedido marcado como enviado.',
    sendResultFailed: 'El correo no ha salido. Vuelve a intentarlo; el motivo está en el historial de envíos.',
    sendSkipped: 'Este pedido no se puede enviar ahora.',
    sendSkippedNoRecipients: 'El proveedor no tiene ningún correo de pedidos donde enviarlo.',
    sendError: 'No se ha podido enviar el pedido. Vuelve a intentarlo.',
    confirmAndSend: 'Confirma y envía',
    autoSentBanner: 'Pedido confirmado y enviado a {{recipients}}.',
    autoSendFailed: 'Pedido confirmado, pero no se ha podido enviar. Envíalo desde «Envío al proveedor».',
    historyTitle: 'Historial de envíos',
    historyWhen: 'Cuándo',
    historyWho: 'Quién',
    historyChannel: 'Canal',
    historyTo: 'A quién',
    historyResult: 'Resultado',
    channelEmail: 'Correo',
    channelManual: 'Manual',
    channelWhatsapp: 'WhatsApp',
    resultSent: 'Enviado',
    resultMarked: 'Marcado',
    resultFailed: 'Ha fallado',
  },

  'backoffice.receptions': {
    title: 'Albaranes de compra',
    receptionNumber: 'Núm. albarán',
    deliveryNote: 'Albarán proveedor',
    supplier: 'Proveedor',
    date: 'Fecha',
    order: 'Pedido',
    lines: 'Líneas',
    invoiced: 'Facturado',
    notInvoiced: 'Pendiente de facturar',
    markInvoiced: 'Marca como facturado',
    markNotInvoiced: 'Desmarca facturado',
    invoiceNumber: 'Núm. factura proveedor',
    invoiceNumberPrompt: 'Introduce el número de factura del proveedor.',
    downloadPdf: 'Descarga PDF',
    empty: 'Todavía no hay albaranes de compra',
    searchLabel: 'Busca por albarán, proveedor o factura',
    emptyFiltered: 'Ningún albarán coincide con la búsqueda.',
    searchPlaceholder: 'Busca por núm. de albarán del proveedor…',
    documentTitle: 'Albarán de compra',
    article: 'Artículo',
    supplierLot: 'Lote proveedor',
    internalLot: 'Lote interno',
    expiry: 'Caducidad',
    quantity: 'Cantidad',
    notesLabel: 'Notas',
    exceptionPending: 'Excepción pendiente',
    control: 'Control',
    corrected: 'Rectificado',
    cancelled: 'Anulado',
    correctedNote: 'Rectificado el {{date}}: {{reason}}',
    wasQuantity: 'antes {{quantity}}',
  },

  // Registro manual de una recepción desde el BackOffice (OBR-77): la mercancía ha llegado y nadie la ha registrado en planta.
  'backoffice.receptionRegister': {
    title: 'Registrar la recepción',
    subtitle: '{{order}} · {{supplier}}',
    intro:
      'Para cuando la mercancía ya ha llegado pero no se ha registrado en planta. Escribe lo que ha llegado de verdad: nada se da por recibido automáticamente, y las existencias suben solo con lo que pongas.',
    notOpen:
      'Este pedido no espera mercancía: solo se puede registrar una recepción de un pedido pendiente de entrega o entregado parcialmente.',
    existing_one: 'Este pedido ya tiene {{count}} albarán: {{list}}. Comprueba que no sea la misma entrega.',
    existing_other: 'Este pedido ya tiene {{count}} albaranes: {{list}}. Comprueba que no sea la misma entrega.',
    existingItem: '{{number}} ({{date}}, albarán {{note}})',
    unsyncedWarning:
      'Si un dispositivo de planta tiene una recepción de este pedido pendiente de sincronizar, aquí no se ve: compruébalo antes de registrarla, o la entrega se duplicaría.',
    headerTitle: 'El albarán',
    deliveryNote: 'Nº de albarán del proveedor',
    deliveryNoteHint: 'Obligatorio: el número que lleva impreso el albarán del proveedor.',
    sameNumber: 'Ya hay el albarán {{number}} de este pedido con este número.',
    receptionDate: 'Fecha de recepción',
    receptionDateHint: 'El día que llegó la mercancía. La caducidad estimada se calcula a partir de ella.',
    dateInvalid: 'Pon una fecha real, de hoy o anterior.',
    notes: 'Notas',
    notesHint: 'Opcional.',
    linesTitle: 'Qué ha llegado',
    ordered: 'Pedido',
    receivedSoFar: 'Ya recibido',
    pending: 'Pendiente',
    orderedConversion: '{{ordered}} × {{factor}} = {{total}}',
    quantityWithBase: '{{quantity}} ({{base}})',
    entryUnit: 'Unidad de la cantidad',
    entry: 'Entrada {{number}}',
    removeEntry: 'Quitar la entrada',
    addEntry: 'Añadir otra entrada',
    quantity: 'Cantidad recibida',
    quantityIn: 'Cantidad recibida ({{unit}})',
    equalsBase: '= {{total}}',
    noConversion: 'El envase «{{unit}}» no tiene conversión en la ficha del artículo. Indica la cantidad en {{base}}.',
    lot: 'Lote del proveedor (opcional)',
    expiry: 'Caducidad (opcional)',
    expiryEstimated: 'Caducidad estimada',
    expiryEstimatedHint_one: 'Propuesta: recepción + {{count}} día de vida útil. Cámbiala si el proveedor indica una real.',
    expiryEstimatedHint_other: 'Propuesta: recepción + {{count}} días de vida útil. Cámbiala si el proveedor indica una real.',
    control: 'Control de recepción',
    controlNone: 'Sin control',
    controlOk: 'Conforme',
    controlNok: 'No conforme',
    flagException: 'Marcar como excepción',
    unflagException: 'Quitar la marca',
    flaggedException: 'Excepción',
    exceptionNote: '¿Qué pasa?',
    exceptionNoteHint: 'Ej.: sacos de 20 kg en lugar de 25',
    exceptionNoteRequired: 'Hay que explicar qué pasa',
    thisReceipt: 'En este albarán',
    willRemainPending: 'Quedará pendiente',
    excess: 'Pasa {{excess}} de lo que faltaba. Se registrará como excepción.',
    confirmLabel: 'Confirmo que esta entrega aún no se ha registrado, ni en la App ni como albarán.',
    submit: 'Registrar la recepción',
    submitting: 'Registrando…',
    missingList: 'Para registrarla falta: {{list}}.',
    missing_deliveryNote: 'el número de albarán',
    missing_date: 'una fecha de recepción real, de hoy o anterior',
    missing_nothingEntered: 'la cantidad de al menos una entrada',
    missing_flagNotes: 'la nota de las entradas marcadas como excepción',
    missing_notConfirmed: 'confirmar que la entrega no se ha registrado',
    errorGeneric:
      'No se ha podido registrar la recepción. Mira en la lista de albaranes del pedido si llegó a quedar registrada antes de volver a intentarlo.',
    openPicker: 'Registrar una recepción',
    pickerTitle: 'Registrar una recepción',
    pickerOrder: 'Pedido',
    pickerPlaceholder: 'Elige el pedido que ha llegado',
    pickerHint: 'Solo los pedidos pendientes de entrega o entregados parcialmente.',
    pickerEmpty: 'Ningún pedido pendiente de recibir',
    pickerConfirm: 'Continuar',
    articleMissing:
      'Este artículo ya no está en el catálogo: no se puede registrar su recepción desde aquí.',
    errorTenant:
      'El obrador activo de tu cuenta ha cambiado (quizá en otra pestaña). No se ha registrado nada: recarga la página y vuelve a intentarlo.',
  },

  // Excepciones de recepción (OBR-83): lo que ha llegado distinto de lo que se pidió.
  'backoffice.receptionExceptions': {
    title: 'Excepciones',
    subtitle:
      'Lo que ha llegado distinto de lo que se pidió. Aquí se revisa y se deja constancia de la decisión.',
    filterLabel: 'Estado',
    filterPending: 'Pendientes',
    filterResolved: 'Resueltas',
    filterAll: 'Todas',
    empty: 'No hay excepciones pendientes de revisar.',
    searchLabel: 'Busca por artículo, albarán o proveedor',
    emptyFiltered: 'Ninguna excepción coincide con la búsqueda.',
    emptyAll: 'Todavía no hay ninguna excepción de recepción.',
    kindOverQuantity: 'Más de lo pedido',
    kindUnorderedArticle: 'Artículo que no está en el pedido',
    kindOperatorFlagged: 'Marcada por el operario',
    statusPending: 'Pendiente de decisión',
    statusAccepted: 'Aceptada',
    statusRejected: 'Rechazada',
    deliveryNoteMeta: 'Albarán del proveedor {{number}}',
    ordered: 'Pedido',
    received: 'Recibido en este albarán',
    excess: 'Exceso',
    operatorNote: 'Nota del operario',
    resolutionAccepted: 'Aceptada por {{user}} · {{date}}',
    resolutionRejected: 'Rechazada por {{user}} · {{date}}',
    resolutionNote: 'Nota',
    accept: 'Aceptar',
    reject: 'Rechazar',
    acceptTitle: 'Aceptar la excepción',
    acceptBody:
      'La mercancía pasa a stock disponible. Queda constancia de quién lo ha decidido y cuándo; el pedido no cambia.',
    acceptNoteLabel: 'Nota (opcional)',
    acceptNotePlaceholder: 'Por qué se acepta, si hace falta explicarlo',
    rejectTitle: 'Rechazar la excepción',
    rejectBody:
      'La mercancía no podrá quedar disponible para producción: se genera una devolución. Elige qué se hace con ella y explica por qué se rechaza.',
    rejectNoteLabel: 'Motivo del rechazo',
    rejectNotePlaceholder: 'Por qué se rechaza',
    resolveErrorGone: 'Esta excepción ya no existe. Actualiza la pantalla.',
    resolveErrorAlreadyResolved: 'Otra persona ya ha resuelto esta excepción.',
    resolveErrorNoteRequired: 'Hay que escribir el motivo para rechazarla.',
    resolveErrorForbidden: 'No tienes permiso para resolver excepciones.',
    resolveError: 'No se ha podido guardar la decisión. Vuelve a intentarlo.',
    resolutionLabel: 'Qué se hace con la mercancía',
    resolutionImmediate: 'Devolución inmediata al proveedor',
    resolutionImmediateHint:
      'El transportista se la lleva ahora. No queda en el stock: la devolución se registra como devuelta.',
    resolutionPending: 'Pendiente de devolución',
    resolutionPendingHint:
      'Queda en el obrador, bloqueada (no disponible para producción), hasta que se confirme la devolución física.',
    allocationsTitle: 'Lotes que se devuelven',
    allocationsHint:
      'Hay que devolver {{quantity}} {{unit}}. Se ha repartido entre los lotes de este albarán, de la entrada más reciente hacia atrás; se puede cambiar.',
    allocationLot: 'Lote {{lot}} · hasta {{max}} {{unit}}',
    allocationSum: 'Suma: {{sum}} de {{required}} {{unit}}',
    allocationMismatch: 'La suma debe ser {{required}} {{unit}}.',
    allocationShortfall:
      'Los lotes de este albarán no tienen suficiente para devolver {{required}} {{unit}}.',
    allocationNoLots: 'No se han encontrado los lotes de este albarán.',
    resolutionRejectedImmediate: 'Devolución inmediata al proveedor',
    resolutionRejectedPending: 'Pendiente de devolución',
    returnsLink: 'Ver las devoluciones',
    blockedNote:
      'Mientras no se decida, {{quantity}} {{unit}} no cuentan como disponibles para producción.',
    resolveErrorResolutionRequired: 'Elige qué se hace con la mercancía.',
    resolveErrorAllocation: 'El reparto en lotes no cuadra con la cantidad que hay que devolver.',
    resolveErrorExceedsLot:
      'No se puede devolver más de lo que llegó de un lote (quizá ya hay devoluciones de ese lote).',
    resolveErrorWithoutSupplier: 'El albarán no tiene proveedor: no se puede crear la devolución.',
  },

  // Devoluciones de compra (OBR-117): la mercancía que vuelve al proveedor.
  'backoffice.purchaseReturns': {
    title: 'Devoluciones',
    subtitle:
      'La mercancía que vuelve al proveedor. Una devolución pendiente sigue en el obrador y no cuenta como disponible; al confirmar la devolución sale de las existencias.',
    filterStatusLabel: 'Estado',
    filterPending: 'Pendientes de devolver',
    filterReturned: 'Devueltas',
    filterAll: 'Todas',
    filterSupplierLabel: 'Proveedor',
    allSuppliers: 'Todos los proveedores',
    empty: 'No hay devoluciones pendientes de devolver.',
    searchLabel: 'Busca por artículo, proveedor, lote o albarán',
    emptyFiltered: 'Ninguna devolución coincide con la búsqueda.',
    emptyAll: 'Todavía no hay ninguna devolución de compra.',
    loadError: 'No se han podido cargar las devoluciones.',
    statusPending: 'Pendiente de devolver',
    statusReturned: 'Devuelta',
    originException: 'Excepción rechazada',
    originIncident: 'Incidencia',
    originReception: 'Albarán',
    supplier: 'Proveedor',
    reception: 'Albarán',
    purchaseOrder: 'Pedido',
    article: 'Artículo',
    lot: 'Lote',
    quantity: 'Cantidad',
    reason: 'Motivo',
    observations: 'Observaciones',
    createdBy: 'Creada por {{user}} · {{date}}',
    returnedBy: 'Devuelta por {{user}} · {{date}}',
    blockedNote: 'Esta cantidad está bloqueada: no cuenta como disponible hasta que se confirme la devolución.',
    confirmReturn: 'Confirma la devolución',
    confirmTitle: 'Confirmar la devolución',
    confirmBody:
      '{{quantity}} {{unit}} de {{article}} (lote {{lot}}) dejarán de formar parte de las existencias. Confírmalo solo cuando la mercancía haya salido del obrador.',
    confirm: 'Confirmar',
    confirmErrorGone: 'Esta devolución ya no existe. Actualiza la pantalla.',
    confirmErrorAlreadyReturned: 'Esta devolución ya está confirmada.',
    confirmErrorForbidden: 'No tienes permiso para confirmar devoluciones.',
    confirmError: 'No se ha podido confirmar la devolución. Vuelve a intentarlo.',
    create: 'Genera una devolución',
    createTitle: 'Nueva devolución de compra',
    createBody:
      'De un lote de un albarán de compra. Si no la marcas como ya devuelta, la mercancía queda en el obrador, bloqueada, hasta que confirmes la devolución.',
    lotLabel: 'Lote',
    lotOption: '{{lot}} · hasta {{max}} {{unit}}',
    quantityLabel: 'Cantidad ({{unit}})',
    reasonLabel: 'Motivo (obligatorio)',
    reasonPlaceholder: 'Por qué se devuelve',
    observationsLabel: 'Observaciones (opcional)',
    alreadyReturned: 'Ya devuelta: el transportista se la ha llevado',
    alreadyReturnedHint: 'La mercancía sale de las existencias ahora mismo.',
    createAction: 'Crea la devolución',
    noLots: 'Este albarán no tiene lotes que devolver.',
    createErrorExceedsLot:
      'No se puede devolver más de lo que llegó de este lote (quizá ya hay otras devoluciones).',
    createErrorReasonRequired: 'Hay que escribir el motivo.',
    createErrorWithoutSupplier: 'El albarán no tiene proveedor: no se puede crear la devolución.',
    createErrorForbidden: 'No tienes permiso para crear devoluciones.',
    createError: 'No se ha podido crear la devolución. Vuelve a intentarlo.',
    reasonOfIncident: 'Incidencia: {{comment}}',
    plannedDateLabel: 'Fecha prevista de recogida o devolución',
    plannedDateHint:
      'Cuándo se prevé que el transportista pase a recogerlo. La App de planta la muestra en Devoluciones pendientes como de hoy, atrasada o futura.',
    plannedShort: 'Recogida prevista',
    plannedNone: 'Sin fecha prevista',
    plannedChipOverdue: 'Atrasada',
    plannedChipToday: 'Hoy',
    returnNumber: 'Albarán de devolución {{number}}',
    downloadNote: 'Descargar el albarán (PDF)',
    downloadingNote: 'Generando el PDF…',
    downloadNoteError: 'No se ha podido generar el albarán. Vuelve a intentarlo.',
    reschedule: 'Cambiar la fecha',
    rescheduleTitle: 'Fecha prevista de la devolución',
    rescheduleBody:
      'Cuándo se prevé que el transportista pase a recogerlo. Puedes dejarla en blanco si aún no se sabe.',
    rescheduleAction: 'Guardar la fecha',
    rescheduleErrorGone: 'Esta devolución ya no existe. Actualiza la pantalla.',
    rescheduleErrorAlreadyReturned: 'Esta devolución ya ha salido: la fecha no se puede cambiar.',
    rescheduleErrorForbidden: 'No tienes permiso para cambiar la fecha de las devoluciones.',
    rescheduleError: 'No se ha podido guardar la fecha. Vuelve a intentarlo.',
    exitHint: 'También se puede confirmar desde la App de planta, en Recepción › Devoluciones pendientes.',
    noteTitle: 'Albarán de devolución',
    noteNumber: 'Núm.',
    noteDate: 'Fecha de salida',
    noteSupplier: 'Proveedor',
    noteOrigin: 'Documento de origen',
    noteReception: 'Albarán de recepción',
    noteOrder: 'Pedido',
    noteSupplierDeliveryNote: 'Albarán del proveedor',
    noteArticle: 'Artículo',
    noteLot: 'Lote',
    noteSupplierLot: 'Lote del proveedor',
    noteQuantity: 'Cantidad',
    noteReason: 'Motivo',
    noteObservations: 'Observaciones',
    noteIssuedBy: 'Salida confirmada por',
    noteFooter:
      'Este documento acredita la salida física de la mercancía y su devolución al proveedor. No es una factura rectificativa ni una nota de abono.',
    noteSignatureOut: 'Entregado por',
    noteSignatureCarrier: 'Recibido por el transportista',
  },


  // Detalle de un albarán de recepción y su rectificación (OBR-86).
  'backoffice.receptionDetail': {
    title: 'Albarán {{number}}',
    titleFallback: 'Albarán de compra',
    notFound: 'No se ha encontrado este albarán.',
    returnsTitle: 'Devoluciones de este albarán',
    returnsEmpty: 'Este albarán no tiene devoluciones.',
    returnsCreate: 'Genera una devolución',
    returnsOpen: 'Ver las devoluciones',
    linesTitle: 'Entradas',
    exceptionsTitle: 'Excepciones',
    exceptionsOpen: 'Revísalas en Excepciones',
    correctionsTitle: 'Rectificaciones',
    noCorrections: 'Este albarán no se ha rectificado.',
    receivedAs: 'Recibido: {{quantity}}',
    counted: 'Contado: {{quantity}} {{pack}} \u00d7 {{factor}} {{base}}',
    writtenAs: 'Al recibir se escribió: {{code}}',
    noLotCode: 'Sin lote',
    noExpiry: 'sin fecha',
    correct: 'Corregir el albarán',
    correctHint:
      'Para corregir la cantidad, el lote del proveedor, la caducidad o el número del albarán. La entrada original no se toca: la corrección queda registrada.',
    correctionMeta: '{{user}} · {{date}}',
    correctionReason: 'Motivo',
    quantityChange: '{{before}} → {{after}}',
    lotCodeChange: 'Lote del proveedor: {{before}} → {{after}}',
    expiryChange: 'Caducidad: {{before}} → {{after}}',
    movement: 'Movimiento correctivo: {{delta}}',
    lotBalance: 'Existencias del lote después: {{balance}}',
    lotBalanceNegative: 'Las existencias del lote quedan en negativo: ya se habían gastado.',
    headerDeliveryNote: 'Albarán del proveedor: {{from}} → {{to}}',
    headerDate: 'Fecha: {{from}} → {{to}}',
    orderEffect: 'Pedido {{order}}: {{from}} → {{to}}',
    cancelledNote: 'Todas las entradas han quedado a cero: el albarán se ha anulado.',
    dialogTitle: 'Corregir el albarán {{number}}',
    dialogBody:
      'La entrada original no se toca: la corrección queda registrada con el motivo, y las existencias se corrigen con un movimiento con la diferencia.',
    fieldDeliveryNote: 'Albarán del proveedor',
    fieldDate: 'Fecha del albarán',
    fieldQuantity: 'Cantidad ({{unit}})',
    fieldLotCode: 'Lote del proveedor',
    fieldExpiry: 'Caducidad',
    currentHint: 'Ahora: {{quantity}}',
    annulEntry: 'Anular la entrada',
    entryTitle: '{{article}} · {{lot}}',
    reasonLabel: 'Motivo de la corrección',
    reasonPlaceholder: 'Qué ha pasado y por qué se corrige',
    impactTitle: 'Qué pasará',
    impactNothing: 'Todavía no hay ningún cambio.',
    impactLine: '{{article}}: {{before}} → {{after}} ({{delta}})',
    impactNegative: 'El lote {{lot}} ya se ha gastado: sus existencias quedarán en {{balance}}.',
    impactCancelled: 'Todas las entradas quedan a cero: el albarán quedará anulado.',
    impactOrder: 'El pedido {{order}} pasará de «{{from}}» a «{{to}}».',
    impactOrderClosed: 'El pedido {{order}} se cerró a mano con motivo: no se reabre.',
    confirm: 'Confirmar la corrección',
    invalidQuantity: 'Escribe una cantidad válida (cero o más).',
    invalidDate: 'Escribe una fecha válida.',
    errorReasonRequired: 'Hay que indicar el motivo de la corrección.',
    errorNotFound: 'Este albarán ya no existe. Actualiza la pantalla.',
    errorNotConfirmed: 'Este albarán ya no se puede corregir (está anulado o no es definitivo).',
    errorLotNotFound: 'Una de las entradas ya no es de este albarán. Actualiza la pantalla.',
    errorNothingToChange: 'No hay ningún cambio que guardar.',
    errorBadQuantity: 'Una de las cantidades no es válida.',
    errorBadHeader: 'El número o la fecha del albarán no son válidos.',
    errorForbidden: 'No tienes permiso para corregir albaranes.',
    errorGeneric: 'No se ha podido guardar la corrección. Vuelve a intentarlo.',
  },

  'backoffice.purchaseOrderStatus': {
    in_progress: 'En curso',
    pending_dispatch: 'Pendiente de envío',
    awaiting_delivery: 'Pendiente de entrega',
    receiving: 'Entregado parcialmente',
    delivered: 'Entregado',
    cancelled: 'Cancelado',
  },

  'backoffice.receptionControlResult': {
    conforme: 'Conforme',
    no_conforme: 'No conforme',
  },

  'backoffice.salesOrders': {
    articleTitle: 'Ventas — {{article}}',
    articleCard: 'Ficha del artículo',
    allOrders: 'Todos los pedidos',
    articleEmpty: 'De este artículo no se ha vendido nunca: ningún pedido ni albarán de venta.',
    articleLinesTitle: 'Líneas de pedido',
    articleNoLines: 'Este artículo no aparece en ningún pedido de venta.',
    articleShipmentsTitle: 'Expediciones',
    articleNoShipments: 'Este artículo no se ha expedido en ningún albarán.',
    totalOrdered: 'Pedido',
    totalShipped: 'Servido',
    totalPending: 'Pendiente de servir',
    title: 'Pedidos de venta',
    newOrder: 'Nuevo pedido',
    orderNumber: 'Núm.',
    client: 'Cliente',
    orderDate: 'Fecha',
    deliveryDate: 'Entrega',
    status: 'Estado',
    lines: 'Líneas',
    empty: 'Todavía no hay pedidos de venta',
    searchLabel: 'Busca por número o cliente',
    emptyFiltered: 'Ningún pedido coincide con la búsqueda.',
    searchPlaceholder: 'Busca por número o cliente…',
  },

  'backoffice.salesOrderDetail': {
    newTitle: 'Nuevo pedido de venta',
    editTitle: 'Pedido {{number}}',
    client: 'Cliente',
    selectClient: 'Selecciona un cliente',
    orderDate: 'Fecha de pedido',
    deliveryDate: 'Fecha de entrega',
    deliveryAddress: 'Dirección de entrega',
    deliveryAddressHint: 'Si se deja vacía, se usa la dirección del cliente.',
    deliveryPoint: 'Punto de entrega',
    deliveryPointFiscal: 'Dirección fiscal del cliente',
    deliveryPointCustom: 'Otra dirección',
    notes: 'Notas',
    linesTitle: 'Líneas',
    addLine: 'Añade artículo',
    article: 'Artículo',
    selectArticle: 'Selecciona un artículo',
    orderedQuantity: 'Cantidad',
    salesUnit: 'Unidad de venta',
    baseUnit: 'Unidad base',
    unitPrice: 'Precio (opcional)',
    ordered: 'Pedido',
    shipped: 'Servido',
    pending: 'Pendiente',
    remove: 'Quita',
    save: 'Guarda',
    confirm: 'Confirma pedido',
    confirmHint: 'El pedido pasa a «Confirmado», pendiente de preparar.',
    closeOrder: 'Cierra pedido',
    closeOrderHint: 'Cierra el pedido dejando el resto sin servir.',
    cancelOrder: 'Cancela pedido',
    shipmentsTitle: 'Albaranes de venta',
    noShipments: 'Ninguna expedición registrada todavía.',
    emptyLines: 'Añade al menos una línea al pedido.',
    availableStock: 'Disponible',
    committedStock: 'Comprometido',
    insufficientStockWarning:
      'Existencias insuficientes para algunos artículos (aviso, no bloquea).',
  },

  'backoffice.shipments': {
    title: 'Albaranes de venta',
    shipmentNumber: 'Núm. albarán',
    client: 'Cliente',
    date: 'Fecha',
    order: 'Pedido',
    lines: 'Líneas',
    downloadPdf: 'Descarga PDF',
    cancel: 'Cancela expedición',
    cancelPrompt:
      'Motivo de la cancelación (obligatorio). Se generarán movimientos contrarios de stock.',
    empty: 'Todavía no hay albaranes de venta',
    status: 'Estado',
    searchPlaceholder: 'Busca por número o cliente…',
    documentTitle: 'Albarán de venta',
    article: 'Artículo',
    lot: 'Lote',
    expiry: 'Caducidad',
    quantity: 'Cantidad',
    unitPrice: 'Precio',
    amount: 'Importe',
    notesLabel: 'Notas',
  },

  'backoffice.salesOrderStatus': {
    in_progress: 'En curso',
    confirmed: 'Confirmado',
    preparing: 'En preparación',
    shipped: 'Expedido',
    cancelled: 'Cancelado',
    partially_shipped: 'Servido parcialmente',
  },

  'backoffice.shipmentStatus': {
    confirmed: 'Confirmada',
    cancelled: 'Cancelada',
  },

  'backoffice.technicalSheet': {
    title: 'Ficha técnica',
    generate: 'Genera FT',
    legalDenomination: 'Denominación legal',
    ingredientsText: 'Ingredientes',
    allergensText: 'Alérgenos',
    approve: 'Aprueba',
    netWeight: 'Peso neto',
    conservation: 'Conservación',
  },

  'backoffice.labels': {
    title: 'Etiquetas',
    template: 'Plantilla',
    print: 'Imprime',
    internalLot: 'Lote interno',
    generatedOnClose: 'Generada al cerrar la OT',
  },

  'backoffice.smartImporter': {
    title: 'Importación',
    subtitle:
      'Lleva los datos de tu hoja de cálculo al obrador: sube, mapea, revisa y confirma',
    back: 'Atrás',
    // Step indicator
    stepUpload: 'Sube',
    stepMap: 'Mapea',
    stepPreview: 'Revisa',
    stepResult: 'Resultado',
    // Upload step
    uploadFileTitle: 'Sube un archivo',
    uploadFileHint:
      'Elige un archivo CSV, XLS o XLSX exportado de tu hoja de cálculo. La primera fila debe ser la cabecera.',
    chooseFile: 'Elige un archivo',
    // Templates
    templatesTitle: 'Plantillas',
    templatesHint:
      'Descarga una hoja con los nombres de columna que el importador reconoce y una pestaña «Instrucciones» que explica qué va en cada una. Empieza de cero, o descarga tu catálogo actual, edítalo y vuelve a subirlo.',
    downloadTemplateBlank: 'Plantilla vacía',
    downloadTemplateCatalog: 'Plantilla con los datos actuales ({{count}})',
    downloadTemplateCatalogLoading: 'Cargando el catálogo…',
    downloadTemplateCatalogHint:
      'La plantilla con datos trae los artículos activos del catálogo, ya rellenados. Las filas que no toques volverán como «sin cambios» y no escribirán nada.',
    templateFilename: 'plantilla-articulos.xlsx',
    templateCatalogFilename: 'articulos-obrador.xlsx',
    templateSheetName: 'Artículos',
    templateExampleName: 'Harina de trigo',
    templateExamplePackaging: 'Saco=25; Palé=1000',
    booleanTrue: 'Sí',
    booleanFalse: 'No',
    // Column guide (OBR-9): the template's instructions sheet + the in-app guide
    guideSheetName: 'Instrucciones',
    guideTitle: 'Cómo se rellena la plantilla de artículos',
    guideShow: 'Qué va en cada columna',
    guideHide: 'Oculta la guía de columnas',
    guideRequiredChip: 'Obligatoria',
    guideColColumn: 'Columna',
    guideColRequired: 'Obligatoria',
    guideColDescription: 'Qué es',
    guideColFormat: 'Formato',
    guideColBlank: 'Si se deja vacía',
    guideColValues: 'Valores aceptados',
    guideRuleSheet:
      'Una fila por artículo en la hoja «{{sheet}}», que debe seguir siendo la primera: es la única que lee Obrava. No cambies el nombre de las columnas; las que no necesites, puedes borrarlas.',
    guideRuleRequired: 'Solo son obligatorias {{fields}}. Si falta alguna, la fila no se importa.',
    guideRuleExisting:
      'Si el código ya está en el catálogo, la fila actualiza ese artículo (o lo deja como está, si lo eliges en la revisión). Una celda vacía nunca borra ningún dato.',
    guideRuleNumbers: 'Los números, sin puntos de millar y con coma en los decimales: 1,5.',
    guideRuleReview:
      'Antes de escribir nada, Obrava muestra qué hará con cada fila, y las que tienen un error, con el número de fila y el motivo.',
    guideValuesAny: '—',
    guideValuesNone: 'Todavía no hay ninguno.',
    guideValuesYesNo: 'Sí y No; también 1 y 0, y una X para decir sí.',
    guideValuesComposition:
      'Cualquier ingrediente; los que no están en Configuración · Ingredientes se crean al importar.',
    guideDesc_sku:
      'El código del artículo en el obrador. Si ya existe, la fila actualiza ese artículo.',
    guideFormat_sku: 'Texto. No se puede repetir en la hoja.',
    guideBlank_sku: 'La fila no se importa.',
    guideDesc_name: 'El nombre del artículo, tal como lo verá todo el mundo en Obrava.',
    guideFormat_name: 'Texto.',
    guideBlank_name: 'La fila no se importa.',
    guideDesc_article_type: 'Qué tipo de artículo es.',
    guideFormat_article_type:
      'Un valor de la lista, completo o abreviado. Con otro valor, la fila no se importa.',
    guideBlank_article_type: 'Materia prima.',
    guideDesc_base_unit: 'La unidad en la que se cuentan las existencias del artículo.',
    guideFormat_base_unit:
      'El código de una unidad de Configuración · Unidades. Con otro, la fila no se importa.',
    guideBlank_base_unit: 'La fila no se importa.',
    guideDesc_recipe_unit:
      'La unidad en la que la receta pide esta materia prima, cuando no es la unidad base (huevos: base Un, receta kg).',
    guideFormat_recipe_unit:
      'El código de una unidad de Configuración · Unidades. Solo para materias primas. Con un código desconocido, la fila no se importa; igual que la unidad base significa que la receta ya va en ella.',
    guideBlank_recipe_unit: 'La receta va en la unidad base.',
    guideDesc_recipe_unit_factor:
      'Cuántas unidades de receta equivalen a UNA unidad base: 1 Un = 0,06 kg se escribe 0,06.',
    guideFormat_recipe_unit_factor:
      'Un número mayor que cero, con coma o punto. Obligatorio si la unidad de receta es distinta de la base; sin unidad de receta no se puede poner.',
    guideBlank_recipe_unit_factor: 'Sin equivalencia: la receta va en la unidad base.',
    guideDesc_category: 'La categoría del catálogo donde va el artículo.',
    guideFormat_category:
      'El nombre de una categoría de Configuración · Categorías. Si no está, se avisa y el artículo queda sin categoría.',
    guideBlank_category: 'Sin categoría.',
    guideDesc_barcode: 'El código de barras del artículo (EAN), si tiene.',
    guideFormat_barcode: 'Las cifras tal como salen en la etiqueta, como texto o como número.',
    guideBlank_barcode: 'Sin código de barras.',
    guideDesc_shelf_life_days: 'Cuántos días dura el artículo. Propone la caducidad de los lotes.',
    guideFormat_shelf_life_days: 'Número entero de días, 0 o más.',
    guideBlank_shelf_life_days: 'Sin vida útil.',
    guideDesc_min_stock: 'Por debajo de esta cantidad, Obrava avisa de que queda poco.',
    guideFormat_min_stock: 'Número en la unidad base, 0 o más.',
    guideBlank_min_stock: 'Sin mínimo.',
    guideDesc_max_stock: 'El máximo de existencias que quieres tener del artículo.',
    guideFormat_max_stock: 'Número en la unidad base, 0 o más.',
    guideBlank_max_stock: 'Sin máximo.',
    guideDesc_tax: 'El impuesto que se aplica al artículo.',
    guideFormat_tax:
      'El nombre o el tipo de un impuesto de Configuración · Impuestos. Con otro, la fila no se importa.',
    guideBlank_tax: 'Sin impuesto; se puede elegir después en la ficha.',
    guideDesc_purchase_price: 'El precio de compra, por unidad base.',
    guideFormat_purchase_price: 'Número, 0 o más.',
    guideBlank_purchase_price: 'Sin precio de compra.',
    guideDesc_sale_price:
      'El precio de venta, por unidad base. Solo se usa si el artículo se vende.',
    guideFormat_sale_price: 'Número, 0 o más.',
    guideBlank_sale_price: 'Sin precio de venta.',
    guideDesc_is_purchased: 'Si el artículo se compra a un proveedor.',
    guideFormat_is_purchased: 'Sí o No.',
    guideBlank_is_purchased: 'No.',
    guideDesc_is_manufactured: 'Si el artículo se hace en el obrador con una receta.',
    guideFormat_is_manufactured: 'Sí o No.',
    guideBlank_is_manufactured: 'No.',
    guideDesc_is_sold: 'Si el artículo se vende a clientes.',
    guideFormat_is_sold: 'Sí o No.',
    guideBlank_is_sold: 'No.',
    guideDesc_is_variable_weight:
      'Si el artículo no tiene una equivalencia fija y se pesa en cada movimiento.',
    guideFormat_is_variable_weight: 'Sí o No.',
    guideBlank_is_variable_weight: 'No.',
    guideDesc_default_zone:
      'La zona del almacén donde queda el artículo cuando se recibe o se produce.',
    guideFormat_default_zone:
      'El código de una zona de Configuración · Estructura del obrador. Con otro, la fila no se importa.',
    guideBlank_default_zone: 'Sin zona por defecto.',
    guideDesc_packaging_units:
      'Cómo viene embalado el artículo: cuántas unidades base lleva cada embalaje.',
    guideFormat_packaging_units:
      'Embalaje=cantidad, separados por punto y coma: Saco=25; Palé=1000. Añade o actualiza; no borra ninguno.',
    guideBlank_packaging_units: 'Sin embalajes.',
    guideDesc_composition: 'Los ingredientes que declara la etiqueta, de más a menos cantidad.',
    guideFormat_composition:
      'Ingredientes en orden, separados por punto y coma; el % es opcional: Harina=62; Agua=30; Sal. Sustituye la lista entera.',
    guideBlank_composition: 'Sin composición.',
    pasteTitle: 'O pega los datos',
    pasteLabel: 'Pega CSV o TSV',
    pasteHint:
      'Copia las celdas desde Excel o Numbers y pégalas aquí. La primera fila debe ser la cabecera.',
    parsePaste: 'Continúa',
    noRows: 'No se ha encontrado ninguna fila de datos. Comprueba que hay cabecera y al menos una fila.',
    fileReadError: 'No se ha podido leer el archivo',
    // Map step
    mapTitle: 'Mapea las columnas',
    mapHint:
      'Indica a qué campo de artículo corresponde cada columna. Hemos propuesto un mapeo a partir de las cabeceras y de importaciones anteriores.',
    mapToLabel: 'Campo de destino',
    unmapped: 'No mapear',
    sampleValue: 'Ejemplo: {{value}}',
    mapMissingRequired: 'Faltan campos obligatorios: {{fields}}',
    toPreview: 'Revisa las filas',
    checkingCatalog: 'Comprobando el catálogo…',
    // Mappable target fields
    field_sku: 'Código (SKU)',
    field_name: 'Nombre',
    field_article_type: 'Tipología',
    field_base_unit: 'Unidad base',
    field_recipe_unit: 'Unidad de receta',
    field_recipe_unit_factor: 'Equivalencia de receta',
    field_category: 'Categoría',
    field_barcode: 'Código de barras',
    field_shelf_life_days: 'Vida útil (días)',
    field_min_stock: 'Existencias mínimas',
    field_max_stock: 'Existencias máximas',
    field_vat_rate: 'IVA (%)',
    field_tax: 'Impuesto (IVA)',
    field_purchase_price: 'Precio de compra',
    field_sale_price: 'Precio de venta',
    field_is_purchased: 'Se compra',
    field_is_manufactured: 'Se fabrica',
    field_is_sold: 'Se vende',
    field_is_variable_conversion: 'Conversión variable',
    field_default_zone: 'Zona por defecto (código)',
    field_packaging_units: 'Unidades de embalaje',
    field_composition: 'Composición declarada',
    // Validation messages (per row)
    msgRequiredMissing: 'Falta el campo obligatorio «{{field}}»',
    msgUnknownTax:
      'El impuesto «{{value}}» no existe. Usa el nombre o el tipo de un impuesto de Configuración · Impuestos.',
    msgUnknownUnit: 'Unidad desconocida: «{{code}}»',
    msgRecipeUnitFactorMissing:
      'Falta la equivalencia de receta: la unidad de receta «{{code}}» es distinta de la base. Indica cuántas unidades de receta equivalen a una unidad base.',
    msgRecipeUnitFactorInvalid:
      'La equivalencia de receta «{{value}}» debe ser un número mayor que cero.',
    msgRecipeFactorWithoutUnit:
      'Se ha indicado una equivalencia de receta sin unidad de receta: no se puede aplicar.',
    msgRecipeUnitNotRawMaterial:
      'La unidad de receta «{{code}}» solo es para materias primas: se omitirá en este artículo.',
    msgUnknownCategory: 'Categoría desconocida «{{name}}»; se omitirá',
    msgUnknownZone: 'La zona «{{code}}» no existe. Usa el código de una zona del almacén.',
    msgSalePriceNotSold:
      'Precio de venta «{{value}}» en un artículo marcado como no vendible: se guardará, pero la ficha no lo mostrará.',
    msgUnknownPackagingUnit:
      'La unidad de embalaje «{{name}}» no existe en el maestro; créala en Configuración antes de importar.',
    msgPackaging_syntax:
      'La {{position}}ª unidad de embalaje («{{part}}») no tiene la forma «Unidad=factor».',
    msgPackaging_factor:
      'La {{position}}ª unidad de embalaje («{{part}}») no tiene un factor numérico mayor que cero.',
    msgPackaging_duplicate:
      'La {{position}}ª unidad de embalaje («{{part}}») repite una unidad: un artículo solo puede tener una medida.',
    msgComposition_syntax:
      'El {{position}}º ingrediente («{{part}}») no tiene la forma «Ingrediente» o «Ingrediente=%».',
    msgComposition_percentage:
      'El {{position}}º ingrediente («{{part}}») no tiene un porcentaje numérico entre 0 y 100.',
    msgComposition_duplicate:
      'El {{position}}º ingrediente («{{part}}») ya aparece antes en la lista: un artículo no puede declararlo dos veces.',
    msgNewIngredients_one: 'Se creará el ingrediente «{{names}}» en el maestro.',
    msgNewIngredients_other: 'Se crearán {{count}} ingredientes nuevos en el maestro: {{names}}.',
    msgCompositionDerived:
      'Este artículo saca los ingredientes de la receta, no de la ficha: la composición se guardará, pero la ficha técnica no la usará.',
    msgCompositionReplaces:
      'Sustituirá los {{before}} ingredientes que el artículo tiene ahora por los {{after}} de la hoja: la celda vale por la lista entera, porque el orden es lo que manda en la etiqueta.',
    msgInvalidArticleType: 'Tipología no válida: «{{value}}»',
    msgInvalidNumber: 'Valor numérico no válido en «{{field}}»: «{{value}}»',
    msgInvalidBoolean: 'Valor sí/no no válido en «{{field}}»: «{{value}}»',
    msgSchemaIssue: '{{path}}: {{message}}',
    msgDuplicateInFile: 'Código repetido en el archivo (ya está en la fila {{row}})',
    msgSkuFormat:
      'El código «{{code}}» no sigue el formato de códigos de artículo del obrador (como {{example}}). No se corrige solo: arréglalo en el archivo.',
    // Preview step
    previewTitle: 'Revisión de filas',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    previewSummary_other:
      '{{count}} filas · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    colRow: 'Fila',
    colAction: 'Acción',
    colSkuName: 'Código · Nombre',
    colMessages: 'Observaciones',
    noMessages: '—',
    // What to do with codes that already exist
    strategyTitle: 'Códigos que ya existen',
    strategyLabel: '¿Qué hacemos con los artículos que ya están?',
    strategyHint:
      'Solo se escriben las columnas que has mapeado y rellenado. Una celda vacía nunca borra un dato existente, y una columna que no has mapeado no se toca.',
    strategy_update_existing: 'Actualízalos con los datos de la hoja',
    strategy_skip_existing: 'Déjalos tal como están',
    // Per-row outcome
    actionCreated: 'Nuevo',
    actionUpdated: 'Actualiza',
    actionUnchanged: 'Sin cambios',
    actionSkipped: 'Omitida',
    actionError: 'Error',
    noteWillUpdate: 'Cambia: {{fields}}',
    noteWillSkip: 'Ya existe; no se tocará (cambiaría: {{fields}})',
    noteUnchanged: 'Ya existe con la misma información',
    nothingToChange:
      'Ninguna fila cambia nada: todos estos artículos ya están con la misma información.',
    nothingToImport: 'No hay ninguna fila válida para importar. Revisa el mapeo o el archivo.',
    commitCreateOnly_one: 'Crea {{count}} artículo',
    commitCreateOnly_other: 'Crea {{count}} artículos',
    commitUpdateOnly_one: 'Actualiza {{count}} artículo',
    commitUpdateOnly_other: 'Actualiza {{count}} artículos',
    commitBoth: 'Crea {{created}} y actualiza {{updated}}',
    committing: 'Escribiendo…',
    noRunError: 'No se ha encontrado la importación; vuelve a revisar las filas.',
    // Result step
    resultSummary_one: 'Importación completada · {{count}} artículo escrito en el catálogo',
    resultSummary_other: 'Importación completada · {{count}} artículos escritos en el catálogo',
    resultNoChanges_one:
      'No se ha cambiado nada en el catálogo: la fila del archivo no ha creado ni actualizado ningún artículo.',
    resultNoChanges_other:
      'No se ha cambiado nada en el catálogo: ninguna de las {{count}} filas del archivo ha creado ni actualizado ningún artículo.',
    resultTitle: 'Resultado de la importación',
    resultCreated: 'Creados',
    resultUpdated: 'Actualizados',
    resultUnchanged: 'Sin cambios',
    resultSkipped: 'Omitidos',
    resultErrors: 'Errores',
    resultTotal_one: '{{count}} fila en el archivo',
    resultTotal_other: '{{count}} filas en el archivo',
    newImport: 'Nueva importación',
    viewCatalog: 'Ir al catálogo',
    // ── Existences importer (entity 'stock') ──────────────────────────────────
    entityTitle: '¿Qué quieres importar?',
    entityLabel: 'Tipo de datos',
    entityHint: 'Cambiar de tipo vacía lo que tengas a medio importar.',
    entity_articles: 'Catálogo de artículos',
    entity_stock: 'Existencias',
    // Mappable stock fields
    field_quantity: 'Cantidad',
    field_unit: 'Unidad',
    field_lot_code: 'Lote',
    field_expiry_date: 'Caducidad',
    // Stock template
    templatesHintStock:
      'Descarga una hoja con las columnas que el importador reconoce, con un ejemplo con lote y otro sin él.',
    downloadTemplateStock: 'Plantilla de existencias',
    downloadTemplateStockHint:
      'Escribe la cantidad TOTAL que tienes de cada artículo. Deja la unidad en blanco para usar la unidad base del artículo, y escribe la caducidad como 2026-12-31.',
    templateStockSheetName: 'Existencias',
    templateStockFilename: 'plantilla-existencias.xlsx',
    // Stock validation messages
    msgUnknownArticle: 'No hay ningún artículo con el código «{{sku}}»; créalo antes en el catálogo',
    msgArticleNotStockTracked:
      'El artículo «{{sku}}» no gestiona existencias; se cargarán igualmente',
    msgNegativeQuantity: 'La cantidad no puede ser negativa: «{{value}}»',
    msgNoUnitConversion:
      'No hay ninguna equivalencia declarada de «{{code}}» a la unidad base de «{{sku}}»',
    msgInvalidDate: 'Fecha no válida: «{{value}}»',
    msgExpiryNeedsLot:
      'La caducidad «{{value}}» necesita un lote; informa el lote o quita la caducidad',
    msgAmbiguousDate: '«{{value}}» se ha leído como {{date}} (día/mes); compruébalo',
    msgExpiryInPast: 'La caducidad {{date}} ya ha pasado',
    msgLotExpiryKept:
      'El lote «{{lot}}» ya existe con caducidad {{stored}}; se mantiene la que ya consta',
    msgDuplicateCoordinateInFile: 'Este artículo y lote ya salen en la fila {{row}}',
    // Stock preview
    stockLotNote:
      'El código de lote del archivo se guarda como «Lote del proveedor», para que coincida con el del proveedor. El número de lote interno (MP-L-…) lo genera Obrava solo y no se puede indicar en el archivo: en Existencias aparece con el código del proveedor al lado.',
    strategyHintStock:
      'Cada fila dice el TOTAL que hay. Solo se escribe la diferencia con lo que ya consta, así que volver a importar el mismo fichero no mueve nada.',
    strategyStock_update_existing: 'Ajústalos a la cantidad de la hoja',
    strategyStock_skip_existing: 'Déjalos tal como están',
    noteStockLot: 'lote {{lot}}',
    noteStockCreate: 'Se cargarán {{quantity}}',
    noteStockUpdate: 'Pasará de {{from}} a {{to}}',
    noteStockSkip: 'Se queda con {{current}}',
    noteStockUnchanged: 'Ya hay {{quantity}}',
    commitCreateOnlyStock_one: 'Carga {{count}} existencia',
    commitCreateOnlyStock_other: 'Carga {{count}} existencias',
    commitUpdateOnlyStock_one: 'Ajusta {{count}} existencia',
    commitUpdateOnlyStock_other: 'Ajusta {{count}} existencias',
    commitBothStock: 'Carga {{created}} y ajusta {{updated}}',
    // Stock commit + result
    stockContextMissing:
      'Todavía no se han podido leer las existencias actuales; vuelve a probarlo en un momento.',
    stockReceptionReference: 'SALDO-INICIAL',
    stockReceptionNote: 'Carga inicial de existencias (importador)',
    resultSummaryStock_one: 'Importación completada · {{count}} línea de existencias escrita',
    resultSummaryStock_other: 'Importación completada · {{count}} líneas de existencias escritas',
    resultNoChangesStock_one:
      'No se ha movido nada: la fila del fichero ya coincidía con las existencias actuales.',
    resultNoChangesStock_other:
      'No se ha movido nada: ninguna de las {{count}} filas ha cambiado las existencias actuales.',
    viewStock: 'Ir a existencias',
  },

  'backoffice.supplierImport': {
    title: 'Importar proveedores',
    subtitle: 'Carga la lista de proveedores desde una hoja de cálculo',
    // Paso 1 — plantilla y archivo
    templateTitle: '1. Descarga la plantilla',
    templateHint:
      'La plantilla trae las columnas que Obrava reconoce y una fila de ejemplo. Rellénala y vuelve a subirla.',
    downloadTemplate: 'Plantilla vacía',
    downloadCurrent: 'Plantilla con los proveedores actuales ({{count}})',
    downloadCurrentHint:
      'Descarga los proveedores que ya tienes, edítalos en Excel y vuelve a subirlos: las filas que no toques no escribirán nada.',
    templateFilename: 'plantilla-proveedores.xlsx',
    currentFilename: 'proveedores-obrador.xlsx',
    templateSheetName: 'Proveedores',
    uploadTitle: '2. Sube el archivo relleno',
    uploadHint:
      'Acepta CSV, XLS y XLSX. La primera fila debe ser la cabecera, con los nombres de columna de la plantilla.',
    chooseFile: 'Elige un archivo',
    reading: 'Leyendo el archivo…',
    fileReadError: 'No se ha podido leer el archivo.',
    noRows: 'El archivo no tiene ninguna fila de datos. Comprueba que hay cabecera y al menos una fila.',
    missingColumns:
      'No se ha encontrado la columna obligatoria «{{fields}}». Descarga la plantilla y usa sus nombres de columna.',
    ignoredColumns: 'Columnas que no se usarán: {{columns}}',
    legacyTermsColumn_one: 'La columna «{{column}}» ya no se importa: Obrava ya no tiene «Condiciones comerciales». {{count}} fila tiene contenido: pásalo a «Notas» y vuelve a subir el archivo.',
    legacyTermsColumn_other: 'La columna «{{column}}» ya no se importa: Obrava ya no tiene «Condiciones comerciales». {{count}} filas tienen contenido: pásalo a «Notas» y vuelve a subir el archivo.',
    columnsGuide: 'Días de entrega: días de la semana separados por comas, p. ej. «L, X, V» (también «Lunes, Miércoles» o «L-V»). Canal de pedidos: «Correo», «WhatsApp» u «Otros medios»; con canal Correo hace falta el correo de pedidos, y con canal WhatsApp el teléfono/WhatsApp de pedidos (el correo y el teléfono generales no los sustituyen). La información libre, como las condiciones de pago, va a «Notas». Los tres correos —general, de pedidos y de calidad— son columnas independientes: ninguno se supone a partir de otro.',
    guideSheetName: 'Instrucciones',
    guideColField: 'Campo',
    guideColWhat: 'Qué va',
    guideColExample: 'Ejemplo',
    guide_code:
      'Opcional. En blanco, Obrava lo genera al crear el proveedor. Un código nuevo debe seguir el formato de códigos del obrador (Configuración) y no se puede repetir; un código que ya existe identifica al proveedor.',
    guide_name: 'Obligatorio.',
    guide_commercial_name: 'Opcional. El nombre con el que se le conoce, si no es la razón social.',
    guide_tax_id: 'Opcional. No se puede repetir entre proveedores activos.',
    guide_contact_name: 'Opcional.',
    guide_phone: 'Opcional. Teléfono general de contacto; el de pedidos va aparte.',
    guide_email: 'Opcional. Correo general de contacto; los de pedidos y de calidad van aparte.',
    guide_address: 'Opcional. Dirección principal (fiscal).',
    guide_delivery_weekdays: 'Opcional. Días de la semana en que pasa, separados por comas: L, M, X, J, V, S, D (también los nombres enteros o un intervalo como L-V).',
    guide_order_channel: 'Opcional. Correo, WhatsApp u Otros medios (teléfono, en persona…).',
    guide_order_emails: 'Uno o más correos separados por comas (máximo 10). Obligatorio si el canal es Correo.',
    guide_order_phone: 'Teléfono o WhatsApp de pedidos, con el prefijo del país si es posible. Obligatorio si el canal es WhatsApp.',
    guide_quality_email: 'Opcional. Un correo: adonde van las solicitudes y renovaciones de fichas técnicas.',
    guide_notes: 'Opcional. Información libre: condiciones de pago, observaciones…',
    guide_status: 'Opcional. Activo o Inactivo.',
    recognizedColumns: 'Columnas reconocidas: {{columns}}',
    // Campos
    field_code: 'Código de proveedor',
    field_name: 'Nombre / razón social',
    field_commercial_name: 'Nombre comercial',
    field_tax_id: 'NIF / CIF',
    field_contact_name: 'Persona de contacto',
    field_phone: 'Teléfono',
    field_email: 'Correo electrónico (general)',
    field_address: 'Dirección principal',
    field_delivery_weekdays: 'Días habituales de entrega',
    field_order_channel: 'Canal de pedidos',
    field_order_emails: 'Correo electrónico de pedidos',
    field_order_phone: 'Teléfono/WhatsApp de pedidos',
    field_quality_email: 'Correo electrónico de calidad',
    field_notes: 'Notas',
    field_status: 'Estado',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    channelEmail: 'Correo',
    channelWhatsapp: 'WhatsApp',
    channelManual: 'Otros medios',
    templateExampleName: 'Harinas Puig, SL',
    templateExampleCommercialName: 'Harinas Puig',
    templateExampleContact: 'Ana Puig',
    templateExampleAddress: 'Calle del Molino, 12 · 17800 Olot',
    templateExampleNotes: 'Pago a 30 días · portes pagados',
    // Observaciones por fila
    msgRequiredMissing: 'Falta el campo obligatorio «{{field}}»',
    msgInvalidEmail: 'Correo electrónico no válido en «{{field}}»: «{{value}}»',
    msgInvalidStatus: 'Estado no reconocido: «{{value}}». Usa «Activo» o «Inactivo».',
    msgInvalidBoolean: 'Valor sí/no no válido en «{{field}}»: «{{value}}»',
    msgInvalidNumber: 'Valor numérico no válido en «{{field}}»: «{{value}}»',
    msgAmbiguousTaxId:
      'Hay más de un proveedor activo con el NIF «{{value}}»: informa el código para decir cuál es.',
    msgUnidentifiable:
      'Sin código ni NIF no se puede comprobar si ya existe; se creará como proveedor nuevo.',
    msgDuplicateInFile: 'Repetido en el archivo (ya está en la fila {{row}})',
    msgCodeTaken: 'El código «{{code}}» ya es de otro proveedor ({{name}}).',
    msgCodeFormat:
      'El código «{{code}}» de «{{field}}» no sigue el formato de códigos del obrador (como {{example}}). No se corrige solo: arréglalo en el archivo o déjalo vacío y se generará uno.',
    msgTaxIdTaken: 'El NIF «{{value}}» ya es de otro proveedor ({{name}}).',
    msgTaxIdInFile: 'El NIF «{{value}}» ya aparece en la fila {{row}} del archivo.',
    msgInvalidWeekdays: 'Días de entrega no reconocidos en «{{field}}»: «{{value}}». Escríbelos como «L, X, V» (también «Lunes, Miércoles» o «L-V»).',
    msgInvalidChannel: 'Canal no reconocido en «{{field}}»: «{{value}}». Valores admitidos: Correo, WhatsApp, Otros medios.',
    msgChannelUnavailable: 'El canal «{{value}}» todavía no está disponible en Obrava. Valores admitidos: Correo, WhatsApp, Otros medios.',
    msgInvalidOrderEmails: 'Correo de pedidos no válido: «{{value}}»',
    msgTooManyOrderEmails: 'Como máximo {{max}} correos de pedidos por proveedor.',
    msgInvalidPhone: 'Teléfono no válido en «{{field}}»: «{{value}}». Usa de 7 a 15 cifras, con espacios y el prefijo + si hace falta.',
    msgOrderEmailRequired: 'El canal es Correo: falta «{{field}}». El correo general no lo sustituye.',
    msgOrderPhoneRequired: 'El canal es WhatsApp: falta «{{field}}». El teléfono general no lo sustituye.',
    // Paso 3 — revisión
    previewTitle: '3. Revisa antes de importar',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    previewSummary_other:
      '{{count}} filas · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    colRow: 'Fila',
    colAction: 'Acción',
    colSupplier: 'Proveedor',
    colMessages: 'Observaciones',
    noMessages: '—',
    codeWillBeGenerated: 'código automático',
    strategyLabel: '¿Qué hacemos con los proveedores que ya existen?',
    strategyHint:
      'Solo se escriben las columnas que la hoja trae rellenas. Una celda vacía nunca borra un dato existente.',
    strategy_update_existing: 'Actualízalos con los datos de la hoja',
    strategy_skip_existing: 'Déjalos tal como están',
    actionCreated: 'Nuevo',
    actionUpdated: 'Actualiza',
    actionUnchanged: 'Sin cambios',
    actionSkipped: 'Omitida',
    actionError: 'Error',
    noteWillUpdate: 'Cambia: {{fields}}',
    noteWillSkip: 'Ya existe; no se tocará (cambiaría: {{fields}})',
    noteUnchanged: 'Ya existe con la misma información',
    noteWillDeactivate: 'Se dará de baja',
    noteWillReactivate: 'Se reactivará',
    deactivationReason: 'Importación de proveedores: el archivo lo marca como inactivo',
    nothingToChange: 'Ninguna fila cambia nada: todos los proveedores del archivo ya están igual.',
    nothingToImport: 'Ninguna fila se puede importar. Corrige los errores y vuelve a subir el archivo.',
    commitCreateOnly_one: 'Crea {{count}} proveedor',
    commitCreateOnly_other: 'Crea {{count}} proveedores',
    commitUpdateOnly_one: 'Actualiza {{count}} proveedor',
    commitUpdateOnly_other: 'Actualiza {{count}} proveedores',
    commitBoth: 'Crea {{created}} y actualiza {{updated}}',
    committing: 'Importando…',
    back: 'Atrás',
    // Paso 4 — resultado
    resultTitle: 'Resultado de la importación',
    resultSummary_one: '{{count}} proveedor importado',
    resultSummary_other: '{{count}} proveedores importados',
    resultNoChanges_one: 'No se ha cambiado nada: {{count}} fila procesada.',
    resultNoChanges_other: 'No se ha cambiado nada: {{count}} filas procesadas.',
    resultCreated: 'Creados',
    resultUpdated: 'Actualizados',
    resultUnchanged: 'Sin cambios',
    resultSkipped: 'Omitidos',
    resultErrors: 'Errores',
    resultTotal_one: '{{count}} fila en el archivo',
    resultTotal_other: '{{count}} filas en el archivo',
    newImport: 'Importar otro archivo',
    close: 'Cerrar',
  },

  'backoffice.supplierArticleImport': {
    title: 'Importar relación artículo-proveedor',
    subtitle: 'Carga qué proveedores sirven cada artículo, a qué precio y en qué unidad',
    prerequisites:
      'Antes hay que tener dados de alta los proveedores, los artículos y las unidades de compra de cada artículo. El importador no crea ninguno.',
    // Paso 1 — plantilla y archivo
    templateTitle: '1. Descarga la plantilla',
    templateHint:
      'Una fila por relación artículo ↔ proveedor. Los códigos deben existir: el importador enlaza, no crea artículos ni proveedores ni conversiones.',
    downloadTemplate: 'Plantilla vacía',
    downloadCurrent: 'Plantilla con las relaciones actuales ({{count}})',
    downloadCurrentHint:
      'Descarga lo que ya tienes, edítalo en Excel y vuelve a subirlo: las filas que no toques no escribirán nada.',
    templateFilename: 'plantilla-relacion-articulo-proveedor.xlsx',
    currentFilename: 'relacion-articulo-proveedor.xlsx',
    templateSheetName: 'Relación artículo-proveedor',
    uploadTitle: '2. Sube el archivo relleno',
    uploadHint:
      'Acepta CSV, XLS y XLSX. La primera fila debe ser la cabecera, con los nombres de columna de la plantilla.',
    chooseFile: 'Elige un archivo',
    reading: 'Leyendo el archivo…',
    fileReadError: 'No se ha podido leer el archivo.',
    noRows: 'El archivo no tiene ninguna fila de datos. Comprueba que hay cabecera y al menos una fila.',
    missingColumns:
      'No se ha encontrado la columna obligatoria «{{fields}}». Descarga la plantilla y usa sus nombres de columna.',
    ignoredColumns: 'Columnas que no se usarán: {{columns}}',
    // Campos
    field_article_code: 'Código artículo',
    field_supplier_code: 'Código proveedor',
    field_supplier_ref: 'Referencia del proveedor',
    field_supplier_description: 'Descripción del proveedor',
    field_last_price: 'Precio de compra (por unidad base)',
    field_purchase_unit: 'Unidad de compra',
    field_purchase_unit_factor: 'Equivalencia (informativa)',
    field_is_primary: 'Proveedor principal',
    booleanTrue: 'Sí',
    booleanFalse: 'No',
    // Ejemplos de la plantilla
    exampleArticleCode: 'MP00001',
    exampleSupplierCode: 'PRV-0001',
    exampleSupplierRef: 'FAR-25',
    exampleDescription: 'Harina de fuerza 25 kg',
    exampleUnit: 'Saco',
    // Observaciones por fila
    msgRequiredMissing: 'Falta el campo obligatorio «{{field}}»',
    msgInvalidNumber: 'Valor numérico no válido en «{{field}}»: «{{value}}»',
    msgInvalidBoolean: 'Valor sí/no no válido en «{{field}}»: «{{value}}»',
    msgUnknownArticle: 'El código de artículo «{{code}}» no existe en el catálogo.',
    msgUnknownSupplier: 'El código de proveedor «{{code}}» no existe en el maestro.',
    msgUnknownUnit:
      'La unidad «{{unit}}» no existe. Usa el nombre de una unidad de envase o el código de la unidad base del artículo.',
    msgUnitNotForArticle:
      'El artículo «{{code}}» no tiene ninguna conversión para «{{unit}}»: defínela en la ficha del artículo.',
    msgDuplicateInFile:
      'Este artículo y este proveedor ya aparecen juntos en la fila {{row}}.',
    msgTwoPrimaries:
      'Este artículo ya tiene la fila {{row}} marcada como proveedor principal; solo puede haber uno.',
    msgPrimaryMoves: 'Pasará a ser el proveedor principal (dejará de serlo {{name}})',
    // Paso 3 — revisión
    previewTitle: '3. Revisa antes de importar',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    previewSummary_other:
      '{{count}} filas · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    colRow: 'Fila',
    colAction: 'Acción',
    colLink: 'Artículo · Proveedor',
    colMessages: 'Observaciones',
    noMessages: '—',
    strategyLabel: '¿Qué hacemos con las relaciones que ya existen?',
    strategyHint:
      'Solo se escriben las columnas que la hoja trae rellenas. Una celda vacía nunca borra un dato, y una relación que la hoja no menciona no se toca.',
    strategy_update_existing: 'Actualízalas con los datos de la hoja',
    strategy_skip_existing: 'Déjalas tal como están',
    actionCreated: 'Nueva',
    actionUpdated: 'Actualiza',
    actionUnchanged: 'Sin cambios',
    actionSkipped: 'Omitida',
    actionError: 'Error',
    noteWillUpdate: 'Cambia: {{fields}}',
    noteWillSkip: 'Ya existe; no se tocará (cambiaría: {{fields}})',
    noteUnchanged: 'Ya existe con la misma información',
    nothingToChange: 'Ninguna fila cambia nada: todo lo que trae el archivo ya está igual.',
    nothingToImport: 'Ninguna fila se puede importar. Corrige los errores y vuelve a subir el archivo.',
    commitCreateOnly_one: 'Crea {{count}} relación',
    commitCreateOnly_other: 'Crea {{count}} relaciones',
    commitUpdateOnly_one: 'Actualiza {{count}} relación',
    commitUpdateOnly_other: 'Actualiza {{count}} relaciones',
    commitBoth: 'Crea {{created}} y actualiza {{updated}}',
    committing: 'Importando…',
    back: 'Atrás',
    // Paso 4 — resultado
    resultTitle: 'Resultado de la importación',
    resultSummary_one: '{{count}} relación importada',
    resultSummary_other: '{{count}} relaciones importadas',
    resultNoChanges_one: 'No se ha cambiado nada: {{count}} fila procesada.',
    resultNoChanges_other: 'No se ha cambiado nada: {{count}} filas procesadas.',
    resultCreated: 'Creadas',
    resultUpdated: 'Actualizadas',
    resultUnchanged: 'Sin cambios',
    resultSkipped: 'Omitidas',
    resultErrors: 'Errores',
    resultTotal_one: '{{count}} fila en el archivo',
    resultTotal_other: '{{count}} filas en el archivo',
    newImport: 'Importar otro archivo',
    close: 'Cerrar',
  },
  'backoffice.clientImport': {
    title: 'Importar clientes',
    subtitle: 'Carga los clientes y sus puntos de envío desde una hoja de cálculo',
    // Paso 1 — plantilla y archivo
    templateTitle: '1. Descarga la plantilla',
    templateHint:
      'La plantilla tiene dos pestañas: «Clientes» (una fila por cliente) y «Direcciones de envío» (una fila por punto de entrega, ligada al cliente por su código).',
    downloadTemplate: 'Plantilla vacía',
    downloadCurrent: 'Plantilla con los clientes actuales ({{count}})',
    downloadCurrentHint:
      'Descarga los clientes y los puntos que ya tienes, edítalos en Excel y vuelve a subirlos: las filas que no toques no escribirán nada.',
    templateFilename: 'plantilla-clientes.xlsx',
    currentFilename: 'clientes-obrador.xlsx',
    sheetClients: 'Clientes',
    sheetPoints: 'Direcciones de envío',
    uploadTitle: '2. Sube el archivo relleno',
    uploadHint:
      'Acepta XLSX y XLS con las dos pestañas. Un CSV también vale, pero solo puede traer clientes: un archivo plano no tiene pestañas.',
    chooseFile: 'Elige un archivo',
    reading: 'Leyendo el archivo…',
    fileReadError: 'No se ha podido leer el archivo.',
    noRows: 'La pestaña «{{sheet}}» no tiene ninguna fila de datos. Comprueba que hay cabecera y al menos una fila.',
    missingColumns:
      'En la pestaña «{{sheet}}» no se ha encontrado la columna obligatoria «{{fields}}». Descarga la plantilla y usa sus nombres de columna.',
    ignoredColumns: '{{sheet}} · columnas que no se usarán: {{columns}}',
    noPointsSheet:
      'El archivo no trae la pestaña «Direcciones de envío»: se importarán solo los clientes.',
    // Campos del cliente
    field_code: 'Código cliente',
    field_name: 'Nombre / razón social',
    field_commercial_name: 'Nombre comercial',
    field_tax_id: 'NIF / CIF',
    field_contact_name: 'Persona de contacto general',
    field_phone: 'Teléfono general',
    field_email: 'Correo electrónico general',
    field_address: 'Dirección fiscal',
    field_notes: 'Notas',
    field_status: 'Estado',
    // Campos del punto de envío
    point_client_code: 'Código cliente',
    point_code: 'Código punto de envío',
    point_name: 'Nombre del punto de envío',
    point_address: 'Dirección',
    point_postal_code: 'Código postal',
    point_city: 'Población',
    point_province: 'Provincia',
    point_country: 'País',
    point_contact_name: 'Persona de contacto',
    point_phone: 'Teléfono',
    point_email: 'Correo electrónico',
    point_delivery_instructions: 'Instrucciones de entrega',
    point_is_default: 'Dirección predeterminada',
    statusActive: 'Activo',
    statusInactive: 'Inactivo',
    booleanTrue: 'Sí',
    booleanFalse: 'No',
    // Ejemplos de la plantilla
    exampleClientName: 'Pastelerías Girona SL',
    exampleClientCommercial: 'Pastelerías Girona',
    exampleClientContact: 'Marta Vidal',
    exampleClientAddress: 'Paseo de la Devesa, 4 · 17001 Girona',
    exampleClientNotes: 'Factura a final de mes',
    examplePointName: 'Tienda Girona Centro',
    examplePointAddress: 'Calle Nou, 21',
    examplePointCity: 'Girona',
    examplePointProvince: 'Girona',
    examplePointCountry: 'España',
    examplePointContact: 'Jordi Serra',
    examplePointInstructions: 'Entrega antes de las 9 h · muelle trasero',
    exampleWarehouseName: 'Almacén central',
    exampleWarehouseAddress: 'Polígono Mas Xirgu, nave 7',
    // Observaciones por fila
    msgRequiredMissing: 'Falta el campo obligatorio «{{field}}»',
    msgInvalidEmail: 'Correo electrónico no válido: «{{value}}»',
    msgInvalidStatus: 'Estado no reconocido: «{{value}}». Usa «Activo» o «Inactivo».',
    msgInvalidBoolean: 'Valor sí/no no válido en «{{field}}»: «{{value}}»',
    msgInvalidNumber: 'Valor numérico no válido en «{{field}}»: «{{value}}»',
    msgAmbiguousTaxId:
      'Hay más de un cliente activo con el NIF «{{value}}»: informa el código para decir cuál es.',
    msgUnidentifiable:
      'Sin código ni NIF no se puede comprobar si ya existe; se creará como cliente nuevo.',
    msgDuplicateInFile: 'Repetido en el archivo (ya está en la fila {{row}})',
    msgCodeTaken: 'El código «{{code}}» ya es de otro cliente ({{name}}).',
    msgCodeFormat:
      'El código «{{code}}» de «{{field}}» no sigue el formato de códigos del obrador (como {{example}}). No se corrige solo: arréglalo en el archivo o déjalo vacío y se generará uno.',
    msgTaxIdTaken: 'El NIF «{{value}}» ya es de otro cliente ({{name}}).',
    msgTaxIdInFile: 'El NIF «{{value}}» ya aparece en la fila {{row}} del archivo.',
    msgUnknownClientCode:
      'El código de cliente «{{code}}» no existe ni aparece en la pestaña «Clientes».',
    msgClientRowFailed:
      'El cliente «{{code}}» no se importará (su fila tiene errores), así que este punto tampoco.',
    msgPointDuplicateInFile:
      'Este punto ya aparece en la fila {{row}} para el mismo cliente.',
    msgPointAmbiguousName:
      'El cliente ya tiene más de un punto llamado «{{name}}»: informa el código del punto para decir cuál es.',
    msgPointAddressRequired: 'Un punto nuevo necesita una dirección.',
    msgTwoDefaults:
      'Este cliente ya tiene la fila {{row}} marcada como dirección predeterminada; solo puede haber una.',
    msgDefaultMoves: 'Pasará a ser la dirección predeterminada del cliente',
    // Paso 3 — revisión
    previewTitle: '3. Revisa antes de importar',
    previewClients: 'Clientes',
    previewPoints: 'Puntos de envío',
    previewSummary_one:
      '{{count}} fila · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    previewSummary_other:
      '{{count}} filas · {{created}} a crear · {{updated}} a actualizar · {{unchanged}} sin cambios · {{skipped}} a omitir · {{error}} con error',
    noPointRows: 'El archivo no trae ningún punto de envío.',
    colRow: 'Fila',
    colAction: 'Acción',
    colClient: 'Cliente',
    colPoint: 'Punto',
    colMessages: 'Observaciones',
    noMessages: '—',
    codeWillBeGenerated: 'código automático',
    strategyLabel: '¿Qué hacemos con los clientes y los puntos que ya existen?',
    strategyHint:
      'Solo se escriben las columnas que la hoja trae rellenas. Una celda vacía nunca borra un dato, y un punto de envío que la hoja no menciona no se toca.',
    strategy_update_existing: 'Actualízalos con los datos de la hoja',
    strategy_skip_existing: 'Déjalos tal como están',
    actionCreated: 'Nuevo',
    actionUpdated: 'Actualiza',
    actionUnchanged: 'Sin cambios',
    actionSkipped: 'Omitida',
    actionError: 'Error',
    noteWillUpdate: 'Cambia: {{fields}}',
    noteWillSkip: 'Ya existe; no se tocará (cambiaría: {{fields}})',
    noteUnchanged: 'Ya existe con la misma información',
    noteWillDeactivate: 'Se dará de baja',
    noteWillReactivate: 'Se reactivará',
    deactivationReason: 'Importación de clientes: el archivo lo marca como inactivo',
    nothingToChange: 'Ninguna fila cambia nada: todo lo que trae el archivo ya está igual.',
    nothingToImport: 'Ninguna fila se puede importar. Corrige los errores y vuelve a subir el archivo.',
    commitLabel: 'Importar',
    commitClients_one: '{{count}} cliente',
    commitClients_other: '{{count}} clientes',
    commitPoints_one: '{{count}} punto',
    commitPoints_other: '{{count}} puntos',
    committing: 'Importando…',
    back: 'Atrás',
    // Paso 4 — resultado
    resultTitle: 'Resultado de la importación',
    resultClients: 'Clientes',
    resultPoints: 'Puntos de envío',
    resultSummary: 'Importación hecha: {{clients}} y {{points}}',
    resultNoChanges: 'No se ha cambiado nada.',
    resultCreated: 'Creados',
    resultUpdated: 'Actualizados',
    resultUnchanged: 'Sin cambios',
    resultSkipped: 'Omitidos',
    resultErrors: 'Errores',
    newImport: 'Importar otro archivo',
    close: 'Cerrar',
  },
  'backoffice.settings': {
    title: 'Configuración',
    subtitle: 'Administración del obrador',
    // Hub (grouped settings index)
    hubSearchPlaceholder: 'Busca un ajuste',
    hubSearchNoResults: 'Ningún ajuste coincide con la búsqueda',
    groupObrador: 'Obrador',
    groupProduction: 'Producción',
    groupCatalog: 'Catálogo',
    groupLabeling: 'Etiquetado y normativa',
    groupThirdParties: 'Terceros',
    descGeneral: 'Numeración, umbrales y sesión de operario',
    descShifts: 'Turnos de trabajo del obrador',
    descStructure: 'Zonas, secciones y ubicaciones: dónde se trabaja y dónde están las existencias',
    descCategories: 'Clasificación de artículos del catálogo',
    descUnits: 'Unidades de medida',
    descTaxes: 'IVA y otros impuestos aplicables',
    descPackagingUnits: 'Unidades de embalaje y presentación',
    descArticleFamilies: 'Conservación, uso, preparación y envase',
    descIngredients: 'Maestro de ingredientes de las recetas',
    descProcesses: 'Pasos reutilizables de las recetas',
    descResources: 'Hornos, amasadoras y otros equipos',
    descLegislation: 'Normativa de referencia para las FT',
    descLabelTemplates: 'Diseños de etiqueta para lotes',
    descBranding: 'Logotipo e identidad de las FT',
    descClients: 'Maestro de clientes para ventas',
    descSuppliers: 'Maestro de proveedores para compras',
    descDevices: 'Tabletas y dispositivos de planta',
    descAuditLog: 'Quién ha cambiado qué y cuándo',
    // Tabs
    tabGeneral: 'General',
    tabShifts: 'Turnos',
    tabStructure: 'Estructura del obrador',
    tabCategories: 'Categorías',
    tabUnits: 'Unidades',
    tabTaxes: 'Impuestos',
    tabPackagingUnits: 'Unidades de embalaje',
    tabClients: 'Clientes',
    tabSuppliers: 'Proveedores',
    tabDevices: 'Dispositivos',
    tabAuditLog: 'Registro de actividad',
    // Registro de actividad (F0.9)
    auditTitle: 'Registro de actividad',
    auditSubtitle: 'Historial de altas, cambios y bajas del obrador: quién, cuándo y qué',
    auditEmpty: 'Aún no hay actividad registrada',
    auditFilterAllActions: 'Todas las acciones',
    auditFilterAllEntities: 'Todos los elementos',
    auditLoadMore: 'Cargar más',
    // Shared
    general: 'General',
    numbering: 'Numeración',
    // Regla de composición de la declaración de ingredientes (FT + etiqueta)
    ingredientsRuleTitle: 'Declaración de ingredientes',
    ingredientsRuleIntro:
      'Cómo se escribe la lista de ingredientes de la ficha técnica y de la etiqueta. La misma regla vale para ambas: la etiqueta imprime el texto que guardó la versión de la ficha.',
    ingredientsRule: 'Regla de composición',
    ingredientsRuleHint:
      'Se aplica a las fichas que se generen a partir de ahora; las versiones ya publicadas conservan su texto.',
    ingredientsRule_raw_material: 'Solo la materia prima',
    ingredientsRule_ingredients: 'Solo los ingredientes',
    ingredientsRule_both: 'Materia prima e ingredientes',
    ingredientsRuleExample: 'Ejemplo',
    thresholds: 'Umbrales',
    quietHours: 'Horas de silencio',
    users: 'Usuarios',
    devices: 'Dispositivos',
    sectorVocabulary: 'Vocabulario por sector',
    loadError: 'No se han podido cargar los datos de configuración',
    saved: 'Cambios guardados',
    saveError: 'No se han podido guardar los cambios',
    noPermission: 'Solo los administradores pueden acceder a la configuración',
    add: 'Añadir',
    edit: 'Editar',
    deactivate: 'Dar de baja',
    reactivate: 'Reactivar',
    showInactive: 'Mostrar los dados de baja',
    selectAllRows: 'Selecciona todas las filas visibles',
    selectRow: 'Selecciona {{name}}',
    bulkSelected_one: '{{count}} seleccionado',
    bulkSelected_other: '{{count}} seleccionados',
    clearSelection: 'Vacía la selección',
    bulkDeactivateConfirm_one: 'Dar de baja ({{count}})',
    bulkDeactivateConfirm_other: 'Dar de baja ({{count}})',
    bulkDeactivateClientsTitle_one: 'Dar de baja {{count}} cliente',
    bulkDeactivateClientsTitle_other: 'Dar de baja {{count}} clientes',
    bulkDeactivateClientsBody_one: 'Se dará de baja el cliente seleccionado. Es una baja lógica: se conserva el histórico, la trazabilidad y los documentos vinculados, y se puede reactivar. El motivo queda registrado.',
    bulkDeactivateClientsBody_other: 'Se darán de baja los {{count}} clientes seleccionados. Es una baja lógica: se conserva el histórico, la trazabilidad y los documentos vinculados, y se pueden reactivar. El motivo queda registrado en cada uno.',
    bulkDeactivateSuppliersTitle_one: 'Dar de baja {{count}} proveedor',
    bulkDeactivateSuppliersTitle_other: 'Dar de baja {{count}} proveedores',
    bulkDeactivateSuppliersBody_one: 'Se dará de baja el proveedor seleccionado. Es una baja lógica: se conserva el histórico, la trazabilidad y los documentos vinculados, y se puede reactivar. El motivo queda registrado.',
    bulkDeactivateSuppliersBody_other: 'Se darán de baja los {{count}} proveedores seleccionados. Es una baja lógica: se conserva el histórico, la trazabilidad y los documentos vinculados, y se pueden reactivar. El motivo queda registrado en cada uno.',
    bulkDeactivatePartial: 'Se han dado de baja {{done}} de {{total}}. No se han podido dar de baja: {{failed}}. La selección queda con los que han fallado.',
    bulkDeactivateCount_one: 'Dar de baja ({{count}})',
    bulkDeactivateCount_other: 'Dar de baja ({{count}})',
    bulkReactivateCount_one: 'Reactivar ({{count}})',
    bulkReactivateCount_other: 'Reactivar ({{count}})',
    bulkDeactivateRecordsTitle_one: 'Dar de baja {{count}} registro',
    bulkDeactivateRecordsTitle_other: 'Dar de baja {{count}} registros',
    bulkDeactivateRecordsBody_one: 'Se dará de baja el registro seleccionado. Es una baja lógica: se conserva el histórico, la trazabilidad y los documentos vinculados, y se puede reactivar. El motivo queda registrado.',
    bulkDeactivateRecordsBody_other: 'Se darán de baja los {{count}} registros seleccionados. Es una baja lógica: se conserva el histórico, la trazabilidad y los documentos vinculados, y se pueden reactivar. El motivo queda registrado en cada uno.',
    bulkReactivateConfirm_one: 'Reactivar ({{count}})',
    bulkReactivateConfirm_other: 'Reactivar ({{count}})',
    bulkReactivateClientsTitle_one: 'Reactivar {{count}} cliente',
    bulkReactivateClientsTitle_other: 'Reactivar {{count}} clientes',
    bulkReactivateClientsBody_one: 'Se reactivará el cliente seleccionado: volverá a salir en los desplegables y se podrá usar de nuevo.',
    bulkReactivateClientsBody_other: 'Se reactivarán los {{count}} clientes seleccionados: volverán a salir en los desplegables y se podrán usar de nuevo.',
    bulkReactivateSuppliersTitle_one: 'Reactivar {{count}} proveedor',
    bulkReactivateSuppliersTitle_other: 'Reactivar {{count}} proveedores',
    bulkReactivateSuppliersBody_one: 'Se reactivará el proveedor seleccionado: volverá a salir en los desplegables y se podrá usar de nuevo.',
    bulkReactivateSuppliersBody_other: 'Se reactivarán los {{count}} proveedores seleccionados: volverán a salir en los desplegables y se podrán usar de nuevo.',
    bulkReactivateRecordsTitle_one: 'Reactivar {{count}} registro',
    bulkReactivateRecordsTitle_other: 'Reactivar {{count}} registros',
    bulkReactivateRecordsBody_one: 'Se reactivará el registro seleccionado: volverá a salir en los desplegables y se podrá usar de nuevo.',
    bulkReactivateRecordsBody_other: 'Se reactivarán los {{count}} registros seleccionados: volverán a salir en los desplegables y se podrán usar de nuevo.',
    bulkReactivatePartial: 'Se han reactivado {{done}} de {{total}}. No se han podido reactivar: {{failed}}. La selección queda con los que han fallado.',
    searchGeneric: 'Buscar',
    sectionIdentity: 'Identificación',
    sectionContact: 'Contacto',
    sectionAddress: 'Dirección',
    sectionAddressDelivery: 'Dirección y entrega',
    sectionAddressNotes: 'Dirección y notas',
    filterTypeAll: 'Todas las tipologías',
    filterTypeNone: 'Sin tipología fijada',
    inactiveBadge: 'Baja',
    empty: 'Todavía no hay ningún registro',
    emptyFiltered: 'Ningún registro coincide con la búsqueda',
    searchByCodeOrName: 'Busca por código o nombre',
    colName: 'Nombre',
    colStatus: 'Estado',
    colActions: 'Acciones',
    // General / tenant_settings
    generalTitle: 'Parámetros generales',
    generalSubtitle: 'Numeración, umbrales y sesión de operario del obrador',
    lotNumberingFormat: 'Formato de numeración de lote',
    lotNumberingHint: 'Incluye {seq} para la secuencia, p. ej. L-{seq:05}',
    workOrderNumberingFormat: 'Formato de numeración de OT',
    workOrderNumberingHint: 'Incluye {seq} para la secuencia, p. ej. OT-{seq:05}',
    purchaseOrderNumberingFormat: 'Formato de número de pedido de compra',
    purchaseOrderNumberingHint: 'Incluye {seq} para la secuencia, p. ej. OC-{seq:05}',
    receptionNumberingFormat: 'Formato de número de albarán de compra',
    receptionNumberingHint: 'Incluye {seq} para la secuencia, p. ej. AR-{seq:05}',
    salesOrderNumberingFormat: 'Formato de numeración de pedidos de venta',
    salesOrderNumberingHint: 'Incluye {seq} para la secuencia, p. ej. CV-{seq:05}',
    shipmentNumberingFormat: 'Formato de numeración de albaranes de venta',
    shipmentNumberingHint: 'Incluye {seq} para la secuencia, p. ej. AV-{seq:05}',
    purchaseReturnNumberingFormat: 'Formato de número de albarán de devolución',
    purchaseReturnNumberingHint: 'Incluye {seq} para la secuencia, p. ej. DV-{seq:05}',
    supplierCodeNumberingFormat: 'Formato de código de proveedor',
    supplierCodeNumberingHint:
      'Incluye {seq} para la secuencia, p. ej. PRV-{seq:04}. Solo se aplica cuando el alta no trae código.',
    clientCodeNumberingFormat: 'Formato de código de cliente',
    clientCodeNumberingHint:
      'Incluye {seq} para la secuencia, p. ej. CLI-{seq:04}. Solo se aplica cuando el alta no trae código.',
    zoneCodeNumberingFormat: 'Formato de código de zona',
    zoneCodeNumberingHint:
      'Incluye {seq} para la secuencia, p. ej. ZON-{seq:03}. Solo se aplica cuando el alta no trae código.',
    numberingPlaceholderRequired: 'Debe incluir el marcador {seq}',
    defaultMermaThresholdPct: 'Umbral de merma por defecto (%)',
    defaultMermaThresholdHint: 'Aviso informativo cuando la merma supera este porcentaje. Nunca bloquea.',
    weighingDeviationThresholdPct: 'Umbral de desviación de pesaje (%)',
    weighingDeviationThresholdHint: 'Aviso visual cuando un pesaje se desvía más de este porcentaje.',
    expiryAlertLeadDays: 'Días de aviso previo de caducidad',
    expiryAlertLeadDaysHint: 'Días antes de la caducidad en que se emite un aviso.',
    operatorSessionHours: 'Duración de la sesión de operario (horas)',
    operatorSessionHoursHint:
      'Límite del turno en planta: pasado este tiempo, planta vuelve a pedir el PIN.',
    operatorIdleMinutes: 'Bloqueo por inactividad (minutos)',
    operatorIdleMinutesHint:
      'Con la app con PIN, planta se bloquea y pide el PIN si nadie la toca durante este tiempo. 0 lo desactiva.',
    idleMinutesRange: 'Debe ser un entero entre 0 y 240',
    quietHoursStart: 'Inicio de las horas de silencio',
    quietHoursEnd: 'Fin de las horas de silencio',
    quietHoursHint: 'Franja sin notificaciones push (HH:MM). Déjalo vacío para desactivarla.',
    pctRange: 'Debe estar entre 0 y 100',
    timeFormat: 'Formato de hora no válido (HH:MM)',
    // Shifts
    shiftsTitle: 'Turnos',
    shiftsSubtitle: 'Turnos de trabajo del obrador',
    newShift: 'Nuevo turno',
    editShift: 'Editar el turno',
    shiftName: 'Nombre del turno',
    shiftStart: 'Hora de inicio',
    shiftEnd: 'Hora de fin',
    shiftSortOrder: 'Orden',
    colStart: 'Inicio',
    colEnd: 'Fin',
    colOrder: 'Orden',
    deleteShiftTitle: 'Dar de baja el turno',
    deleteShiftBody: 'El turno dejará de estar disponible para la planificación. La baja es lógica y reversible.',
    // Estructura del obrador (zonas · secciones · ubicaciones)
    structureTitle: 'Estructura del obrador',
    structureSubtitle: 'Dónde se trabaja y dónde están las existencias. Con zonas basta; secciones y ubicaciones, solo si hacen falta.',
    structureEmpty: 'Todavía no hay ninguna zona. Crea una y el obrador ya podrá decir dónde están las existencias.',
    newZone: 'Nueva zona',
    editZone: 'Editar la zona',
    zoneCode: 'Código',
    zoneCodeHint: 'Déjalo vacío y Obrava lo genera (p. ej. ZON-001). Es el código con el que el importador de artículos enlaza la zona.',
    zoneName: 'Nombre de la zona',
    deleteZoneTitle: 'Dar de baja la zona',
    deleteZoneBody: 'La zona dejará de estar disponible para nuevas existencias. El historial se conserva y la baja es reversible.',
    zoneHasChildren: 'Para darla de baja, primero da de baja sus secciones y ubicaciones.',
    areaFunction: 'Tipo funcional',
    areaStockMode: 'Comportamiento logístico',
    storageCondition: 'Conservación',
    storageConditionNone: 'Sin especificar',
    allowsLocations: 'Permite ubicaciones',
    allowsLocationsHint: 'Actívalo para poder indicar el punto exacto (estante, palé, cámara) donde está cada existencia.',
    allowsLocationsLocked: 'Tiene ubicaciones activas: dalas de baja antes de desactivarlo.',
    addSection: 'Añadir sección',
    addLocation: 'Añadir ubicaciones',
    newSection: 'Nueva sección',
    editSection: 'Editar la sección',
    sectionZone: 'Zona',
    sectionZonePlaceholder: 'Elige la zona',
    sectionCode: 'Código',
    sectionCodeHint: 'Opcional.',
    sectionName: 'Nombre de la sección',
    sectionDescription: 'Descripción',
    colDescription: 'Descripción',
    deleteSectionTitle: 'Dar de baja la sección',
    deleteSectionBody: 'La sección dejará de estar disponible. El historial se conserva y la baja es reversible.',
    sectionHasLocations: 'Para darla de baja, primero da de baja sus ubicaciones.',
    unassignedSectionsTitle: 'Secciones sin zona',
    unassignedSectionsBody: 'Estas secciones son anteriores a la estructura. Edítalas para ponerlas dentro de una zona.',
    newLocations: 'Nuevas ubicaciones',
    editLocation: 'Editar la ubicación',
    locationParent: 'Dentro de',
    locationCodes: 'Códigos',
    locationCodesHint: 'Una ubicación por línea. El código puede indicar el pasillo, la estantería y el nivel (p. ej. C-02-03).',
    locationCode: 'Código',
    locationDescription: 'Descripción',
    locationDescriptionHint: 'Opcional. Por ejemplo, «Estante de las harinas».',
    locationDuplicate: 'Alguno de estos códigos ya lo usa otra ubicación activa.',
    locationCodesRepeated: 'Hay códigos repetidos en la lista.',
    deleteLocationTitle: 'Dar de baja la ubicación',
    deleteLocationBody: 'La ubicación dejará de estar disponible para nuevas existencias. El historial se conserva y la baja es reversible.',
    locationsCount_one: '{{count}} ubicación',
    locationsCount_other: '{{count}} ubicaciones',
    noLocations: 'Todavía no hay ninguna ubicación.',
    // Reordenar la estructura (OBR-89)
    reorder: 'Reordenar',
    reorderDone: 'Hecho',
    reorderHint: 'Arrastra por los puntos o usa las flechas. El orden se guarda solo y lo siguen los selectores de lugar.',
    moveUp: 'Subir',
    moveDown: 'Bajar',
    dragToReorder: 'Arrastra para cambiar el orden',
    reorderError: 'No se ha podido guardar el orden. Se ha vuelto al orden guardado.',
    colType: 'Tipo',
    // Categories (article_categories)
    categoriesTitle: 'Categorías de artículo',
    categoriesSubtitle: 'Categorías del catálogo del obrador, editables y ampliables',
    newCategory: 'Nueva categoría',
    editCategory: 'Editar la categoría',
    categoryName: 'Nombre de la categoría',
    categoryParent: 'Categoría padre',
    categoryNoParent: 'Ninguna (categoría principal)',
    categoryParentHint: 'Opcional. Déjalo en «Ninguna» para una categoría principal.',
    categoryParentLocked: 'Esta categoría ya tiene subcategorías y no puede ser una de ellas.',
    colParent: 'Categoría padre',
    deleteCategoryTitle: 'Dar de baja la categoría',
    deleteCategoryBody:
      'La categoría dejará de estar disponible para nuevos artículos. Los artículos ya asignados la conservan. La baja es lógica y reversible.',
    // Units (unidades de medida)
    unitsTitle: 'Unidades de medida',
    unitsSubtitle: 'Unidades del obrador; se usan en artículos, recetas, existencias y etiquetas',
    newUnit: 'Nueva unidad',
    editUnit: 'Editar la unidad',
    unitCode: 'Código',
    unitCodeHint: 'Símbolo corto que se muestra junto a las cantidades (p. ej. kg, L, Un)',
    unitNameLabel: 'Nombre',
    unitDimension: 'Magnitud',
    unitIsBase: 'Unidad base de la magnitud',
    unitIsBaseHint: 'Márcala si es la unidad de referencia de esta magnitud.',
    unitCodeDuplicate: 'Ya existe una unidad con este código',
    colCode: 'Código',
    colDimension: 'Magnitud',
    colBase: 'Base',
    deleteUnitTitle: 'Dar de baja la unidad',
    deleteUnitBody:
      'La unidad dejará de estar disponible para nuevos artículos y recetas. Los que ya la usan la conservan. La baja es lógica y reversible.',
    // Taxes (impuestos aplicables)
    taxesTitle: 'Impuestos',
    taxesSubtitle:
      'Impuestos del obrador; la ficha de artículo elige uno en lugar de escribir el tipo',
    newTax: 'Nuevo impuesto',
    editTax: 'Editar el impuesto',
    taxName: 'Nombre',
    taxNameHint: 'Como se le llama en el obrador (p. ej. IVA 21 %, IVA reducido)',
    taxRate: 'Tipo (%)',
    taxIsDefault: 'Impuesto por defecto',
    taxIsDefaultHint: 'Es el que se ofrece en una ficha nueva. Solo uno por obrador.',
    taxNameDuplicate: 'Ya existe un impuesto con este nombre',
    colRate: 'Tipo',
    deleteTaxTitle: 'Dar de baja el impuesto',
    deleteTaxBody:
      'El impuesto dejará de estar disponible para nuevas fichas. Los artículos que ya lo usan lo conservan. La baja es lógica y reversible.',
    // Packaging / logistic units (packaging_units)
    packagingUnitsTitle: 'Unidades de embalaje',
    packagingUnitsSubtitle:
      'Unidades de embalaje y logísticas del obrador (saco, caja, palé…); se usan en envasado y expediciones',
    newPackagingUnit: 'Nueva unidad de embalaje',
    editPackagingUnit: 'Editar la unidad de embalaje',
    packagingUnitNameHasDigits:
      '«{{name}}» lleva la medida en el nombre. En Obrava la unidad es genérica —«Saco»— y la medida va en la conversión de cada artículo: el mismo saco hace 25 kg de harina y 10 de sal. Puedes guardarla igualmente si es lo que quieres.',
    packagingUnitName: 'Nombre',
    packagingUnitNameDuplicate: 'Ya existe una unidad de embalaje con este nombre',
    deletePackagingUnitTitle: 'Dar de baja la unidad de embalaje',
    deletePackagingUnitBody:
      'La unidad de embalaje dejará de estar disponible. La baja es lógica y reversible.',
    // Clients (clientes)
    clientsTitle: 'Clientes',
    clientsSubtitle: 'Clientes del obrador; los compradores de los productos acabados',
    newClient: 'Nuevo cliente',
    importClients: 'Importar',
    clientCode: 'Código',
    clientCodeHint: 'Déjalo vacío y Obrava lo genera (p. ej. CLI-0001).',
    clientCommercialName: 'Nombre comercial',
    clientCommercialNameHint: 'Opcional. Solo si difiere de la razón social.',
    editClient: 'Editar el cliente',
    clientName: 'Nombre',
    clientTaxId: 'NIF / CIF',
    clientTaxIdHint: 'Opcional. Identificación fiscal del cliente.',
    clientContactName: 'Persona de contacto',
    clientPhone: 'Teléfono',
    clientEmail: 'Correo electrónico',
    clientAddress: 'Dirección fiscal',
    clientAddressHint: 'Opcional. Dirección fiscal o de facturación del cliente.',
    clientNotes: 'Notas',
    clientNotesHint: 'Opcional. Observaciones, condiciones de entrega…',
    sectionInstructions: 'Instrucciones',
    clientShippingTitle: 'Puntos de envío',
    clientShippingHint:
      'Puntos de entrega del cliente (tiendas, centros, locales…). La marcada por defecto se propone en los pedidos de venta.',
    clientShippingName: 'Nombre del punto de entrega',
    clientShippingAddress: 'Dirección de entrega',
    clientShippingDefault: 'Por defecto',
    clientShippingAdd: 'Añade un punto de envío',
    clientShippingEdit: 'Editar',
    clientShippingEmpty: 'Este cliente no tiene ningún punto de envío.',
    clientShippingNew: 'Nuevo punto de envío',
    clientShippingEditTitle: 'Punto de envío',
    clientShippingDefaultBadge: 'Predeterminado',
    clientShippingNeedsAddress: 'El punto de envío necesita nombre y dirección.',
    clientShippingIncomplete: 'Cada punto de envío necesita nombre y dirección.',
    clientEmailInvalid: 'Correo electrónico no válido',
    deleteClientTitle: 'Dar de baja el cliente',
    deleteClientBody:
      'El cliente dejará de estar disponible. La baja es lógica y reversible.',
    // Suppliers (proveedores)
    suppliersTitle: 'Proveedores',
    suppliersSubtitle: 'Proveedores del obrador; alimentan el proveedor por defecto de los artículos del catálogo',
    newSupplier: 'Nuevo proveedor',
    editSupplier: 'Editar el proveedor',
    importSuppliers: 'Importar',
    supplierCode: 'Código',
    supplierCodeHint: 'Déjalo vacío y Obrava lo genera (p. ej. PRV-0001).',
    supplierName: 'Nombre o razón social',
    supplierCommercialName: 'Nombre comercial',
    supplierCommercialNameHint: 'Opcional. Solo si difiere de la razón social.',
    supplierTaxId: 'NIF / CIF',
    supplierTaxIdHint: 'Opcional. Identificación fiscal del proveedor.',
    supplierContactName: 'Persona de contacto',
    supplierPhone: 'Teléfono',
    supplierEmail: 'Correo general',
    supplierAddress: 'Dirección principal',
    supplierAddressHint: 'Opcional. Dirección fiscal del proveedor.',
    supplierNotes: 'Notas',
    colDeliveryDays: 'Días de entrega',
    supplierDeliveryDays: 'Días habituales de entrega',
    supplierDeliveryDaysHint:
      'Los días que este proveedor reparte. Al crear un pedido, la fecha de entrega prevista se propondrá sola con el primer día que toque; siempre podrás cambiarla.',
    supplierEmailInvalid: 'Correo electrónico no válido',
    supplierQualityEmail: 'Correo de calidad',
    supplierQualityEmailHint: 'Opcional. Adonde van las solicitudes y renovaciones de fichas técnicas. Independiente del correo general y del de pedidos.',
    supplierQualityEmailInvalid: 'El correo de calidad no es válido.',
    colTaxId: 'NIF / CIF',
    colContact: 'Contacto',
    deleteSupplierTitle: 'Dar de baja el proveedor',
    deleteSupplierBody:
      'El proveedor dejará de estar disponible para nuevos artículos y nuevas compras. Los vínculos con artículos (referencia, precio, unidad de compra), los pedidos y los albaranes lo conservan, marcados como inactivos. Si era el proveedor principal de un artículo, el artículo se quedará sin proveedor principal hasta que elijas otro: no se asigna ninguno automáticamente. La baja es lógica y reversible.',
    // Users & roles
    usersTitle: 'Usuarios',
    usersSubtitle: 'Miembros del obrador y asignación de roles',
    colEmail: 'Correo',
    colAccess: 'Acceso',
    appPlanta: 'App planta',
    appBackoffice: 'Backoffice',
    noAccess: 'Ninguno',
    appsLegend:
      'Cada columna muestra con qué roles entra en esa app; un guion significa que no entra. Los roles se suman: un administrador que también trabaje en planta lleva, además, el rol de Operario.',
    colSection: 'Sección',
    memberSection: 'Sección de trabajo',
    memberSectionHint:
      'Determina qué OT llegan a su dispositivo: recibirá las asignadas a él y las asignadas a esta sección.',
    noSection: 'Sin sección',
    assignRole: 'Asignar rol',
    revokeRole: 'Revocar rol',
    selectRole: 'Selecciona un rol',
    noRoles: 'Sin roles asignados',
    rolesUpdated: 'Roles actualizados',
    rolesError: 'No se han podido actualizar los roles',
    inactiveUser: 'Inactivo',
    // Add member
    addUserTitle: 'Añade un usuario',
    addUserName: 'Nombre completo',
    addUserEmail: 'Correo',
    addUserRole: 'Rol inicial',
    addUserPassword: 'Contraseña (opcional)',
    addUserPasswordPlaceholder: 'Mínimo 6 caracteres',
    addUserPasswordHint:
      'Si pones una contraseña, el usuario entrará directamente (comunícasela tú). Si la dejas en blanco, recibirá un correo para establecerla.',
    addUserCta: 'Añade el usuario',
    addUserSubmitting: 'Añadiendo…',
    addUserNameRequired: 'Hay que indicar el nombre',
    addUserEmailInvalid: 'Correo no válido',
    addUserPasswordWeak: 'La contraseña debe tener al menos 6 caracteres',
    addUserCreated: 'Usuario añadido. Se ha enviado un correo a {{email}} para establecer la contraseña.',
    addUserCreatedWithPassword: 'Usuario añadido ({{email}}) con la contraseña indicada. Ya puede iniciar sesión.',
    addUserAlreadyMember: 'Este correo ya es miembro del obrador.',
    addUserMenuAccessDenied:
      'Tienes oculta la gestión de usuarios en este obrador: no puedes añadir a nadie.',
    addUserError: 'No se ha podido añadir el usuario.',
    sortBy: 'Ordena por',
    sortByRole: 'Rol',
    sortByName: 'Nombre',
    searchNoResults: 'Ningún usuario coincide con la búsqueda.',
    editRolesTitle: 'Roles y permisos',
    deactivateUserTitle: 'Da de baja al usuario',
    deactivateUserBody:
      'Se bloquea el acceso de este usuario. Se conserva el historial y es reversible. Indica el motivo de la baja.',
    memberLastAdminError:
      'No se puede hacer: el obrador se quedaría sin ningún administrador que pueda gestionar los usuarios.',
    memberChangeError: 'No se ha podido hacer el cambio.',
    // Menu access
    colMenuAccess: 'Accesos',
    menuAccessNoneHidden: 'Todo visible',
    menuAccessHidden_one: '{{count}} oculta',
    menuAccessHidden_other: '{{count}} ocultas',
    menuAccessUnknown: 'No se ha podido leer',
    menuAccessSelfHint: 'No puedes cambiar tus propios accesos.',
    menuAccessDialogTitle: 'Accesos de {{name}}',
    menuAccessIntro:
      'Elige qué ve esta persona en el menú del Backoffice. Una opción oculta desaparece del menú y no se puede abrir.',
    menuAccessScopeNote:
      'Ocultar una opción es una restricción de uso, no de seguridad: solo la gestión de usuarios y los roles de las personas se hacen cumplir también en el servidor.',
    menuAccessIntroPlanta:
      'Elige a qué opciones de la App de planta tiene acceso este operario: Órdenes, Recepción, Ventas y Avisos. Una opción oculta desaparece de la barra de planta.',
    menuAccessScopeNotePlanta:
      'Ocultar una opción es una restricción de uso, no de seguridad. Las opciones se guardan ya y la App de planta las aplicará a partir de la próxima versión.',
    menuAccessNotForRole: 'No disponible para el rol',
    menuAccessProtectedHint: 'El obrador debe tener siempre un administrador que la vea.',
    menuAccessError: 'No se han podido guardar los accesos.',
    menuAccessErrorLastAdmin:
      'No se puede ocultar: el obrador se quedaría sin ningún administrador que pueda gestionar los usuarios.',
    menuAccessErrorSelfEdit:
      'No puedes cambiar tus propios accesos. Pídeselo a otro administrador.',
    menuAccessErrorDenied:
      'Tienes oculta la gestión de usuarios en este obrador: no puedes cambiar los accesos de nadie.',
    menuAccessErrorForbidden: 'Solo un administrador puede cambiar los accesos.',
    menuAccessErrorNotFound: 'Esta persona ya no está en el obrador.',
    menuAccessErrorInvalidKeys:
      'Alguna de las opciones no se puede ocultar desde aquí. Recarga la página e inténtalo de nuevo.',
    // Operaris amb PIN (OBR-25, F5)
    operatorsTitle: 'Operarios',
    operatorsSubtitle: 'Personas que entran en planta con un PIN',
    newOperator: 'Nuevo operario',
    searchTeam: 'Busca por nombre, correo o tableta',
    colPin: 'PIN',
    colRoles: 'Roles',
    colLastUse: 'Último uso',
    pinStatusActive: 'Activo',
    pinStatusPending: 'Pendiente',
    pinWithdrawn: 'PIN retirado',
    pinNeverUsed: 'Nunca utilizado',
    pinPendingHint: 'Sin PIN vigente',
    pinNotShown: 'Regenéralo para verlo',
    operatorsPendingBanner_one:
      'Hay {{count}} persona sin PIN vigente. Regénerale el PIN o dala de baja.',
    operatorsPendingBanner_other:
      'Hay {{count}} personas sin PIN vigente. Regénerales el PIN o dalas de baja.',
    operatorsEmpty: 'Aún no hay ningún operario. Crea uno y entrégale el PIN en mano.',
    operatorDeletedReason: 'Motivo: {{reason}}',
    operatorRegenerate: 'Regenera el PIN',
    newOperatorTitle: 'Nuevo operario',
    newOperatorName: 'Nombre (opcional)',
    newOperatorNameHint: 'Si lo dejas vacío, se llamará «Operario» con el siguiente número.',
    newOperatorRoles: 'Roles',
    newOperatorRolesHint: 'Solo los roles de planta. Los del Backoffice se dan en Usuarios.',
    newOperatorRoleRequired: 'Elige al menos un rol.',
    newOperatorLinked: 'Esta persona ya tiene usuario con correo',
    newOperatorLinkedNone: 'No',
    newOperatorLinkedHint:
      'Solo sirve para recordar que es la misma persona; el historial que ya tiene se queda con su usuario.',
    newOperatorCta: 'Crea y muestra el PIN',
    pinRevealTitle: 'PIN de {{name}}',
    pinRevealBody:
      'Entrégale el PIN en mano. También lo tienes siempre en la lista de Operarios.',
    pinRevealWhere: 'Entra en planta desde cualquier tableta escribiendo este PIN.',
    pinReveal: 'Mostrar',
    pinHide: 'Ocultar',
    pinCopy: 'Copiar',
    pinCopied: 'Copiado',
    pinAutoMask: 'Se oculta solo en un minuto.',
    pinRevealDone: 'Hecho',
    regeneratePinTitle: 'Regenera el PIN de {{name}}',
    regeneratePinBody:
      'El PIN actual dejará de funcionar al instante. Se creará uno nuevo, que verás en la lista.',
    regeneratePinCta: 'Regenerar',
    revokeOperatorTitle: 'Dar de baja a {{name}}',
    revokeOperatorBody:
      'Dejará de entrar en planta al instante y su PIN se retira. La baja es lógica: el historial se conserva y se puede reactivar.',
    reactivateOperatorTitle: 'Reactiva a {{name}}',
    reactivateOperatorBody:
      'Volverá a la lista con un PIN nuevo (el antiguo no se recupera), que verás en la lista.',
    reactivateOperatorCta: 'Reactiva y muestra el PIN',
    editOperatorTitle: 'Edita a {{name}}',
    editOperatorNameSave: 'Guardar el nombre',
    // Errors de la gestió d’operaris i d’aparells
    pinErrorTryLater: 'No se ha podido completar ahora mismo. Vuelve a intentarlo.',
    pinErrorUnauthorized: 'La sesión ha caducado. Vuelve a entrar.',
    pinErrorForbidden: 'Solo un administrador puede gestionar los operarios.',
    pinErrorMenuAccessDenied:
      'Tienes oculta la opción de Operarios (o la de Configuración): no puedes hacer esto.',
    pinErrorUsePinManage: 'Los operarios con PIN se gestionan desde la pestaña Operarios.',
    pinErrorNotFound:
      'No se ha encontrado: puede que ya no exista. Refresca la pantalla y vuelve a intentarlo.',
    pinErrorReasonRequired: 'Hay que escribir un motivo.',
    pinErrorRoleNotAllowed: 'Este rol no se puede dar a un operario con PIN.',
    pinErrorAlreadyIssued: 'Esta petición ya se había hecho. Si no viste el PIN, regéneralo.',
    pinErrorCreatedNoPin:
      'La persona ya se ha creado, pero el PIN no se ha entregado. Cierra este diálogo y regénerale el PIN desde la lista.',
    pinErrorAlreadyPin: 'Esta persona ya tiene un PIN.',
    pinErrorDuplicateName:
      'Ya hay un operario con este nombre: cambia el nombre de uno de los dos y vuelve a intentarlo.',
    pinErrorPersonDeleted: 'Esta persona está dada de baja. Reactívala primero.',
    pinErrorPersonInactive: 'Esta persona está desactivada.',
    pinErrorNotDeleted: 'Esta persona no está dada de baja.',
    pinErrorNoLivePin: 'Esta persona no tiene ningún PIN vigente.',
    pinErrorConvertPlantaOnly: 'Solo se puede pasar a PIN a quien trabaja solo en planta.',
    pinErrorServer: 'Hay un problema en el servidor. Avisa al equipo de Obrava.',
    pinErrorGeneric: 'No se ha podido hacer. Vuelve a intentarlo.',
    // Usuaris: «Passa a PIN» i «Crea també un PIN» (OBR-25, F5)
    convertToPinAction: 'Pasar a PIN',
    createAlsoPinAction: 'Crear también un PIN',
    convertToPinTitle: 'Pasar a {{name}} a PIN',
    convertToPinBody:
      'Dejará de entrar con correo y contraseña y entrará en planta con un PIN. El historial se conserva y su cuenta con correo no se borra.',
    convertToPinCta: 'Pasar a PIN y mostrarlo',
    accessCodeTitle: 'Código de acceso',
    accessCodeHint: 'Recibirá un correo con un enlace para elegir uno nuevo. Hasta que lo haga, el código actual sigue siendo válido.',
    accessCodeAction: 'Envía un enlace para cambiar el código de acceso',
    accessCodeSending: 'Enviando…',
    accessCodeSent: 'Se ha enviado a {{email}} un enlace para cambiar el código de acceso.',
    accessCodeError: 'No se ha podido enviar el enlace.',
    accessCodeMenuAccessDenied: 'Tienes oculta la gestión de usuarios en este obrador: no puedes enviar códigos.',
    accessCodeUserInactive: 'Esta persona ya no está activa en el obrador.',
    accessCodeNoEmail: 'Esta persona no tiene correo: entra con PIN.',
    accessCodeAuditError: 'Se ha enviado el enlace, pero no se ha podido registrar en la actividad.',
    pinOfferTitle: 'PIN para planta',
    pinOfferConvertHint:
      'Trabaja solo en planta: puede entrar con un PIN en lugar de correo y contraseña.',
    pinOfferAlsoHint:
      'Tiene acceso al Backoffice. Si también trabaja en planta, se le puede crear un PIN aparte.',
    createAlsoPinTitle: 'Crear también un PIN para {{name}}',
    createAlsoPinBody:
      'Se crea una persona nueva que entra en planta con PIN. Su usuario con correo no cambia, y lo que haga en planta con el PIN quedará registrado a nombre de la persona nueva.',
    // Devices
    devicesTitle: 'Dispositivos',
    devicesSubtitle: 'Hardware registrado del obrador',
    newDevice: 'Nuevo dispositivo',
    editDevice: 'Editar el dispositivo',
    deviceName: 'Nombre del dispositivo',
    deviceType: 'Tipo',
    deviceIdentifier: 'Identificador',
    deviceIdentifierHint: 'Número de serie, MAC o dirección (opcional)',
    deviceStatus: 'Estado',
    colIdentifier: 'Identificador',
    colPrinterAddress: 'Dirección',
    printerNotConfigured: 'Sin dirección',
    devicePrinterPlantaNote:
      'De momento las etiquetas se imprimen desde la app de planta, con la tableta en la misma red que la impresora.',
    devicePrinterHost: 'Dirección de la impresora',
    devicePrinterHostHint:
      'IP o nombre en la red del obrador, por ejemplo 192.168.1.80. Sin http:// ni puerto.',
    devicePrinterHostInvalid:
      'Dirección no válida. Escribe una IP (192.168.1.80) o un nombre, sin http:// ni puerto.',
    devicePrinterPort: 'Puerto',
    devicePrinterPortHint: 'Normalmente 9100',
    devicePrinterPortInvalid: 'El puerto debe ser un número entre 1 y 65535.',
    devicePrinterLabelWidth: 'Ancho de la etiqueta (mm)',
    devicePrinterLabelHeight: 'Alto de la etiqueta (mm)',
    devicePrinterSizeHint:
      'El tamaño del rollo cargado. Si se deja vacío, se imprime en 50 × 30 mm.',
    devicePrinterSizeInvalid: 'Hacen falta el ancho y el alto, entre 10 y 200 mm.',
    deleteDeviceTitle: 'Dar de baja el dispositivo',
    deleteDeviceBody: 'El dispositivo dejará de estar disponible. La baja es lógica y reversible.',
    tabArticleFamilies: 'Familias de artículo',
    tabIngredients: 'Ingredientes',
    tabProcesses: 'Procesos',
    tabLegislation: 'Legislación y normativa',
    tabResources: 'Recursos',
    tabLabelTemplates: 'Formatos de etiqueta',
    masterName: 'Nombre',
    conservationConditionsTitle: 'Condiciones de conservación',
    conservationConditionsSubtitle: 'Condiciones de conservación de los artículos (ambiente, refrigerado, congelado…); se usan en la ficha de artículo y en la etiqueta',
    newConservationCondition: 'Nueva condición de conservación',
    editConservationCondition: 'Edita la condición de conservación',
    conservationConditionDuplicate: 'Ya existe una condición de conservación con este nombre',
    deleteConservationConditionTitle: 'Da de baja la condición de conservación',
    deleteConservationConditionBody: 'La condición dejará de estar disponible para nuevos artículos. Los artículos que ya la usan la conservan. La baja es lógica y reversible.',
    usageTypesTitle: 'Tipos de uso',
    usageTypesSubtitle: 'Tipos de uso de los artículos (consumo directo, uso en elaboración…); se usan en la ficha de artículo',
    newUsageType: 'Nuevo tipo de uso',
    editUsageType: 'Edita el tipo de uso',
    usageTypeDuplicate: 'Ya existe un tipo de uso con este nombre',
    deleteUsageTypeTitle: 'Da de baja el tipo de uso',
    deleteUsageTypeBody: 'El tipo de uso dejará de estar disponible para nuevos artículos. Los artículos que ya lo usan lo conservan. La baja es lógica y reversible.',
    preparationModesTitle: 'Modos de preparación',
    preparationModesSubtitle: 'Modos de preparación de los artículos (listo para consumir, cocer antes…); se usan en la ficha de artículo',
    newPreparationMode: 'Nuevo modo de preparación',
    editPreparationMode: 'Edita el modo de preparación',
    preparationModeDuplicate: 'Ya existe un modo de preparación con este nombre',
    deletePreparationModeTitle: 'Da de baja el modo de preparación',
    deletePreparationModeBody: 'El modo de preparación dejará de estar disponible para nuevos artículos. Los artículos que ya lo usan lo conservan. La baja es lógica y reversible.',
    containerTypesTitle: 'Tipos de envase',
    containerTypesSubtitle: 'Envases de venta (bote, botella, bandeja…); distintos de las unidades de embalaje logísticas',
    newContainerType: 'Nuevo tipo de envase',
    editContainerType: 'Edita el tipo de envase',
    containerTypeDuplicate: 'Ya existe un tipo de envase con este nombre',
    deleteContainerTypeTitle: 'Da de baja el tipo de envase',
    deleteContainerTypeBody: 'El tipo de envase dejará de estar disponible para nuevos artículos. Los artículos que ya lo usan lo conservan. La baja es lógica y reversible.',
    complianceItemsTitle: 'Legislación y cumplimiento',
    complianceItemsSubtitle: 'Ítems de legislación y cumplimiento («No contiene OMG», certificaciones…); se usan en la ficha técnica',
    newComplianceItem: 'Nuevo ítem de cumplimiento',
    editComplianceItem: 'Edita el ítem de cumplimiento',
    complianceItemDuplicate: 'Ya existe un ítem de cumplimiento con este nombre',
    deleteComplianceItemTitle: 'Da de baja el ítem de cumplimiento',
    deleteComplianceItemBody: 'El ítem dejará de estar disponible para nuevos artículos. Los artículos que ya lo usan lo conservan. La baja es lógica y reversible.',
    ingredientsTitle: 'Ingredientes',
    ingredientsSubtitle: 'Ingredientes de la composición declarada de los artículos; también se pueden crear al vuelo desde la ficha de artículo',
    newIngredient: 'Nuevo ingrediente',
    editIngredient: 'Edita el ingrediente',
    ingredientDuplicate: 'Ya existe un ingrediente con este nombre',
    deleteIngredientTitle: 'Da de baja el ingrediente',
    deleteIngredientBody: 'El ingrediente dejará de estar disponible para nuevos artículos. Los artículos que ya lo declaran lo conservan. La baja es lógica y reversible.',
    processesTitle: 'Procesos',
    processesSubtitle: 'Procesos de elaboración: la plantilla de los pasos de una receta (duración, temperatura, control, instrucciones y recursos por defecto)',
    newProcess: 'Nuevo proceso',
    editProcess: 'Edita el proceso',
    processDescription: 'Descripción',
    processDuration: 'Duración (min)',
    processTemperature: 'Temperatura (°C)',
    processControlType: 'Tipo de control',
    processInstructions: 'Instrucciones de base',
    processInstructionsHint:
      'Se copian al paso cuando se crea uno desde este proceso; después se pueden adaptar a cada receta.',
    processResources: 'Recursos por defecto',
    processResourcesHint:
      'Los tipos de recurso que este proceso propone. Se copian al paso y después se pueden cambiar en cada receta.',
    colProcessResources: 'Recursos',
    processResourceDuplicate: 'Este tipo de recurso ya está en la lista: dos unidades son una sola línea con cantidad 2',
    processDuplicateDescription: 'Ya existe un proceso con esta descripción',
    colProcessDuration: 'Duración (min)',
    colProcessTemperature: 'Temperatura (°C)',
    colProcessControl: 'Control',
    deleteProcessTitle: 'Da de baja el proceso',
    deleteProcessBody: 'El proceso dejará de estar disponible para nuevas recetas. Las recetas que ya lo usan lo conservan. La baja es lógica y reversible.',
    legislationTitle: 'Legislación y normativa',
    legislationSubtitle: 'Textos legales y normativos del obrador; se imprimen, en este orden, como texto final de todas las fichas técnicas',
    newLegislationNote: 'Nuevo texto',
    editLegislationNote: 'Editar el texto',
    legislationNoteTitle: 'Título',
    legislationNoteBody: 'Texto',
    legislationNoteSortOrder: 'Orden',
    colLegislationBody: 'Texto',
    colLegislationSortOrder: 'Orden',
    deleteLegislationNoteTitle: 'Dar de baja el texto',
    deleteLegislationNoteBody: 'El texto dejará de aparecer en las nuevas fichas técnicas. Las fichas ya aprobadas lo conservan. La baja es lógica y reversible.',
    labelTemplatesTitle: 'Formatos de etiqueta',
    labelTemplatesSubtitle: 'Formatos de etiqueta del obrador; la etiqueta se genera al cerrar la OT o al envasar, nunca desde la ficha técnica',
    newLabelTemplate: 'Nuevo formato de etiqueta',
    editLabelTemplate: 'Edita el formato de etiqueta',
    labelTemplateName: 'Nombre',
    labelTemplateType: 'Tipo de etiqueta',
    labelType_internal_lot: 'Lote interno',
    labelType_sales_legal: 'Etiqueta legal de venta',
    labelTemplateIsDefault: 'Formato por defecto de su tipo',
    labelTemplateIsDefaultHint: 'Al marcarlo, sustituye el formato por defecto anterior de este tipo.',
    labelTemplateDefaultBadge: 'Por defecto',
    colDefault: 'Por defecto',
    deleteLabelTemplateTitle: 'Da de baja el formato de etiqueta',
    deleteLabelTemplateBody: 'El formato dejará de estar disponible para nuevas etiquetas. La baja es lógica y reversible.',
    categoryArticleType: 'Tipología',
    categoryArticleTypeHint: 'Opcional. Limita la categoría a una tipología de artículo; «Todas» la hace válida para todas.',
    categoryAllTypes: 'Todas',
    colArticleType: 'Tipología',
    skuSection: 'Codificación de artículos (SKU)',
    skuModeLabel: 'Asignación de códigos',
    skuModeHint: 'Cómo se asigna el código (SKU) al crear un artículo nuevo.',
    skuMode_free: 'Manual: el código se escribe al crear el artículo',
    skuMode_sequential: 'Automático: una numeración única para todo el catálogo',
    skuMode_per_type: 'Automático por tipología: una numeración para cada tipo de artículo',
    skuFormat: 'Máscara del código',
    skuFormatHint: 'Incluye {seq} para la secuencia, p. ej. ART-{seq:05}',
    salesBarcodeEnabled: 'Códigos de barras en artículos de venta',
    salesBarcodeEnabledHint: 'Muestra el campo de código de barras (EAN) en los artículos que se venden.',
    tabBranding: 'Marca y FT',
    // Recursos (tipos de recurso + recursos)
    resourceTypesTitle: 'Tipos de recurso',
    resourceTypesSubtitle:
      'Cómo clasificas lo que consume una elaboración (Horno, Operario, Cámara de frío…); define los tuyos antes de dar de alta recursos',
    newResourceType: 'Nuevo tipo de recurso',
    editResourceType: 'Edita el tipo de recurso',
    resourceTypeDuplicate: 'Ya existe un tipo de recurso con este nombre',
    deleteResourceTypeTitle: 'Da de baja el tipo de recurso',
    deleteResourceTypeBody:
      'El tipo dejará de estar disponible para nuevos recursos y recetas. Los que ya lo usan lo conservan. La baja es lógica y reversible.',
    resourcesTitle: 'Recursos',
    resourcesSubtitle:
      'Lo que ocupa una elaboración y no es materia prima (Horno 1, Amasadora 02, un operario) y dónde está; la receta pide el tipo y la planificación elige cuál',
    newResource: 'Nuevo recurso',
    editResource: 'Edita el recurso',
    resourceType: 'Tipo de recurso',
    selectResourceType: 'Selecciona un tipo',
    resourcePlace: 'Dónde está',
    resourcePlaceHint:
      'Zona, sección o ubicación del obrador. Sirve para proponer el recurso más cercano al planificar una OT.',
    resourcePlaceNone: 'Todo el obrador',
    colResourcePlace: 'Lugar',
    resourceCostPerHour: 'Coste por hora (€)',
    resourceCostPerHourHint:
      'Opcional. Se guarda para el cálculo de costes; todavía no se calcula nada.',
    resourceNotes: 'Notas',
    resourceDuplicate: 'Ya existe un recurso con este nombre',
    colResourceType: 'Tipo',
    colResourceCost: 'Coste/hora',
    deleteResourceTitle: 'Da de baja el recurso',
    deleteResourceBody:
      'El recurso dejará de estar disponible para nuevas OT. Las OT que ya lo tienen elegido lo conservan. La baja es lógica y reversible.',
    supplierOrdersSection: 'Envío de pedidos',
    supplierOrdersSectionHint: 'Cómo recibe los pedidos: por correo desde Obrava, por WhatsApp o por otros medios.',
    supplierOrderChannel: 'Canal',
    supplierOrderChannelEmail: 'Correo electrónico (desde Obrava)',
    supplierOrderChannelWhatsapp: 'WhatsApp (al número de pedidos)',
    supplierOrderChannelManual: 'Otros medios (teléfono, en persona…)',
    supplierOrderEmails: 'Correos de pedidos',
    supplierOrderEmailsHint: 'Sepáralos con comas. Obligatorio si el canal es correo: el correo general de contacto no lo sustituye.',
    supplierOrderEmailsInvalid: 'Hay correos de pedidos que no son válidos: {{list}}',
    supplierOrderEmailsRequired: 'El canal es Correo electrónico: indica el correo de pedidos (el correo general no lo sustituye).',
    supplierOrderPhone: 'Teléfono/WhatsApp de pedidos',
    supplierOrderPhoneHint: 'Número donde el proveedor recibe los pedidos, con el prefijo del país si es posible (+34 600 000 000). Independiente del teléfono general.',
    supplierOrderPhoneInvalid: 'El teléfono/WhatsApp de pedidos no es válido: usa de 7 a 15 cifras, con espacios y el prefijo + si hace falta.',
    supplierOrderPhoneRequired: 'El canal es WhatsApp: indica el teléfono/WhatsApp de pedidos (el teléfono general no lo sustituye).',
    supplierOrderEmailsTooMany: 'Como máximo, diez correos de pedidos.',
    supplierOrderLanguage: 'Idioma',
    supplierOrderLanguageSender: 'El de quien envía',
    supplierOrderSubject: 'Asunto (opcional)',
    supplierOrderMessage: 'Mensaje (opcional)',
    supplierOrderTemplateHint: 'Si lo dejas en blanco, se pone uno por defecto. Puedes usar {{tokens}}.',
    supplierOrderWhatsappMessage: 'Mensaje de WhatsApp (opcional)',
    supplierOrderWhatsappHint: 'Independiente del mensaje del correo. Si lo dejas en blanco, se pone uno por defecto. Puedes usar {{tokens}}. El bloque de artículos va donde pongas {{linesToken}}, mejor en una línea propia (si no lo pones, va después del texto). WhatsApp no puede llevar el PDF: no menciones ningún adjunto.',
    supplierOrderWhatsappPreview: 'Previsualización (con datos de ejemplo)',
    supplierOrderWhatsappPreviewTooLong: 'Este texto es demasiado largo para el enlace de WhatsApp: acórtalo.',
    supplierOrderWhatsappSampleArticle1: 'Harina de trigo',
    supplierOrderWhatsappSampleArticle2: 'Levadura fresca',
    supplierOrderWhatsappSampleSupplier: 'Proveedor de ejemplo',
    supplierOrderAttachPdf: 'Adjunta el PDF del pedido',
    supplierOrderAutoSend: 'Envía automáticamente al confirmar',
    supplierOrderAutoSendHint: 'Al confirmar un pedido de este proveedor, se envía solo por correo.',
  },

  'backoffice.areaFunction': {
    storage: 'Almacén',
    production: 'Producción',
    reception: 'Recepción',
    shipping: 'Expedición',
    quality: 'Calidad',
    other: 'Otros',
  },

  'backoffice.areaStockMode': {
    stock: 'Stock',
    provisioning: 'Aprovisionamiento',
    none: 'Sin existencias',
  },

  'backoffice.areaStockModeHint': {
    stock: 'Aquí viven existencias de forma habitual (almacenes, cámaras).',
    provisioning: 'Zona de trabajo: llega material para consumirlo o transformarlo, pero no vive aquí.',
    none: 'No se controlan existencias (limpieza, vestuarios…).',
  },

  'backoffice.storageCondition': {
    ambient: 'Ambiente',
    refrigerated: 'Refrigerado',
    frozen: 'Congelado',
  },

  'backoffice.unitDimension': {
    mass: 'Masa',
    volume: 'Volumen',
    count: 'Recuento',
    length: 'Longitud',
  },

  'backoffice.deviceType': {
    label_printer: 'Etiquetadora',
    scale: 'Báscula',
    barcode_reader: 'Lector de códigos',
    sensor: 'Sensor',
    tablet: 'Tableta',
  },

  'backoffice.deviceStatus': {
    registered: 'Registrado',
    active: 'Activo',
    offline: 'Sin conexión',
    error: 'Error',
    decommissioned: 'Retirado',
  },

  'backoffice.roleLabel': {
    admin: 'Administrador',
    production_manager: 'Responsable de producción',
    operator: 'Operario',
    quality: 'Calidad',
    purchasing: 'Compras',
    sales: 'Ventas',
    viewer: 'Consulta',
  },

  'backoffice.traceability': {
    title: 'Trazabilidad',
    backward: 'Hacia atrás',
    forward: 'Hacia delante',
    byLot: 'Por lote',
  },

  'platformConsole.common': {
    consoleName: 'Consola de Plataforma',
    themeLight: 'Claro',
    themeDark: 'Oscuro',
    saving: 'Guardando…',
    creating: 'Creando…',
    actionError: 'No se ha podido completar la acción',
    loadError: 'No se han podido cargar los datos',
    never: 'Nunca',
    lang_ca: 'Català',
    lang_es: 'Español',
    lang_en: 'English',
    settings: 'Configuración',
    language: 'Idioma',
    appearance: 'Apariencia',
    themeSystem: 'Sistema',
    done: 'Hecho',
    theme: 'Tema',
    account: 'Cuenta y perfil',
    copyVersion: 'Copiar versión',
    versionCopied: 'Versión copiada',
    collapse: 'Minimiza',
    openMenu: 'Abre el menú',
    closeMenu: 'Cierra el menú',
    comingSoon: 'Próximamente',
  },

  'platformConsole.auth': {
    consoleTitle: 'Consola de Plataforma Obrava',
    consoleSubtitle: 'Acceso reservado al equipo Obrava',
    signInTitle: 'Inicia sesión',
    email: 'Correo electrónico',
    password: 'Contraseña',
    signInCta: 'Entrar',
    signingIn: 'Entrando…',
    signInError: 'Credenciales incorrectas',
    showPassword: 'Mostrar la contraseña',
    hidePassword: 'Ocultar la contraseña',
    notAuthorizedTitle: 'No autorizado',
    notAuthorizedBody:
      'Esta cuenta no es un superusuario de plataforma. La consola es exclusiva para el equipo Obrava.',
    backToSignIn: 'Volver a iniciar sesión',
    mfaRequiredTitle: 'Verificación en dos pasos',
    mfaRequiredSubtitle: 'Introduce el código de 6 dígitos de tu app de autenticación',
    mfaEnrollTitle: 'Configura la verificación en dos pasos',
    mfaEnrollSubtitle:
      'La verificación en dos pasos es obligatoria para acceder a la consola. Añade esta cuenta a tu app de autenticación (Google Authenticator, 1Password…).',
    mfaSecretLabel: 'Clave secreta',
    mfaUriLabel: 'URL de configuración (otpauth)',
    mfaScanHint: 'Escanea el código QR con tu app de autenticación (Google Authenticator, 1Password…), o introduce la clave manualmente.',
    mfaSecretHint:
      'Introduce manualmente la clave en tu app, o pega la URL otpauth. Después confirma con el código generado.',
    mfaCodeLabel: 'Código de 6 dígitos',
    mfaCodePlaceholder: '000000',
    mfaVerifyCta: 'Verifica',
    mfaVerifying: 'Verificando…',
    mfaEnrollCta: 'Activa y verifica',
    mfaInvalidCode: 'Código incorrecto. Inténtalo de nuevo.',
    mfaEnrollError: 'No se ha podido iniciar la configuración',
    mfaCodeFormat: 'El código debe tener 6 dígitos',
    copy: 'Copia',
    copied: 'Copiado',
  },

  'platformConsole.obradors': {
    title: 'Obradores',
    subtitle: 'Gestión de obradores (tenants) de la plataforma',
    create: 'Crea obrador',
    empty: 'Aún no hay ningún obrador',
    emptyFiltered: 'Ningún obrador coincide con los filtros',
    searchPlaceholder: 'Busca por nombre o NIF',
    showInactive: 'Mostrar inactivos',
    filterStatus: 'Estado',
    filterSector: 'Sector',
    filterPlan: 'Plan',
    colName: 'Nombre comercial',
    colTaxId: 'NIF',
    colSector: 'Sector',
    colPlan: 'Plan',
    colStatus: 'Estado',
    colCreatedAt: 'Fecha de alta',
    colLastAccess: 'Último acceso',
    suspend: 'Suspende',
    reactivate: 'Reactiva',
    suspendReason: 'Motivo de la suspensión',
    reactivateReason: 'Motivo de la reactivación',
    supportAccess: 'Acceso de soporte',
    supportAccessReason: 'Motivo del acceso',
  },

  'platformConsole.statusLabel': {
    active: 'Activo',
    suspended: 'Suspendido',
    deactivated: 'Desactivado',
  },

  'platformConsole.sectorLabel': {
    forn: 'Panadería',
    pastisseria: 'Pastelería',
    xarcuteria: 'Charcutería',
    precuinats: 'Precocinados',
    conserves: 'Conservas',
    generic: 'Genérico',
  },

  'platformConsole.planLabel': {
    obrador: 'Obrador',
    pime: 'PYME',
    cadena: 'Cadena',
  },

  'platformConsole.create': {
    title: 'Nuevo obrador',
    subtitle: 'Alta de un obrador (tenant) en la plataforma',
    sectionFiscal: 'Datos del obrador',
    sectionPlan: 'Plan e idioma',
    sectionAdmin: 'Admin inicial',
    name: 'Nombre comercial',
    legalName: 'Razón social',
    taxId: 'NIF',
    sector: 'Sector',
    plan: 'Plan',
    language: 'Idioma',
    adminEmail: 'Email del admin inicial',
    submit: 'Crea obrador',
    cancel: 'Cancela',
    deployNote:
      'Al crear el obrador se despliega la estructura base por defecto (almacenes, familias, turnos, zonas). La cuenta del admin se da de alta después, desde la ficha del obrador.',
    createError: 'No se ha podido crear el obrador',
    taxIdTaken: 'Este NIF ya pertenece a un obrador activo.',
  },

  'platformConsole.detail': {
    title: 'Ficha del obrador',
    sectionFiscal: 'Datos fiscales',
    sectionPlan: 'Plan y sector',
    sectionStatus: 'Estado',
    sectionDanger: 'Baja del obrador',
    name: 'Nombre comercial',
    legalName: 'Razón social',
    taxId: 'NIF',
    sector: 'Sector',
    plan: 'Plan',
    createdAt: 'Fecha de alta',
    lastAccess: 'Último acceso',
    save: 'Guarda los cambios',
    saved: 'Cambios guardados',
    saveError: 'No se han podido guardar los cambios',
    saveTitle: 'Guarda los cambios del obrador',
    saveBody:
      'La modificación de los datos fiscales y del plan queda registrada en la auditoría. Indica el motivo del cambio.',
    suspend: 'Suspende obrador',
    reactivate: 'Reactiva obrador',
    deactivate: 'Desactiva obrador',
    suspendTitle: 'Suspende el obrador',
    suspendBody:
      'Se bloquea inmediatamente todo el acceso (backoffice, app y API). Los datos se conservan íntegros y es reversible.',
    reactivateTitle: 'Reactiva el obrador',
    reactivateBody: 'Se restaura el acceso con todos los datos intactos.',
    deactivateTitle: 'Desactiva el obrador',
    deactivateBody:
      'Baja lógica reversible: se bloquea todo el acceso y el obrador desaparece de los listados. Los datos regulatorios (lotes, OTs, trazabilidad, APPCC) se conservan íntegros. La purga física solo se hace por proceso diferido, nunca desde aquí.',
    reasonLabel: 'Motivo',
    reasonPlaceholder: 'Indica el motivo (obligatorio)',
    reasonRequired: 'Hay que indicar un motivo',
    notFound: 'No se ha encontrado el obrador',
    deletedBanner: 'Este obrador está desactivado. Reactívalo para editarlo.',
    suspendedBanner: 'Este obrador está suspendido.',
    sectionOwner: 'Usuario principal',
    // Registro de actividad (F0.9)
    sectionActivity: 'Registro de actividad',
    activityNotice:
      'Consultar este registro queda registrado en el log del obrador y es visible para el cliente.',
    activityEmpty: 'Sin actividad registrada',
    activityError: 'No se ha podido cargar el registro de actividad',
    activityLoadMore: 'Cargar más',
    activityFilterAllActions: 'Todas las acciones',
    activityFilterAllEntities: 'Todos los elementos',
    ownerName: 'Nombre',
    ownerEmail: 'Correo',
    ownerStatus: 'Estado de la cuenta',
    ownerStatusActive: 'Activo',
    ownerStatusBlocked: 'Bloqueado',
    ownerStatusInvited: 'Invitado (pendiente de alta)',
    ownerStatusNone: 'Sin propietario asignado',
    ownerNone: 'Este obrador todavía no tiene propietario ni correo de administrador.',
    ownerLoadError: 'No se ha podido cargar el usuario principal.',
    ownerResetCta: 'Envía un código de acceso nuevo',
    ownerResetHint:
      'El propietario recibirá un código en su correo para establecer una contraseña nueva. La consola no muestra ninguna contraseña.',
    ownerResetSent: 'Se ha enviado un código de acceso a {{email}}.',
    ownerResetError: 'No se ha podido enviar el código de acceso.',
    ownerCreateHint:
      'Da de alta al propietario directamente. Se creará su cuenta y recibirá un correo para establecer su contraseña; no hace falta esperar a que se registre.',
    ownerCreateName: 'Nombre del propietario',
    ownerCreateEmail: 'Correo del propietario',
    ownerCreateNameRequired: 'Hay que indicar el nombre',
    ownerCreateEmailInvalid: 'Correo no válido',
    ownerCreatePassword: 'Contraseña (opcional)',
    ownerCreatePasswordPlaceholder: 'Mínimo 6 caracteres',
    ownerCreatePasswordHint:
      'Si pones una contraseña, el propietario entrará directamente (comunícasela tú). Si la dejas en blanco, recibirá un correo para establecerla él mismo.',
    ownerCreatePasswordWeak: 'La contraseña debe tener al menos 6 caracteres',
    ownerCreateCta: 'Da de alta al propietario',
    ownerCreated: 'Cuenta creada. Se ha enviado un correo a {{email}} para establecer la contraseña.',
    ownerCreatedWithPassword:
      'Cuenta creada para {{email}} con la contraseña indicada. Ya puede iniciar sesión.',
    ownerCreatedNoEmail:
      'Cuenta creada para {{email}}, pero no se ha podido enviar el correo. Reinténtalo con «Envía un código de acceso nuevo».',
    ownerCreateError: 'No se ha podido crear la cuenta del propietario.',
    ownerEmailInUse: 'Este correo ya tiene una cuenta. Usa «Envía un código de acceso nuevo».',
    ownerExistsLinkHint:
      'Este correo ({{email}}) ya tiene una cuenta en InsForge. Se vinculará a este obrador y podrá acceder con su contraseña actual, sin ningún correo.',
    ownerLinkCta: 'Vincula al obrador',
    ownerLinked:
      'Cuenta vinculada al obrador. El propietario ya puede acceder con su contraseña actual.',
  },

  'sectors.forn': {
    finishedProduct: 'Producto de panadería',
    'unit.loaves': 'barras',
  },
  'sectors.pastisseria': {
    finishedProduct: 'Producto de pastelería',
  },
  'sectors.xarcuteria': {
    finishedProduct: 'Producto de charcutería',
    'unit.loaves': 'piezas',
  },
  'backoffice.brandingTab': {
    logoSection: 'Logotipo',
    logoHint: 'Se muestra en la cabecera de la ficha técnica. Imagen (PNG o JPG), máximo 2 MB.',
    logoUploading: 'Subiendo…',
    logoRemove: 'Quitar el logotipo',
    logoInvalidType: 'El archivo debe ser una imagen.',
    logoTooLarge: 'La imagen supera los 2 MB.',
    logoUploadError: 'No se ha podido subir el logotipo.',
    companySection: 'Datos de la empresa (cabecera de la FT)',
    companyHint: 'Estos datos aparecen en la cabecera de la ficha técnica. Rellena solo los que tengas.',
    fieldInvalid: 'Valor no válido',
    field_legal_name: 'Razón social',
    field_tax_id: 'NIF / CIF',
    field_address: 'Dirección',
    field_city: 'Población',
    field_postal_code: 'Código postal',
    field_province: 'Provincia',
    field_phone: 'Teléfono',
    field_email: 'Correo electrónico',
    field_website: 'Sitio web',
    field_sanitary_registry: 'Registro sanitario (RGSEAA)',
    designSection: 'Diseño de la ficha técnica',
    designHint: 'Elige el estilo con el que se genera el PDF de la ficha técnica.',
    design_classic: 'Clásico',
    designDesc_classic: 'Como la muestra: cabecera con logotipo y secciones enmarcadas.',
    design_modern: 'Moderno',
    designDesc_modern: 'Limpio, con tipografía de palo seco y color de acento.',
    design_compact: 'Compacto',
    designDesc_compact: 'Denso, toda la información en una sola página.',
  },
  'backoffice.labelPrint': {
    proofCta: 'Prueba de etiqueta',
    cta: 'Genera etiqueta',
    generating: 'Generando…',
    error: 'No se ha podido generar la etiqueta.',
    noTemplate: 'No hay ningún formato de etiqueta configurado. Define uno en Configuración · Formatos de etiqueta.',
    noSheet: 'Este artículo todavía no tiene ninguna ficha técnica aprobada: la etiqueta saldrá sin ingredientes, alérgenos ni información nutricional.',
    sampleLot: 'LOTE-DE-PRUEBA',
    sampleDate: '00/00/0000',
    ingredientsPrefix: 'Ingredientes',
    copies: 'Copias',
    issued: 'Etiqueta generada y registrada.',
  },

  'backoffice.labelEditor': {
    field_legal_denomination: 'Denominación legal',
    field_product_name: 'Nombre comercial',
    field_ingredients: 'Ingredientes',
    field_allergens: 'Alérgenos',
    field_nutrition: 'Información nutricional',
    field_net_weight: 'Cantidad neta',
    field_conservation: 'Conservación',
    field_country_of_origin: 'País de origen',
    field_business_name: 'Empresa',
    field_business_address: 'Dirección de la empresa',
    field_sanitary_registry: 'Registro sanitario',
    field_lot_number: 'Número de lote',
    field_production_date: 'Fecha de producción',
    field_expiry_date: 'Fecha de caducidad',
    field_best_before: 'Consumo preferente',
    field_quantity: 'Cantidad',
    field_sku: 'Código (SKU)',
    field_custom_text: 'Texto libre',
    field_qr: 'Código QR',
    field_barcode: 'Código de barras',
    field_logo: 'Logotipo',
    field_image: 'Imagen del producto',
    field_spacer: 'Espacio',
    align_left: 'Izquierda',
    align_center: 'Centro',
    align_right: 'Derecha',
    editTitle: 'Editar el formato de etiqueta',
    newTitle: 'Nuevo formato de etiqueta',
    back: 'Volver a la lista',
    dimensionsTitle: 'Dimensiones (mm)',
    widthMm: 'Ancho (mm)',
    heightMm: 'Alto (mm)',
    paddingMm: 'Margen interno (mm)',
    showBorder: 'Mostrar el borde',
    blocksTitle: 'Contenido de la etiqueta',
    resetDefault: 'Restaurar el diseño por defecto',
    blockField: 'Campo',
    blockCaption: 'Prefijo (opcional)',
    blockText: 'Texto',
    blockFont: 'Tamaño (pt)',
    blockAlign: 'Alineación',
    blockBold: 'Negrita',
    addBlock: 'Añadir bloque',
    previewTitle: 'Vista previa',
    previewHint: 'Resultado aproximado · {{w}}×{{h}} mm · {{type}}. Datos de muestra.',
    missingMandatory: 'Faltan campos obligatorios en la etiqueta de venta: {{fields}}.',
    invalidLayout: 'El diseño de la etiqueta no es válido. Revisa las dimensiones y los bloques.',
  },
  ...existenciesEs,
  ...qualityEs,
  ...compresEs,
  ...catalegEs,
} as const;

export type EsResources = typeof es;
